/* PFC moulding product page.
 *
 * Wires:
 *   - Size picker -> updates variant, redraws canvas preview at new aspect, updates price.
 *   - Finish swatches -> hard navigation to linked sibling product.
 *   - File upload -> cropperjs modal -> on confirm, redraws canvas with cropped art.
 *   - Frame-only toggle -> skip image properties on ATC, redraws canvas empty.
 *   - ATC -> 2 S3 presigned PUTs, then /cart/add.js with bundled properties.
 *
 * Live preview uses HTML5 <canvas> rendered via portable port of
 * dropshipping-project's previewClient.ts. Frame is drawn as 4 mitred quads
 * with the moulding texture clipped to each quad, so the same texture works
 * across all aspect ratios without warping.
 *
 * Reads window.PFC_MOULDING (emitted from section liquid):
 *   {
 *     activeMouldingId,
 *     mouldings: [{
 *       productId, productTitle, handle, mouldingFamily, mouldingCode,
 *       mouldingWidthMm, mouldingTextureUrl, swatchImage, isActive,
 *       defaultVariantId,
 *       variants: [{ id, title, sku, price, compareAtPrice, available, widthMm, heightMm, options, bundles }],
 *       mountOptions: [{ id, productId, size, colour, price }]
 *     }],
 *     apertureMap,            // shared constant (size title -> openings)
 *     gcfUploadUrl, uploadBucket, uploadPrefix
 *   }
 *
 * The active moulding is selectable live (swatch click -> swapMoulding) with
 * no page reload. `cfg` is a live alias to the active moulding entry so the
 * rest of the module reads cfg.mouldingCode/Family/WidthMm/TextureUrl/variants
 * /mountOptions exactly as before. apertureMap is shared (mirrored onto cfg).
 */
(function () {
  if (!window.PFC_MOULDING) return;
  var root_cfg = window.PFC_MOULDING;
  if (!root_cfg.mouldings || !root_cfg.mouldings.length) return;
  // Hub-only openings (frame_hub opening_overrides): this page swaps the openings
  // for the sizes it lists (the comics hub offers comic openings on 11x14", say).
  // Every other page emits null, so the shared map is used exactly as before.
  if (root_cfg.openingOverrides && root_cfg.apertureMap) {
    var _am = {};
    Object.keys(root_cfg.apertureMap).forEach(function (k) { _am[k] = root_cfg.apertureMap[k]; });
    Object.keys(root_cfg.openingOverrides).forEach(function (k) { _am[k] = root_cfg.openingOverrides[k]; });
    root_cfg.apertureMap = _am;
  }

  function findMoulding(id) {
    return root_cfg.mouldings.find(function (m) { return m.productId === id; }) || root_cfg.mouldings[0];
  }

  /* ---------- Width-recommendation engine (silent in v1) ----------
     Picks the best moulding from the loaded registry for a given size
     + use-case, biasing toward continuity with the customer's current
     choice (colour/material/depth). Pure function — no DOM. */
  var DEPTH_PREF_BY_USE_CASE = {
    photo:       ['standard', 'flat',     'deep'],
    poster:      ['flat',     'standard', 'deep'],
    print:       ['standard', 'flat',     'deep'],
    painting:    ['deep',     'standard', 'flat'],
    certificate: ['standard', 'flat',     'deep']
  };
  function recommendMoulding(sizeTitle, useCase, previousCfg) {
    if (!sizeTitle) return null;
    var candidates = root_cfg.mouldings.filter(function (m) {
      var v = (m.variants || []).find(function (vv) {
        return vv.title === sizeTitle && vv.available;
      });
      return !!v;
    });
    if (useCase) {
      candidates = candidates.filter(function (m) {
        return (m.useCases || []).indexOf(useCase) !== -1;
      });
    }
    if (!candidates.length) return null;
    if (candidates.length === 1) return candidates[0];
    var prevColour   = previousCfg && (previousCfg.mouldingColourLower || '');
    var prevLinked   = (previousCfg && previousCfg.linkedFinishIds) || [];
    var prevDepth    = previousCfg && previousCfg.depthClass;
    var prevMaterial = previousCfg && previousCfg.material;
    function score(m) {
      var s = 0;
      if (prevColour && m.mouldingColourLower === prevColour) s += 100;
      if (prevLinked.indexOf(m.productId) !== -1) s += 60;
      if (prevMaterial && m.material === prevMaterial) s += 30;
      if (useCase && m.depthClass) {
        var preferred = DEPTH_PREF_BY_USE_CASE[useCase] || [];
        var rank = preferred.indexOf(m.depthClass);
        if (rank !== -1) s += (10 - rank * 3);
      }
      if (prevDepth && m.depthClass === prevDepth) s += 5;
      if (previousCfg && m.productId === previousCfg.productId) s += 1;
      return s;
    }
    candidates.sort(function (a, b) { return score(b) - score(a); });
    return candidates[0];
  }

  // Live alias to the active moulding. Reassigned by swapMoulding().
  // Shared (registry-root) fields the per-moulding cfg needs locally. defaultSize
  // + initialOrientation come from the landing-page section (window.PFC_MOULDING),
  // not the per-moulding config JSON, so they must be aliased onto cfg or
  // initialVariant()/initialOrientation() read undefined and fall back to the
  // first (smallest) variant. Reapplied in swapMoulding so the preselected size
  // survives a moulding swap.
  function applyRootCfg(c) {
    c.apertureMap = root_cfg.apertureMap;          // shared map, exposed on cfg
    c.gcfUploadUrl = root_cfg.gcfUploadUrl;          // shared upload config
    c.uploadBucket = root_cfg.uploadBucket;
    c.uploadPrefix = root_cfg.uploadPrefix;
    if (root_cfg.defaultSize) c.defaultSize = root_cfg.defaultSize;
    if (root_cfg.initialOrientation) c.initialOrientation = root_cfg.initialOrientation;
    c.sizeAllowlist = root_cfg.sizeAllowlist;   // per-hub customiser_sizes allowlist (titles), section-level
    c.frameOnlySizesOnly = root_cfg.frameOnlySizesOnly; // hub gate: show only sizes this moulding can sell FRAME-ONLY
    c.personalisation = root_cfg.personalisation;  // printed text/logo add-on config (root-level)
    c.certMode = root_cfg.certMode;             // certificate hubs: mount GROWS the frame (root-level)
    c.certTargetSize = root_cfg.certTargetSize; // the fixed document/opening size (market-aware)
    // Conservation glazing + quote mode (service hubs only). Undefined/false on
    // every other hub and on the moulding PDPs, so all glazing/quote code no-ops.
    c.enableGlazing = !!root_cfg.enableGlazing;
    c.quoteMode = !!root_cfg.quoteMode;
    c.glazingRates = (root_cfg.glazingRates && typeof root_cfg.glazingRates === 'object')
      ? root_cfg.glazingRates : null;
    return c;
  }
  var cfg = applyRootCfg(findMoulding(root_cfg.activeMouldingId));

  // Occasion mount templates (docs/SPEC-occasion-mount-templates.md): names +
  // date printed on the mount in a chosen template / lettering / ink. Gated per
  // product (custom.occasion_mount, emitted as root_cfg.occasion) AND on the
  // shared core (assets/pfc-mount-pattern-core.js), which draws the lettering
  // with the same code the print file uses. Off everywhere else: OCC is null
  // and every branch below is skipped.
  var MC = window.PFCMountCore || null;
  var OCC = (root_cfg.occasion && root_cfg.occasion.enabled && MC && MC.drawStyledBox) ? root_cfg.occasion : null;
  // Customer-typed mount opening (frame_hub `custom_opening`, e.g. tickets). OFF
  // unless a hub opts in: every other page emits null, and every branch that
  // touches it is gated on CO, so existing pages run exactly as before.
  // CO = { default: {w,h} mm, presets: [{label,w,h}] }. The opening rides a
  // generic "Standard/Premium Mount (Custom opening)" bundle per frame size
  // (price depends on the frame size only); the exact mm go on the order line.
  var CO = (root_cfg.customOpening && root_cfg.customOpening.default
            && root_cfg.customOpening.default.w > 0 && root_cfg.customOpening.default.h > 0) ? root_cfg.customOpening : null;
  var CO_LABEL = 'Custom';
  var CO_MIN_MM = 30;          // smallest opening side we cut (bare-mount customiser rule)
  var CO_MIN_BORDER_MM = 15;   // board left each side (bare-mount customiser MIN_BORDER)
  // frame_hub `mount_required`: the mount can't be unticked (as on occasion hubs).
  var MOUNT_REQ = !!OCC || root_cfg.mountRequired === true;
  // Landscape hubs (puzzle, landscape frames…): the page's orientation is the point,
  // so a size change must not snap the preview back to the size's natural (usually
  // tall) reading. Only the frame-hub section emits this, and only when the hub entry
  // says landscape — every other page keeps the per-size auto-orient below.
  var PIN_ORIENT = root_cfg.keepInitialOrientation === true;
  // Sample artwork for the empty frame-only preview ({url, wideUrl}), from the hub
  // entry's preview_art fields; null on every other page. Drawn on the on-screen
  // preview only — no upload, cart thumbnail or print file ever reads that canvas.
  var PREVIEW_ART = root_cfg.previewArt || null;
  var previewArtImgs = {};
  function previewArtFor(ratio) {
    if (!PREVIEW_ART || !PREVIEW_ART.url) return null;
    var url = (ratio >= 2 && PREVIEW_ART.wideUrl) ? PREVIEW_ART.wideUrl : PREVIEW_ART.url;
    var img = previewArtImgs[url];
    if (!img) {
      img = new Image();
      img.onload = function () { renderPreview(); };
      img.src = url;
      previewArtImgs[url] = img;
    }
    return (img.complete && img.naturalWidth > 0) ? img : null;
  }
  // Smallest bottom border that can carry the names (Plain): rebate-safe edge
  // 12 + gap 3 + band 14 mm. Literal, because this runs while `state` and the
  // PERS_* constants further down are still being set up.
  var OCC_MIN_BORDER_MM = 12 + 3 + 14;
  // Occasion products only list sizes where SOME opening leaves that border;
  // the printed names are the product, so a size that cannot print them is not
  // offered at all.
  function occSizeFits(v) {
    var ap = cfg.apertureMap && v && cfg.apertureMap[v.title];
    var d = (v && v.widthMm && v.heightMm) ? { w: v.widthMm, h: v.heightMm } : (v && parseDim(v));   // A-sizes have no "W x H" title
    if (!ap || !d) return false;
    var land = (typeof state !== 'undefined' && state && state.orientation === 'landscape') && d.w !== d.h;
    var boardH = land ? Math.min(d.w, d.h) : Math.max(d.w, d.h);
    return Object.keys(ap).some(function (k) {
      var a = ap[k]; if (!a || !a.h) return false;
      var apH = (a.w !== a.h && land) ? Math.min(a.w, a.h) : Math.max(a.w, a.h);
      return (boardH - apH) / 2 >= OCC_MIN_BORDER_MM;
    });
  }

  var root = document.querySelector('[data-pfc-moulding]');
  if (!root) return;

  var UI_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

  // Room-scene backdrop: soft wall gradient + floor band + faint skirting.
  // Wall + floor backdrop for the to-scale comparison. floorYpx passed so the
  // floor line matches where the person stands + the frame hangs.
  function drawScaleWall(ctx, W, H, floorYpx) {
    var wall = ctx.createLinearGradient(0, 0, 0, floorYpx);
    wall.addColorStop(0, '#f1ece3');
    wall.addColorStop(1, '#e6ddce');
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, W, floorYpx);
    var floor = ctx.createLinearGradient(0, floorYpx, 0, H);
    floor.addColorStop(0, '#dacfb9');
    floor.addColorStop(1, '#cdbfa5');
    ctx.fillStyle = floor;
    ctx.fillRect(0, floorYpx, W, H - floorYpx);
    ctx.strokeStyle = 'rgba(120,100,70,0.22)';
    ctx.lineWidth = Math.max(1, H * 0.004);
    ctx.beginPath();
    ctx.moveTo(0, floorYpx); ctx.lineTo(W, floorYpx); ctx.stroke();
  }

  // Simple to-scale human silhouette, standing on the floor. cx = horizontal
  // centre, baseY = floor line, hpx = full height in px.
  function drawPerson(ctx, cx, baseY, hpx) {
    var h = hpx;
    ctx.save();
    // contact shadow
    ctx.fillStyle = 'rgba(60,45,25,0.16)';
    ctx.beginPath();
    ctx.ellipse(cx, baseY, h * 0.13, h * 0.012, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(74,62,48,0.62)';
    var headR = h * 0.060;
    var headCy = baseY - h + headR;
    ctx.beginPath(); ctx.arc(cx, headCy, headR, 0, Math.PI * 2); ctx.fill();

    var shoulderY = headCy + headR * 1.6;
    var shoulderW = h * 0.112;
    var hipW = h * 0.082;
    var hipY = baseY - h * 0.47;
    var footY = baseY;
    var legGap = h * 0.016;
    ctx.beginPath();
    ctx.moveTo(cx - shoulderW, shoulderY);
    ctx.quadraticCurveTo(cx - shoulderW * 1.04, (shoulderY + hipY) / 2, cx - hipW, hipY);
    ctx.lineTo(cx - hipW, footY);
    ctx.lineTo(cx - legGap, footY);
    ctx.lineTo(cx - legGap, hipY + h * 0.012);
    ctx.lineTo(cx + legGap, hipY + h * 0.012);
    ctx.lineTo(cx + legGap, footY);
    ctx.lineTo(cx + hipW, footY);
    ctx.lineTo(cx + hipW, hipY);
    ctx.quadraticCurveTo(cx + shoulderW * 1.04, (shoulderY + hipY) / 2, cx + shoulderW, shoulderY);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /* ---------- DOM ---------- */
  var elPreview = root.querySelector('[data-preview]');
  var elCanvas = root.querySelector('[data-preview-canvas]');
  var elPrice = root.querySelector('[data-price]');
  var elCompare = root.querySelector('[data-compare]');
  var elSizeSelect = root.querySelector('[data-size-select]');
  var elSizeTrigger = root.querySelector('[data-size-trigger]');
  var elSizeList = root.querySelector('[data-size-list]');
  var elSizeValue = root.querySelector('[data-size-value]');
  var elSizeUnitBtns = Array.from(root.querySelectorAll('[data-size-unit]'));
  var elTitle = root.querySelector('[data-title]');
  var elFamily = root.querySelector('[data-family]');
  var elDetailsAbout = root.querySelector('[data-details-about]');
  var elSwatches = root.querySelector('[data-swatches]');
  var elUpload = root.querySelector('[data-upload]');
  var elUploadPanel = root.querySelector('[data-upload-panel]');
  var elFileInput = root.querySelector('[data-file-input]');
  // iOS: drop HEIC from accept so the picker hands over a full-res JPEG itself.
  if (elFileInput && window.PFCUploadNormalise) elFileInput.accept = window.PFCUploadNormalise.accept();
  var elUploadTitle = root.querySelector('[data-upload-title]');

  // 'Add a photo' gate (checked = print & frame -> show dropzone; unchecked =
  // frame only -> hide it). Replaces the old frame-only checkbox; frameOnlyMode
  // is the single source of truth for the rest of the code.
  var elAddPhoto = root.querySelector('[data-add-photo]');
  var elAddPhotoWrap = root.querySelector('[data-add-photo-wrap]');
  var elAddPhotoHead = root.querySelector('[data-add-photo-head]');
  var elPhotoLockHint = root.querySelector('[data-photo-lock-hint]');
  function frameOnlyMode() {
    return !!(elAddPhoto && !elAddPhoto.checked);
  }
  function setUploadOpen(open) {
    // The dropzone lives in the accordion panel; fall back to the dropzone itself.
    var panel = elUploadPanel || elUpload;
    if (panel) panel.hidden = !open;
  }
  // Printed text / logo add-on (data-pers-*) — ported from the bare-mount customiser.
  var elPersWrap = root.querySelector('[data-pers-wrap]');
  var elPersToggle = root.querySelector('[data-pers-toggle]');
  var elPersPanel = root.querySelector('[data-pers-panel]');
  var elPersFitNote = root.querySelector('[data-pers-fit-note]');
  var elPersPrice = root.querySelector('[data-pers-price]');
  var elPersLogoFields = root.querySelector('[data-pers-logo-fields]');
  var elPersHeading = root.querySelector('[data-pers-heading]');
  var elPersLine2 = root.querySelector('[data-pers-line2]');
  var elPersAlignBtns = Array.from(root.querySelectorAll('[data-pers-align]'));
  var elPersSizeBtns = Array.from(root.querySelectorAll('[data-pers-size]'));
  var elPersSizeGroup = root.querySelector('[data-pers-size-group]');
  var elPersSizeNote = root.querySelector('[data-pers-size-note]');
  var elPersLogoToggle = root.querySelector('[data-pers-logo-toggle]');
  var elPersLogoPosBtns = Array.from(root.querySelectorAll('[data-pers-logo-pos]'));
  var elPersLogoDrop = root.querySelector('[data-pers-logo-drop]');
  var elPersLogoInput = root.querySelector('[data-pers-logo-input]');
  var elPersLogoTitle = root.querySelector('[data-pers-logo-title]');
  var elPersLogoThumb = root.querySelector('[data-pers-logo-thumb]');
  var elPersFontSelect = root.querySelector('[data-pers-font-select]');
  var elPersFontTrigger = root.querySelector('[data-pers-font-trigger]');
  var elPersFontValue = root.querySelector('[data-pers-font-value]');
  var elPersFontList = root.querySelector('[data-pers-font-list]');
  var PERS_FONTS = (cfg.personalisation && cfg.personalisation.fonts) || ['Playfair Display','Cormorant Garamond','EB Garamond','Lora','Cinzel','Montserrat','Poppins','Raleway','Oswald','Dancing Script','Great Vibes','Sacramento'];
  var elAtc = root.querySelector('[data-atc]');
  var elAtcStatus = root.querySelector('[data-atc-status]');
  var elCropModal = root.querySelector('[data-crop-modal]');
  var elCropImg = root.querySelector('[data-crop-img]');
  var elCropConfirm = root.querySelector('[data-crop-confirm]');
  var elCropCancel = root.querySelector('[data-crop-cancel]');
  // Orientation toggle refs declared early — refreshOrientationToggle runs
  // during the initial selectVariant call (line ~166) before the handler block.
  // Several copies (size row, mobile sheet, size-locked row) — keep all in sync.
  var elOrientationWraps = Array.from(root.querySelectorAll('[data-orientation-toggle]'));
  var elOrientationBtns = Array.from(root.querySelectorAll('[data-orientation-btn]'));
  /* Compact controls (mobile sheet) — state declared early so it is defined
     when renderCompactMount runs during initial selectVariant call. */
  var compactState = { open: null, aperture: null };
  var elCompact = root.querySelector('[data-compact]');
  var elCompactSizeTrigger = root.querySelector('[data-compact-trigger="size"]');
  var elCompactSizeValue = root.querySelector('[data-compact-value="size"]');
  var elCompactSizeList = root.querySelector('[data-compact-list="size"]');
  var elCompactMountRow = root.querySelector('[data-compact-row="mount"]');
  var elCompactMountTrigger = root.querySelector('[data-compact-trigger="mount"]');
  var elCompactMountValue = root.querySelector('[data-compact-value="mount"]');
  var elCompactMountList = root.querySelector('[data-compact-list="mount"]');
  var elCompactColour = root.querySelector('[data-compact-colour]');
  var elCompactColourBtns = Array.from(root.querySelectorAll('[data-compact-colour-btn]'));
  /* Framing-style picker (desktop + mobile compact) */
  var elFramings = root.querySelector('[data-framings]');
  var elFramingBtns = Array.from(root.querySelectorAll('[data-framing-btn]'));
  var elCompactFramingRow = root.querySelector('[data-compact-row="framing"]');
  var elCompactFramingTrigger = root.querySelector('[data-compact-trigger="framing"]');
  var elCompactFramingValue = root.querySelector('[data-compact-value="framing"]');
  var elCompactFramingList = root.querySelector('[data-compact-list="framing"]');
  /* Paper-edge sub-control (only relevant when Float-mount is active) */
  var elEdgeRow = root.querySelector('[data-edge-row]');
  var elEdgeBtns = Array.from(root.querySelectorAll('[data-edge-btn]'));

  /* Float-border (shadow-gap) sub-control — only relevant when Float-mount is
     active. Selecting a gap grows the frame around the (fixed-size) print. */
  var FLOAT_GAPS = [10, 20, 30];
  var elAdvanced = root.querySelector('[data-advanced]');
  var elGapRow = root.querySelector('[data-gap-row]');
  var elGapBtns = Array.from(root.querySelectorAll('[data-gap-btn]'));
  /* Conservation glazing + itemised breakdown + quote form (service hubs only). */
  var elGlazingRow = root.querySelector('[data-glazing-row]');
  var elGlazingBtns = Array.from(root.querySelectorAll('[data-glazing-btn]'));
  var elBreakdown = root.querySelector('[data-breakdown]');
  var elQuoteForm = document.querySelector('[data-quote-form]');
  /* External frame dims — a secondary line under Size (desktop) plus a copy
     in the mobile sheet's compact block. Shown for every framing style. */
  var elExtDims = Array.from(root.querySelectorAll('[data-extdims]'));
  /* Spec strip — four icon cells + ONE shared drawer above the buy button.
     Tapping a cell opens the drawer on that cell's explanation; tapping it
     again closes it. updateSpecStrip() keeps the labels in the active unit. */
  var elSpecStrip = root.querySelector('[data-spec-strip]');
  var elSpecBtns = elSpecStrip ? Array.from(elSpecStrip.querySelectorAll('[data-spec]')) : [];
  var elSpecBar = root.querySelector('[data-spec-bar]');
  var elSpecDrawer = root.querySelector('[data-spec-drawer]');
  var elSpecText = root.querySelector('[data-spec-text]');
  var specOpen = null;        // key of the open cell ('face' | 'depth' | 'glaze' | 'hang') or null
  var specSwapTimer = null;   // pending fade-out -> swap, or close -> hide
  var specAutoTimer = null;   // releases the drawer to height:auto after it settles
  if (elSpecDrawer) {
    var specDrawerId = 'pfc-spec-drawer-' + Math.random().toString(36).slice(2, 8);
    elSpecDrawer.id = specDrawerId;
    elSpecBtns.forEach(function (b) {
      b.setAttribute('aria-controls', specDrawerId);
      b.addEventListener('click', function () {
        setSpecOpen(specOpen === b.dataset.spec ? null : b.dataset.spec);
      });
    });
    window.addEventListener('resize', placeSpecBar);
  }

  // Per-side surround (mm) the frame grows by beyond the print. Only float
  // grows the frame; standard/gallery/mount keep the print-size frame.
  function floatSurroundMm() {
    return state.framingStyle === 'float' ? state.floatGapMm : 0;
  }

  // The moulding face width (mm) used for every preview-scale calc. Single
  // owner so renderPreview / sizeDepthBox / applyDepthVar stay in sync.
  //  - `|| 20`: fallback when the metafield is missing.
  //  - 3mm floor: pure render-sanity guard (stop a sub-pixel hairline on small
  //    canvases). Previously 14mm, which collapsed every slim moulding (7.8mm,
  //    10mm metals) to one width and made true proportions read as marginal —
  //    a 7.8mm vs 20mm face must show as ~2.5× wider, not ~1.4×.
  function faceWidthMm() {
    return Math.max(cfg.mouldingWidthMm || 20, 3);
  }

  // Float-mount enlarges the frame, so we charge it like a size change: snap to
  // the smallest existing size variant whose dimensions accommodate the grown
  // frame, and use that variant's price/line (reusing the normal size ladder +
  // Bundle Pro). Returns the current variant when float is off or nothing is
  // bigger/cheaper.
  function floatPriceVariant(gap) {
    var v = state.variant;
    if (state.framingStyle !== 'float' || !gap) return v;
    var pm = printDimsMm(v);
    if (!pm.w || !pm.h) return v;
    var tMin = Math.min(pm.w, pm.h) + 2 * gap;
    var tMax = Math.max(pm.w, pm.h) + 2 * gap;
    var best = null, bestArea = Infinity, largest = null, largestArea = -1;
    cfg.variants.forEach(function (c) {
      if (!c.widthMm || !c.heightMm || c.available === false) return;
      var cMin = Math.min(c.widthMm, c.heightMm), cMax = Math.max(c.widthMm, c.heightMm);
      var area = cMin * cMax;
      if (area > largestArea) { largestArea = area; largest = c; }
      if (cMin >= tMin - 1 && cMax >= tMax - 1 && area < bestArea) { bestArea = area; best = c; }
    });
    best = best || largest || v;
    // Never price below the chosen print size.
    return (Number(best.price) >= Number(v.price)) ? best : v;
  }

  // Float-mount is sold like custom framing (2026-10-22): the frame is built to
  // the EXACT opening (print + gap each side), priced per metre of that frame, and
  // charged on this moulding's "Custom Size" variant with a bucket from the shared
  // custom-framing ladder (cfg.floatPricing, pfc-moulding-config-json). Same
  // formula as the studio's computeCustomFramingPrice (shopify-print-studio-TPE
  // constants.ts) minus paper — a moulding-customiser frame includes its print.
  // These three MUST match the studio's CUSTOM_FRAMING_MARKUP,
  // CUSTOM_FRAMING_LABOUR_BY_MARKET and CUSTOM_FRAMING_FLOAT_UPLIFT_PER_M.
  var CF_MARKUP = 1.1;
  var CF_LABOUR = { GBP: 1.36, USD: 2.51 };
  var FLOAT_UPLIFT_PER_M = 5;   // trade £/m on top of the moulding's own £/m

  function floatOpeningMm(gap) {
    var pm = printDimsMm(state.variant);
    if (!pm.w || !pm.h) return null;
    return { w: pm.w + 2 * gap, h: pm.h + 2 * gap };
  }
  // The opening as the order states it, in the unit the size was sold in.
  function floatOpeningText(o) {
    var lab = String((state.variant && state.variant.title) || '');
    var inch = /"/.test(lab) || (!/\d\s*(?:cm|mm)\b|^\s*A\d\s*$/i.test(lab) && dispIn());
    return inch ? fmtInch(o.w / 25.4) + '×' + fmtInch(o.h / 25.4) + '"'
      : Math.round(o.w) + '×' + Math.round(o.h) + 'mm';
  }
  // null when float is off or this moulding has no float pricing — callers then
  // fall back to floatPriceVariant (the old size-snap), so nothing breaks.
  function floatQuote(gap) {
    var fp = cfg && cfg.floatPricing;
    if (state.framingStyle !== 'float' || !gap || !fp || !fp.hasLadder || !(Number(fp.pricePerMeter) > 0)) return null;
    var grid = ((fp.ladder && fp.ladder.frame) || []).map(Number).filter(isFinite).sort(function (a, b) { return a - b; });
    var o = floatOpeningMm(gap);
    if (!grid.length || !o) return null;
    var perimM = 2 * (o.w + o.h) / 1000;
    var raw = perimM * (Number(fp.pricePerMeter) + FLOAT_UPLIFT_PER_M) * CF_MARKUP * (CF_LABOUR[fp.shopCurrency] || CF_LABOUR.GBP);
    raw = Math.max(raw, Number(fp.minOrderTotal) || 0);
    var total = null;
    for (var i = 0; i < grid.length; i++) { if (grid[i] >= raw - 0.001) { total = grid[i]; break; } }
    if (total === null) { total = grid[grid.length - 1]; try { console.warn('[pfc] float total over ladder top — clamped', raw, total); } catch (_) {} }
    // Charged = host line (presentment) + the bucket's add-on at the host line's
    // market multiple — what Bundle Pro charges, so page == cart in every market.
    var hostBase = Number(fp.hostBase) || Number(fp.minOrderTotal) || 0;
    var rate = window.PFCBundleRate ? window.PFCBundleRate(fp.hostPrice, fp.hostBase) : 1;
    var cents = Number(fp.hostPrice) + Math.round(addonChargeMajor(total - hostBase, rate) * 100);
    return { id: 'f' + Math.round(total), total: total, raw: raw, priceCents: cents, opening: o };
  }
  // The price a float line will be charged at this gap (minor units).
  function floatLinePriceCents(gap) {
    var q = floatQuote(gap);
    return q ? q.priceCents : Number(floatPriceVariant(gap).price);
  }

  // Conservation glazing (service hubs only). Priced by AREA, not off the size
  // ladder — glazing is bought by the sheet, so cost tracks m² while every other
  // component tracks a fixed size point. Area is taken from the size point the
  // frame is actually built to (float snaps to a larger variant), so the glazing
  // and the frame always describe the same physical object.
  //   price = max(min, area_m2 * rate_per_m2)
  // Rates live in the hub metaobject (store currency) so they can be corrected
  // without a deploy. Returns 0 unless the hub enables glazing AND one is chosen.
  function glazingSpec(type) {
    var rates = cfg && cfg.glazingRates;
    if (!rates || !type) return null;
    return rates[type] || null;
  }
  function glazingPriceFor(type) {
    if (!cfg || !cfg.enableGlazing) return 0;
    var spec = glazingSpec(type);
    if (!spec) return 0;
    var v = floatPriceVariant(state.floatGapMm);
    var w = Number(v.widthMm) || 0, h = Number(v.heightMm) || 0;
    // Float on the custom-framing ladder builds to the exact opening.
    var fq = floatQuote(state.floatGapMm);
    if (fq) { w = fq.opening.w; h = fq.opening.h; }
    if (!w || !h) return 0;
    var areaM2 = (w / 1000) * (h / 1000);
    var rate = Number(spec.rate_per_m2) || 0;
    var min = Number(spec.min) || 0;
    // Money is handled in minor units throughout the customiser.
    return Math.max(min, Math.round(areaM2 * rate)) * 100;
  }
  function glazingLinePrice() {
    return state.glazing ? glazingPriceFor(state.glazing) : 0;
  }

  // Live price on each glazing option + active state on the buttons.
  function refreshGlazingPrices() {
    if (!elGlazingBtns.length) return;
    elGlazingBtns.forEach(function (b) {
      var type = b.dataset.glazingBtn;                 // '' | 'acrylic' | 'glass'
      b.setAttribute('aria-pressed', String((state.glazing || '') === type));
      var el = b.querySelector('[data-glazing-price]');
      if (!el) return;
      el.textContent = type ? '+ ' + formatMoney(glazingPriceFor(type)) : '';
    });
    updateSpecStrip();
  }

  // Itemised estimate. Only rendered where the markup exists (conservation hub);
  // every other customiser keeps its single headline price.
  function renderBreakdown(priceVar, glazingCents, totalCents) {
    if (!elBreakdown) return;
    var rows = [];
    var frameLabel = (cfg.mouldingFamily || cfg.productTitle || 'Frame') + ' · ' + priceVar.title;
    rows.push(['Frame (' + frameLabel + ')', Number(priceVar.price)]);
    if (state.mount) {
      rows.push(['Mount (' + (state.mount.colour || '') + ')', mountLinePrice()]);
    }
    if (glazingCents > 0) {
      var spec = glazingSpec(state.glazing);
      rows.push([(spec && spec.label) || 'Conservation glazing', glazingCents]);
    }
    var html = rows.map(function (r) {
      return '<div class="pfc-moulding__bd-row"><span>' + escapeAttr(r[0]) + '</span>'
           + '<span>' + formatMoney(r[1]) + '</span></div>';
    }).join('');
    html += '<div class="pfc-moulding__bd-row pfc-moulding__bd-row--total">'
          + '<span>Estimated total</span><span>' + formatMoney(totalCents) + '</span></div>';
    elBreakdown.innerHTML = html;
  }

  // Full-state deep-link — quote hubs only. The quote email carries this URL, so
  // clicking it must rebuild the exact design the customer specified. Everywhere
  // else the URL keeps its existing ?variant-only behaviour, untouched.
  // State is small enough to live entirely in the URL, so Phase 1 needs no store.
  function syncDeepLink() {
    if (!cfg || !cfg.quoteMode) return;
    try {
      var url = new URL(window.location.href);
      var p = url.searchParams;
      if (cfg.handle) p.set('m', cfg.handle);
      if (state.variant) p.set('s', state.variant.title);
      p.set('mount', state.mount ? (state.mount.colour || '') + ':' + (state.mount.aperture || '') : 'none');
      p.set('glaze', state.glazing || 'none');
      if (state.framingStyle) p.set('style', state.framingStyle);
      if (state.framingStyle === 'float') {
        p.set('gap', String(state.floatGapMm));
        p.set('edge', state.edgeType);
      } else { p.delete('gap'); p.delete('edge'); }
      window.history.replaceState({}, '', url);
    } catch (_) {}
  }

  // Restore glazing from the URL on load. Moulding + size restore through the
  // existing swapMoulding/?variant path; this only adds what that doesn't carry.
  function restoreFromDeepLink() {
    if (!cfg || !cfg.quoteMode) return;
    try {
      var p = new URLSearchParams(window.location.search);
      var g = p.get('glaze');
      if (g && g !== 'none' && glazingSpec(g)) state.glazing = g;
    } catch (_) {}
  }

  // Glazing click handler. Empty data-glazing-btn = "None".
  if (elGlazingBtns.length) {
    elGlazingBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.dataset.glazingBtn || '';
        var next = v || null;
        if (next === state.glazing) return;
        state.glazing = next;
        updatePriceAndPhotoSize();
        syncDeepLink();
      });
    });
  }

  // Show each float-border button's price RELATIVE to the currently selected
  // gap: cheaper gaps show −£, pricier gaps +£, the selected one shows nothing.
  function refreshGapPrices() {
    if (!elGapBtns || !elGapBtns.length || !state.variant) return;
    var floatOn = state.framingStyle === 'float';
    var selPrice = floatLinePriceCents(state.floatGapMm);
    elGapBtns.forEach(function (b) {
      var g = Number(b.dataset.gapBtn);
      var el = b.querySelector('[data-gap-price]');
      if (!el) return;
      var d = floatOn ? (floatLinePriceCents(g) - selPrice) : 0;
      if (!floatOn || g === state.floatGapMm || Math.abs(d) < 0.5) {
        el.textContent = '';
        el.classList.remove('is-cheaper');
      } else {
        el.textContent = (d > 0 ? '+' : '−') + formatMoney(Math.abs(d));
        el.classList.toggle('is-cheaper', d < 0);
      }
    });
  }

  /* Framing-style: label lookup + helpers. cfg.allowedFramingStyles drives
     which buttons are visible; cfg.defaultFramingStyle drives the preselect
     when the customer's current pick isn't in the new allowed list. */
  var FRAMING_LABEL = { standard: 'Standard', gallery: 'Gallery', float: 'Float-mount' };

  // Float-mount = print suspended inside the box depth with the paper edge
  // visible. There's no mount aperture in this construction, so the mount
  // picker is hidden and any current mount selection is cleared. The paper-
  // edge sub-control (Straight / Deckled) reveals only when float is active.
  var prevFloatActive = false;
  function syncMountAvailability() {
    var floatActive = state.framingStyle === 'float';
    if (floatActive && state.mount) {
      state.mount = null;
      syncMountButtons();
    }
    if (elMounts && floatActive) elMounts.hidden = true;
    if (elAddMountWrap && floatActive) { elAddMountWrap.hidden = true; if (elAddMount) elAddMount.checked = false; }
    if (elCompactMountRow && floatActive) elCompactMountRow.hidden = true;
    // Paper-edge + float-border live in the advanced accordion; show the whole
    // <details> only when float is active, and collapse it on leaving.
    if (elAdvanced) {
      elAdvanced.hidden = !floatActive;
      if (!floatActive) elAdvanced.open = false;
    }
    // When leaving float, reset edge to straight so non-float orders never
    // carry a stale edgeType in the cart payload.
    if (!floatActive && state.edgeType !== 'straight') {
      state.edgeType = 'straight';
      if (elEdgeBtns && elEdgeBtns.length) {
        elEdgeBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.edgeBtn === 'straight')); });
      }
    }
    // Reset the float gap to the 20mm default whenever leaving float so a later
    // re-entry starts clean (and the gap buttons reflect it).
    if (!floatActive && state.floatGapMm !== 20) {
      state.floatGapMm = 20;
      if (elGapBtns && elGapBtns.length) {
        elGapBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.gapBtn === '20')); });
      }
    }
    // Animate the frame growing/shrinking on the float transition only — not on
    // every incidental sync (size swap, moulding swap, initial render).
    if (floatActive !== prevFloatActive) {
      animateSurr(state.animSurr, floatSurroundMm());
      if (floatActive) flashFrameSize();
      prevFloatActive = floatActive;
    }
    applyPreviewAspect();
  }

  // Paper-edge click handler (Straight / Deckled).
  if (elEdgeBtns.length) {
    elEdgeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.dataset.edgeBtn;
        if (!v || v === state.edgeType) return;
        state.edgeType = v;
        elEdgeBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
        renderPreview();
      });
    });
  }

  // Float-border (shadow-gap) click handler. Re-anchors: the print stays its
  // selected size, the frame grows by 2× the chosen gap. Animates the change.
  if (elGapBtns.length) {
    elGapBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var g = Number(btn.dataset.gapBtn);
        if (!g || g === state.floatGapMm) return;
        var from = state.animSurr;
        state.floatGapMm = g;
        elGapBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
        applyPreviewAspect();
        animateSurr(from, floatSurroundMm());
        flashFrameSize();
        updateFrameSizeUi();
        updatePriceAndPhotoSize(); // gap changes the nearest size point -> price
      });
    });
  }

  // ---- Indicator A: tween the float surround (mm) so the canvas frame eases
  // outward, and arm the on-canvas dimension annotation. ----
  var surrRaf = null;
  function animateSurr(from, to) {
    if (surrRaf) { cancelAnimationFrame(surrRaf); surrRaf = null; }
    state.dimAnnoUntil = Date.now() + 1800; // hold + fade window for dim lines
    if (from === to) { state.animSurr = to; renderPreview(); return; }
    var start = performance.now(), dur = 260;
    function step(now) {
      var t = Math.min(1, (now - start) / dur);
      var e = 1 - Math.pow(1 - t, 3); // ease-out cubic
      state.animSurr = from + (to - from) * e;
      renderPreview();
      if (t < 1) { surrRaf = requestAnimationFrame(step); }
      else { surrRaf = null; state.animSurr = to; renderPreview(); }
    }
    surrRaf = requestAnimationFrame(step);
  }

  // ---- Indicator B: pulse the external-dims line on a float change. ----
  var framePulseTimer = null;
  function flashFrameSize() {
    if (!elExtDims.length) return;
    elExtDims.forEach(function (el) {
      el.classList.remove('is-pulse');
      // force reflow so the animation restarts even on rapid changes
      void el.offsetWidth;
      el.classList.add('is-pulse');
    });
    if (framePulseTimer) clearTimeout(framePulseTimer);
    framePulseTimer = setTimeout(function () {
      elExtDims.forEach(function (el) { el.classList.remove('is-pulse'); });
    }, 650);
  }

  // ---- 'View depth' toggle: tilt the whole framed preview to a subtle 3/4
  // angle using a real CSS 3D transform (perspective + rotateY/rotateX on the
  // element), animated by a CSS transition. Works for every framing style. ----
  var elDepthBtn = root.querySelector('[data-depth-toggle]');
  var elBox = root.querySelector('[data-3dbox]');
  var elFaces = Array.from(root.querySelectorAll('[data-3dface]'));

  // Figure aspect: print ratio normally; frame-outer ratio in depth view so the
  // edge-to-edge canvas frame fills the box and the DOM side faces (which sit
  // at the box edges) line up with the moulding edges.
  function applyPreviewAspect() {
    if (!elPreview) return;
    // Fixed square "stage" (like Level Frames) for BOTH flat and depth views, so
    // tall portrait frames are contained with breathing room rather than making
    // the preview tower past the viewport. The canvas render centres + contains
    // the frame inside it; depth view additionally sizes the 3D box.
    elPreview.style.aspectRatio = '1 / 1';
    if (state.depthView) sizeDepthBox();
    else if (elBox) { elBox.style.width = ''; elBox.style.height = ''; }
  }

  // Size the 3D box to CONTAIN the frame (its real aspect) within the square
  // stage, leaving a margin. The box equals the frame, so the DOM side faces
  // stay aligned; it's just smaller and centred.
  function sizeDepthBox(pm) {
    if (!elBox || !elPreview) return;
    pm = pm || printDimsMm(state.variant);
    if (!pm.w || !pm.h) return;
    var fwm = faceWidthMm();
    var surr = floatSurroundMm();
    var fr = (pm.w + surr * 2 + fwm * 2) / (pm.h + surr * 2 + fwm * 2);
    var MARGIN = 0.78; // frame occupies ~78% of the stage's limiting dimension
    var availW = (elPreview.clientWidth || 440) * MARGIN;
    var availH = (elPreview.clientHeight || 440) * MARGIN;
    var w, h;
    if (availW / availH > fr) { h = availH; w = h * fr; }
    else { w = availW; h = w / fr; }
    elBox.style.width = Math.round(w) + 'px';
    elBox.style.height = Math.round(h) + 'px';
  }

  // Average colour of a (CORS-clean) texture image, cached per moulding code.
  // Used to give the 3D sides a plain moulding colour rather than the front
  // face pattern (which is wrong for profiled mouldings — Level uses plain
  // sides too).
  var edgeColourCache = {};
  function avgColour(img) {
    try {
      var c = document.createElement('canvas');
      c.width = 8; c.height = 8;
      var x = c.getContext('2d');
      x.drawImage(img, 0, 0, 8, 8);
      var d = x.getImageData(0, 0, 8, 8).data;
      var r = 0, g = 0, b = 0, n = 0;
      for (var i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
      return 'rgb(' + Math.round(r / n) + ',' + Math.round(g / n) + ',' + Math.round(b / n) + ')';
    } catch (e) { return null; }
  }
  function mouldingSideColour() {
    // Key on the unique product id — mouldingCode defaults to 'X' for many
    // mouldings, which collided and pinned every side to the first colour.
    var key = cfg.productId || cfg.mouldingTextureUrl || '';
    if (edgeColourCache[key]) return edgeColourCache[key];
    var img = state.mouldingTextureImg;
    // Only sample if the loaded image actually belongs to the current moulding
    // (on swap the immediate call can still hold the previous texture).
    var matches = img && cfg.mouldingTextureUrl && img.src && img.src.indexOf(cfg.mouldingTextureUrl) === 0;
    if (matches && img.complete && img.naturalWidth) {
      var col = avgColour(img);
      if (col) { edgeColourCache[key] = col; return col; }
    }
    return null; // not ready / not matching yet — keep the current colour
  }

  // 90°-rotated copy of the moulding texture (data URL), cached per moulding.
  // The left/right depth faces run the rail VERTICALLY, so the swatch grain must
  // be turned 90° to run along the rail — exactly like the front canvas rotates
  // the left/right rails. Top/bottom faces use the texture unrotated.
  var edgeImgRotCache = {};
  function mouldingSideTextureRot() {
    var key = cfg.productId || cfg.mouldingTextureUrl || '';
    if (edgeImgRotCache[key]) return edgeImgRotCache[key];
    var img = state.mouldingTextureImg;
    var matches = img && cfg.mouldingTextureUrl && img.src && img.src.indexOf(cfg.mouldingTextureUrl) === 0;
    if (matches && img.complete && img.naturalWidth) {
      try {
        var c = document.createElement('canvas');
        c.width = img.naturalHeight; c.height = img.naturalWidth;
        var x = c.getContext('2d');
        x.translate(c.width / 2, c.height / 2);
        x.rotate(Math.PI / 2);
        x.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        var url = c.toDataURL('image/jpeg', 0.9);
        edgeImgRotCache[key] = url;
        return url;
      } catch (e) { return null; }
    }
    return null;
  }

  // Set the 3D side-face thickness (--pm-depth) proportional to the real
  // moulding depth at the current on-screen frame scale, and skin the sides
  // with a plain sampled moulding colour (--pm-edge-col).
  function applyDepthVar() {
    if (!elPreview) return;
    var pm = printDimsMm(state.variant);
    var fwm = faceWidthMm();
    var surr = floatSurroundMm();
    var frameOuterWmm = (pm.w || 300) + surr * 2 + fwm * 2;
    var boxW = elPreview.getBoundingClientRect().width || 440;
    var pxPerMm = boxW / frameOuterWmm;
    var px = Math.max(8, Math.round((cfg.mouldingDepthMm || 22) * pxPerMm));
    elPreview.style.setProperty('--pm-depth', px + 'px');
    var col = mouldingSideColour();
    if (col) elPreview.style.setProperty('--pm-edge-col', col);
    // Flat mouldings (custom.moulding_side_textured) wrap the front texture onto
    // the 3D side faces instead of a plain sampled colour. Profiled mouldings
    // (bobbin etc.) leave the flag off and keep the colour sides.
    if (cfg.mouldingSideTextured && cfg.mouldingTextureUrl) {
      elPreview.style.setProperty('--pm-edge-img', 'url("' + cfg.mouldingTextureUrl + '")');
      // left/right faces use a 90°-rotated swatch (grain runs along the rail);
      // fall back to the unrotated url if the rotate canvas isn't ready.
      var rot = mouldingSideTextureRot();
      elPreview.style.setProperty('--pm-edge-img-rot', 'url("' + (rot || cfg.mouldingTextureUrl) + '")');
      elPreview.classList.add('pm-side-tex');
    } else {
      elPreview.classList.remove('pm-side-tex');
    }
  }

  // Single owner of the pending "rotate-back then restore padded render" so a
  // stale disable can never fire after a later enable (which previously flipped
  // depthView off mid-tilt and made the padded canvas swing).
  var depthRestoreTimer = null;
  var depthEndListener = null;
  function cancelPendingRestore() {
    if (depthRestoreTimer) { clearTimeout(depthRestoreTimer); depthRestoreTimer = null; }
    if (elBox && depthEndListener) { elBox.removeEventListener('transitionend', depthEndListener); }
    depthEndListener = null;
  }
  function enableDepth() {
    cancelPendingRestore();
    state.depthView = true;
    if (elDepthBtn) elDepthBtn.setAttribute('aria-pressed', 'true');
    // Depth alignment only holds in the plain scene.
    if (state.scene !== 'flat') {
      state.scene = 'flat';
      elSceneBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.sceneBtn === 'flat')); });
      elPreview.dataset.scene = 'flat';
    }
    // Render edge-to-edge first (no scene margin to swing), let layout settle,
    // THEN start the tilt rotation.
    applyPreviewAspect();
    applyDepthVar();
    renderPreview();
    requestAnimationFrame(function () {
      sizeDepthBox();
      renderPreview();
      requestAnimationFrame(function () { elPreview.classList.add('is-3d'); });
    });
  }
  // immediate=true skips the graceful rotate-back (used when leaving depth via
  // a scene switch, where the box is reset anyway).
  function disableDepth(immediate) {
    cancelPendingRestore();
    if (elDepthBtn) elDepthBtn.setAttribute('aria-pressed', 'false');
    elPreview.classList.remove('is-3d');
    if (immediate) {
      state.depthView = false;
      applyPreviewAspect();
      renderPreview();
      return;
    }
    var restore = function () {
      cancelPendingRestore();
      state.depthView = false;
      applyPreviewAspect();
      renderPreview();
    };
    depthEndListener = function (e) { if (!e || e.propertyName === 'transform') restore(); };
    if (elBox) elBox.addEventListener('transitionend', depthEndListener);
    depthRestoreTimer = setTimeout(restore, 850); // fallback if no transitionend
  }
  if (elDepthBtn && elPreview) {
    elDepthBtn.addEventListener('click', function () {
      if (state.depthView) disableDepth(false); else enableDepth();
    });
  }

  /* Announce the live framing style so the "What you'll receive" guide
     (snippets/pfc-framing-guide.liquid) can show the right instructions. Fired
     for a LOCKED style too — a hidden picker still ships a Gallery frame. */
  function emitFramingChange() {
    try {
      root.dispatchEvent(new CustomEvent('pfc:framing-change', {
        bubbles: true,
        detail: { style: state.framingStyle || 'standard' }
      }));
    } catch (_) {}
  }

  function applyFramingStyleFor(activeCfg) {
    var allowed = (activeCfg && activeCfg.allowedFramingStyles) || ['standard'];
    var defaultStyle = (activeCfg && activeCfg.defaultFramingStyle) || 'standard';
    var prev = state.framingStyle;
    var keep = (prev && allowed.indexOf(prev) !== -1) ? prev : defaultStyle;

    // Single-option: hide the picker. Two ways to get here — a shallow moulding
    // (only 'standard'), or a style hub that locked the list to one style.
    var multi = allowed.length > 1;
    if (elFramings) elFramings.hidden = !multi;
    if (elCompactFramingRow) elCompactFramingRow.hidden = !multi;

    if (!multi) {
      // 'standard' stays implicit: null so postCart skips the property and the
      // order line stays clean. A LOCKED non-standard style must survive the
      // hidden picker — it's what the frame is built and priced from, so
      // nulling it here would ship a Gallery frame with no float upcharge.
      state.framingStyle = (keep === 'standard') ? null : keep;
      // Reset compact list so a future swap to a deep moulding rebuilds cleanly.
      if (elCompactFramingList) elCompactFramingList.innerHTML = '';
      // Locked float still needs its mount hidden + edge/gap sub-controls shown;
      // a shallow moulding still needs them torn down after leaving float.
      syncMountAvailability();
      if (state.variant) applyPhotoLockForFraming();
      emitFramingChange();
      return;
    }

    state.framingStyle = keep;

    // Desktop buttons: show/hide per allowed list + reflect active state.
    elFramingBtns.forEach(function (b) {
      var v = b.dataset.framingBtn;
      var inList = allowed.indexOf(v) !== -1;
      b.hidden = !inList;
      b.setAttribute('aria-pressed', String(inList && v === keep));
    });

    // Compact list (mobile sheet): rebuild from allowed.
    if (elCompactFramingList) {
      var html = '';
      allowed.forEach(function (v) {
        var sel = v === keep;
        html += '<li class="pfc-moulding__compact-option" role="option" data-framing="' + v + '"'
              + ' aria-selected="' + (sel ? 'true' : 'false') + '">'
              + FRAMING_LABEL[v]
              + '</li>';
      });
      elCompactFramingList.innerHTML = html;
    }
    if (elCompactFramingValue) elCompactFramingValue.textContent = FRAMING_LABEL[keep] || 'Gallery';
    syncMountAvailability();
    if (state.variant) applyPhotoLockForFraming(); // lock photo on if landed on float
    emitFramingChange();
  }

  // Desktop click handler.
  if (elFramingBtns.length) {
    elFramingBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var v = btn.dataset.framingBtn;
        if (!v || v === state.framingStyle) return;
        state.framingStyle = v;
        elFramingBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
        if (elCompactFramingValue) elCompactFramingValue.textContent = FRAMING_LABEL[v] || v;
        if (elCompactFramingList) {
          elCompactFramingList.querySelectorAll('[data-framing]').forEach(function (li) {
            li.setAttribute('aria-selected', String(li.dataset.framing === v));
          });
        }
        syncMountAvailability();
        applyPhotoLockForFraming();
        emitFramingChange();
        // Refresh mount visibility for the current size (handles return from float).
        refreshMountUi();
        renderCompactMount();
        updatePriceAndPhotoSize();
        renderPreview();
      });
    });
  }

  // Mobile compact-list click handler (mirrors mount-compact pattern).
  if (elCompactFramingList) {
    elCompactFramingList.addEventListener('click', function (e) {
      var li = e.target.closest('[data-framing]');
      if (!li) return;
      var v = li.dataset.framing;
      if (!v || v === state.framingStyle) { setCompactPopover(null); return; }
      state.framingStyle = v;
      elFramingBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(b.dataset.framingBtn === v)); });
      elCompactFramingList.querySelectorAll('[data-framing]').forEach(function (l) {
        l.setAttribute('aria-selected', String(l.dataset.framing === v));
      });
      if (elCompactFramingValue) elCompactFramingValue.textContent = FRAMING_LABEL[v] || v;
      setCompactPopover(null);
      syncMountAvailability();
      applyPhotoLockForFraming();
      emitFramingChange();
      refreshMountUi();
      renderCompactMount();
      updatePriceAndPhotoSize();
      renderPreview();
    });
  }
  if (elCompactFramingTrigger) {
    elCompactFramingTrigger.addEventListener('click', function () {
      setCompactPopover(compactState.open === 'framing' ? null : 'framing');
    });
  }
  /* Bottom-sheet (mobile) */
  var elSheet = root.querySelector('[data-sheet]');
  var elSheetHandle = root.querySelector('[data-sheet-handle]');
  var elSheetBody = root.querySelector('[data-sheet-body]');
  var elSheetPrice = root.querySelector('[data-sheet-price]');
  var elSheetSummary = root.querySelector('[data-sheet-summary]');

  /* ---------- State ---------- */
  // Resolve starting orientation: ?orientation=landscape URL param wins over
  // section setting (cfg.initialOrientation). Used by SEO landing pages
  // targeting landscape/portrait keyword clusters.
  function initialOrientation() {
    try {
      var qp = new URLSearchParams(window.location.search).get('orientation');
      if (qp === 'landscape' || qp === 'portrait') return qp;
    } catch (_) {}
    var cfgVal = cfg && cfg.initialOrientation;
    if (cfgVal === 'landscape' || cfgVal === 'portrait') return cfgVal;
    // Default to the starting variant's natural orientation so panoramic
    // (landscape-titled, w>h) sizes open wide instead of rotated to portrait.
    var d = parseDim(initialVariant());
    if (d && d.w !== d.h) return d.w > d.h ? 'landscape' : 'portrait';
    return 'portrait';
  }

  // Resolve starting variant: cfg.defaultSize (size title from a landing-page
  // metaobject, e.g. "30cm x 30cm") wins, then ?variant=, then cfg.defaultVariantId.
  // Falls back to first available variant if the requested size isn't stocked
  // on this moulding.
  // cm/inch list filter — inch variants carry a " in the title; everything else
  // (cm sizes + A-sizes) is the cm group. Mirrors the mount customiser.
  function variantUnit(v) {
    return (v && v.title && v.title.indexOf('"') !== -1) ? 'in' : 'cm';
  }
  // Default list unit by store: US → inch, UK → cm.
  function defaultSizeUnit() {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return cur === 'USD' ? 'in' : 'cm';
  }

  // ---- Per-hub size curation (customiser_sizes allowlist + specialist sizes) ----
  // SPECIALIST_SIZES (panoramic ratios) exist as variants on eligible mouldings
  // but are HIDDEN by default — shown only on entries whose customiser_sizes
  // allowlist names them — so they never clutter standard moulding PDPs/hubs.
  // Titles must match the variant titles exactly (compared case/space-insensitive).
  var SPECIALIST_SIZES = [
    '24" x 8"', '36" x 12"', '60cm x 20cm', '90cm x 30cm',   // 3:1 scenic (no mount)
    '24" x 12"', '36" x 18"', '50cm x 25cm', '60cm x 30cm'    // 2:1 group (mount)
  ].map(function (s) { return s.toLowerCase(); });
  // Some hubs exist for people framing something they ALREADY OWN — a movie
  // poster, a record sleeve. There, frame-only is the whole point of the page,
  // so a size the active moulding can't sell frame-only is a dead end: the
  // customer picks it and 'Add a photo' silently forces itself back on. When the
  // entry sets frameOnlySizesOnly, such sizes are hidden instead. The rule reuses
  // the EXISTING per-moulding data (requirePrint / allowedFrameOnlySizes) rather
  // than a second hard-coded size list — aluminium sold empty at a non-stock size
  // would ship as a self-assembly kit, which is exactly what that data encodes.
  // Wood is frame-only at any size, so it is unaffected and keeps Custom Size.
  function normTitle(t) { return String(t || '').trim().toLowerCase(); }
  // cfg.sizeAllowlist from metaobject.customiser_sizes. When set → dropdown shows
  // ONLY those titles. When absent → standard set with SPECIALIST_SIZES excluded.
  function sizeAllowSet() {
    var a = cfg.sizeAllowlist;
    if (!a || !a.length) return null;
    return a.map(normTitle);
  }
  function sizeVisible(v) {
    var allow = sizeAllowSet();
    var t = normTitle(v && v.title);
    if (cfg.frameOnlySizesOnly && !frameOnlyAllowed(v)) return false;
    if (OCC && !occSizeFits(v)) return false;
    if (allow) return allow.indexOf(t) !== -1;
    return SPECIALIST_SIZES.indexOf(t) === -1;
  }
  function visibleVariants() { return cfg.variants.filter(sizeVisible); }

  /* ---------- Premium (patterned) mount ---------- */
  // Data-driven pattern list — add a pattern by adding one entry here (+ its
  // render branch in drawMountPattern). All patterns share the Premium price
  // tier, so no data/pricing change is needed for a new one. Mirrors the
  // patterned boards in the standalone mount customiser (pfc-mount.js).
  var STRIPE_BASE_HEX = '#fafaf6';   // white mount base
  var MOUNT_PATTERNS = [
    // `group`/`groupLabel`/`scaleLabel` drive the two-tier picker (mirrors pfc-mount-pattern-core.js).
    { key: 'fine',     label: 'Fine 3mm stripes', type: 'stripes', bandWidthMm: 3, group: 'stripe',      groupLabel: 'Stripe',      scaleLabel: 'Fine' },
    { key: 'vertical', label: 'Vertical stripes', type: 'stripes', bands: 19,      group: 'stripe',      groupLabel: 'Stripe',      scaleLabel: 'Regular' },
    { key: 'wc-fine',   label: 'Watercolour stripe — Fine',   type: 'watercolour', bandMm: 12, gapMm: 8,  wobbleMm: 1.8, poolAmt: 0.45, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Fine' },
    { key: 'wc-even',   label: 'Watercolour stripe — Even',   type: 'watercolour', bandMm: 22, gapMm: 18, wobbleMm: 3.0, poolAmt: 0.55, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Regular' },
    { key: 'wc-cabana', label: 'Watercolour stripe — Cabana', type: 'watercolour', bandMm: 34, gapMm: 9,  wobbleMm: 3.4, poolAmt: 0.60, seed: 7, group: 'watercolour', groupLabel: 'Watercolour', scaleLabel: 'Bold' },
    { key: 'gingham',  label: 'Gingham',          type: 'gingham', cellMm: 16,           group: 'gingham', groupLabel: 'Gingham' },
    { key: 'checker',  label: 'Checkered',        type: 'checkers', checkMm: 15,          group: 'checker', groupLabel: 'Checked' },
    { key: 'polka',    label: 'Polka dot',        type: 'polka', dotMm: 18,              group: 'polka',   groupLabel: 'Polka dot' },
    { key: 'chevron',  label: 'Chevron',          type: 'chevron', bandMm: 14,           group: 'chevron', groupLabel: 'Chevron' },
    { key: 'accent',   label: 'Accent keyline',   type: 'accent', gapMm: 10, lineMm: 1.4, group: 'accent', groupLabel: 'Keyline' },
    // French-line keylines — hidden from the picker until the test print signs
    // them off; occasion templates select them by key (mirrors the core).
    { key: 'accent-fine',   label: 'Fine keyline',       type: 'accent', gapMm: 8, lineMm: 0.3,                                 group: 'accent', groupLabel: 'Keyline', hidden: true },
    { key: 'accent-double', label: 'Double keyline',     type: 'accent', gapMm: 8, lineMm: 0.3, lines: 2, spacingMm: 3,         group: 'accent', groupLabel: 'Keyline', hidden: true },
    { key: 'accent-wash',   label: 'French line & wash', type: 'accent', gapMm: 6, lineMm: 0.3, lines: 2, spacingMm: 6, washAlpha: 0.12, group: 'accent', groupLabel: 'Keyline', hidden: true }
  ];
  var STRIPE_PRESETS = [
    { label: 'Navy',       hex: '#23395d' },
    { label: 'Black',      hex: '#1a1a1a' },
    { label: 'Sage',       hex: '#7d8c6a' },
    { label: 'Burgundy',   hex: '#6e2433' },
    { label: 'Mustard',    hex: '#c79a3a' },
    { label: 'Dusty pink', hex: '#c98b8b' },
    { label: 'Slate grey', hex: '#6b7280' },
    { label: 'Teal',       hex: '#2e6e6a' }
  ];
  function normaliseHex(h) {
    if (!h) return null;
    h = String(h).trim();
    if (h[0] !== '#') h = '#' + h;
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(h) ? h : null;
  }
  function stripePresetLabel(hex) {
    var m = STRIPE_PRESETS.find(function (p) { return p.hex.toLowerCase() === String(hex).toLowerCase(); });
    return m ? m.label : hex;
  }
  function patternByKey(key) {
    return MOUNT_PATTERNS.find(function (p) { return p.key === key; }) || MOUNT_PATTERNS[0];
  }
  // Two-tier picker model — collapse patterns by `group` into styles + scales.
  function patternGroups() {
    var order = [], map = {};
    MOUNT_PATTERNS.forEach(function (p) {
      if (p.hidden) return;   // printable, not offered in the picker
      var g = p.group || p.key;
      if (!map[g]) { map[g] = { group: g, label: p.groupLabel || p.label, items: [] }; order.push(map[g]); }
      map[g].items.push(p);
    });
    return order;
  }
  function groupOf(key) {
    var p = patternByKey(key), g = p.group || p.key;
    return patternGroups().find(function (x) { return x.group === g; });
  }
  // CSS gradient preview for a pattern button chip — mirrors the canvas render
  // so the swatch reads like the actual mount. Uses the live stripe colour.
  function patternChipGradient(pat) {
    var a = state.stripeColour;
    if (pat.type === 'checkers') return 'repeating-conic-gradient(' + a + ' 0% 25%, ' + STRIPE_BASE_HEX + ' 0% 50%) 0 / 9px 9px';
    if (pat.type === 'gingham') return 'repeating-linear-gradient(0deg,' + a + ' 0 2px,transparent 2px 7px),repeating-linear-gradient(90deg,' + a + ' 0 2px,transparent 2px 7px),' + STRIPE_BASE_HEX;
    if (pat.type === 'polka') return 'radial-gradient(' + a + ' 30%, transparent 33%) 0 0 / 8px 8px, ' + STRIPE_BASE_HEX;
    if (pat.type === 'chevron') return 'repeating-linear-gradient(135deg,' + a + ' 0 2px,' + STRIPE_BASE_HEX + ' 2px 5px)';
    if (pat.type === 'accent') return 'radial-gradient(closest-side, ' + STRIPE_BASE_HEX + ' 52%, ' + a + ' 55% 74%, ' + STRIPE_BASE_HEX + ' 77%)';
    if (pat.type === 'watercolour') { var bmm = pat.bandMm || 26, gmm = pat.gapMm || 14, unit = 34, bp = Math.max(2, Math.round(unit * bmm / (bmm + gmm))); return 'repeating-linear-gradient(90deg,' + STRIPE_BASE_HEX + ' 0 ' + (unit - bp) + 'px,' + a + ' ' + (unit - bp) + 'px ' + unit + 'px)'; }
    var u = pat.bandWidthMm ? 2 : 4;   // stripes (fine = tighter)
    return 'repeating-linear-gradient(90deg,' + STRIPE_BASE_HEX + ' 0 ' + u + 'px,' + a + ' ' + u + 'px ' + (u * 2) + 'px)';
  }
  function stripeBandCount(boardW_mm, pat) {
    if (pat.bands) return pat.bands;
    if (pat.bandWidthMm) { var n = Math.round((boardW_mm || 210) / pat.bandWidthMm); return n < 5 ? 5 : n; }
    return 19;
  }
  function isPremiumMount() { return !!(state.mount && state.mount.colour === 'premium'); }

  function initialVariant() {
    var sizeMatch = null;
    if (cfg.defaultSize) {
      var want = String(cfg.defaultSize).trim().toLowerCase();
      sizeMatch = cfg.variants.find(function (v) {
        return v.title && v.title.trim().toLowerCase() === want && v.available;
      });
    }
    if (sizeMatch) return sizeMatch;
    var byId = cfg.variants.find(function (v) { return v.id === cfg.defaultVariantId; });
    if (byId && sizeVisible(byId)) return byId;        // don't preselect a hidden (specialist) size
    return visibleVariants()[0] || cfg.variants[0];
  }

  var state = {
    variant: initialVariant(),
    customOp: CO ? coNorm(CO.default.w, CO.default.h) : null,   // typed opening, mm portrait (CO hubs only)
    coShown: CO ? { w: CO.default.w, h: CO.default.h } : null,     // ...as the customer typed it (width first)
    desiredSizeTitle: null,    // the user's last EXPLICIT size pick (sticky across moulding swaps)
    orientation: initialOrientation(),   // 'portrait' | 'landscape', display-only swap of preview aspect
    fullDataUrl: null,         // GENERATED artwork only (name-art / preset), full-res dataURL.
                               // A customer photo never sets this — see state.photo.
    previewDataUrl: null,      // preview-res (800px), dataURL
    photo: false,              // artwork on screen is the customer's photo (uploaded as an
                               // untouched original + crop box, not as fullDataUrl)
    srcImg: null,              // HTMLImageElement of the ORIGINAL upload (uncropped)
    croppedAspect: null,       // the print ratio the crop was cut to
    cropStale: false,          // ratio moved on and the original is gone — must recrop
    pendingPrev: null,         // confirmed photo a not-yet-confirmed pick replaced
    cropFor: null,             // S3 url of the original the confirmed crop belongs to
    cropper: null,
    sessionId: cryptoRandom(),
    designId: null,            // ONE design's id, minted fresh on every add-to-cart
                               // that uploads artwork. sessionId is per PAGE LOAD, so
                               // keying uploads on it made a second design overwrite
                               // the first at the same S3 path and merge into its cart
                               // line (PFC-UK-1338359, two photos ordered, one kept).
    textureCache: {},          // mouldingCode -> HTMLImageElement
    mouldingTextureImg: null,  // HTMLImageElement, active moulding's texture
    userImg: null,             // HTMLImageElement of cropped preview, redrawn on size change
    scene: 'flat',             // 'flat' | 'scale' (to-scale comparison)
    hoverPlaceholder: false,   // canvas hover state for empty preview
    mount: null,               // { colour: 'white'|'black', aperture: 'small'|'large', variantId, price }
    framingStyle: null,        // 'standard' | 'gallery' | 'float' | null (null = picker not applicable to this moulding)
    edgeType: 'straight',      // 'straight' | 'deckled' — applies only when framingStyle === 'float'
    glazing: null,             // null | 'acrylic' | 'glass' — conservation hubs only (cfg.enableGlazing)
    floatGapMm: 20,            // float-mount shadow gap per side (10|20|30) — frame grows by 2× this
    animSurr: 0,               // tweened float surround (mm) used by renderPreview for the grow animation
    dimAnnoUntil: 0,           // Date.now() until which the canvas dimension annotation is drawn (Indicator A)
    depthView: false,          // 'View depth' toggle — CSS 3D cuboid tilt (Level-style)
    sizeUnit: 'cm',            // 'cm' | 'in' — which sizes the dropdown shows (set just below)
    stripeColour: STRIPE_PRESETS[0].hex, // coloured band/cell hex for a patterned (Premium) mount
    pers: {                    // printed text / logo add-on (prints in the bottom mount border)
      on: false,
      heading: '', line2: '',
      align: 'center',         // 'left' | 'center' | 'right'
      size: 'm',               // 's' | 'm' | 'l' — discreet physical text size
      font: PERS_FONTS[0],
      logoOn: false,
      logoPos: 'above',        // 'above' | 'below' the text
      logoImg: null,
      logoDataUrl: null,
    },
    occ: OCC ? { template: OCC.template || 'plain', lettering: OCC.lettering || 'playfair', ink: OCC.ink || 'black' } : null,
  };

  // The customer's ORIGINAL photo, uploading in the background from the moment it
  // is picked. The print is cut from it server-side using state.cropData
  // (pfc-photo-source.js).
  var photoSrc = window.PFCPhotoSource ? window.PFCPhotoSource.create({
    signerUrl: cfg.gcfUploadUrl, bucket: cfg.uploadBucket, onChange: syncAtcGate,
  }) : null;
  var adding = false;   // add-to-cart request in flight

  // Is there artwork to print — a photo, or generated name-art / preset art.
  function hasArtwork() { return !!(state.fullDataUrl || (state.photo && state.previewDataUrl)); }

  // Initial list unit: store default (UK cm / US inch). If an explicit size was
  // configured (default_size_handle) its unit wins so the preselected size stays
  // visible; otherwise switch the selection to a size in the store's unit.
  state.sizeUnit = defaultSizeUnit();
  if (variantUnit(state.variant) !== state.sizeUnit) {
    if (cfg.defaultSize) {
      state.sizeUnit = variantUnit(state.variant);
    } else {
      var _initU = visibleVariants().find(function (v) { return variantUnit(v) === state.sizeUnit && v.available; })
        || visibleVariants().find(function (v) { return variantUnit(v) === state.sizeUnit; });
      if (_initU) { state.variant = _initU; } else { state.sizeUnit = variantUnit(state.variant); }
    }
  }

  // Load a moulding's texture, caching per mouldingCode so cycling back to a
  // finish doesn't re-fetch. cb fires once the image is ready (or errored).
  // Append a cache-bust param: the same image may already sit in the browser
  // cache from a non-CORS <img> load (e.g. as a product image). Reusing that
  // cached copy under crossOrigin='anonymous' fails CORS even though S3 sends
  // Access-Control-Allow-Origin:* on a fresh request. A distinct URL forces a
  // clean fetch that carries the CORS header, keeping the canvas untainted.
  function loadTexture(mcfg, cb) {
    var key = mcfg.mouldingCode || mcfg.mouldingTextureUrl || '';
    var cached = state.textureCache[key];
    if (cached) { cb(cached.naturalWidth > 0 ? cached : null); return; }
    if (!mcfg.mouldingTextureUrl) { cb(null); return; }
    var ti = new Image();
    ti.crossOrigin = 'anonymous';
    ti.onload = function () { state.textureCache[key] = ti; cb(ti); };
    ti.onerror = function () { cb(null); };
    ti.src = mcfg.mouldingTextureUrl + (mcfg.mouldingTextureUrl.indexOf('?') === -1 ? '?' : '&') + 'v=1';
  }

  // Initial active-moulding texture.
  loadTexture(cfg, function (img) { state.mouldingTextureImg = img; if (state.depthView) applyDepthVar(); renderPreview(); });

  var elMounts = root.querySelector('[data-mounts]');
  // Two-step desktop picker: opening buttons (JS-built) + colour buttons.
  var elMountOpenings = root.querySelector('[data-mount-openings]');
  var elMountColourBtns = Array.from(root.querySelectorAll('[data-mount-colour-btn]'));
  var elMountPhotosize = root.querySelector('[data-mount-photosize]');
  // "Add a mount?" opt-in — the mount picker is hidden until the customer ticks
  // it. US stores say "mat" instead of "mount".
  var elAddMount = root.querySelector('[data-add-mount]');
  var elAddMountWrap = root.querySelector('[data-add-mount-wrap]');
  var elAddMountLabel = root.querySelector('[data-add-mount-label]');
  // Premium (patterned) mount sub-pickers — desktop + mobile sheet share the
  // same data-attrs, so these are NodeList-wide (both instances driven at once).
  var elMountPatternsAll = Array.from(root.querySelectorAll('[data-mount-patterns]'));
  var elPatternGrids = Array.from(root.querySelectorAll('[data-pattern-grid]'));
  var elStripeWrapsAll = Array.from(root.querySelectorAll('[data-stripe-colours]'));
  var elStripeGrids = Array.from(root.querySelectorAll('[data-stripe-grid]'));
  var elStripeCustoms = Array.from(root.querySelectorAll('[data-stripe-custom]'));
  if (elAddMountLabel) {
    var _matVocab = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) === 'USD';
    elAddMountLabel.textContent = _matVocab ? 'Add a mat?' : 'Add a mount?';
  }

  /* ---------- Recommendation state ---------- */
  state.lockedIntent = (root_cfg.lockedIntent || '').toLowerCase() || null;
  state.activeIntent = state.lockedIntent;
  state.swapping = false;          // re-entrancy guard for auto-swap
  state.userOverride = false;      // set when a swatch click manually swaps moulding
  state.preOverridePick = null;    // remembers the customer's pick for "Restore" undo
  state.undoTimer = null;

  var elReco = root.querySelector('[data-reco]');
  var elRecoFamily = elReco && elReco.querySelector('[data-reco-family]');
  var elRecoReason = elReco && elReco.querySelector('[data-reco-reason]');
  var elRecoToggle = elReco && elReco.querySelector('[data-reco-toggle]');
  var elRecoAlt = elReco && elReco.querySelector('[data-reco-alt]');
  var elRecoUndo = elReco && elReco.querySelector('[data-reco-undo]');

  var USE_CASE_NOUN = {
    photo: 'photos', poster: 'posters', print: 'prints',
    painting: 'paintings', certificate: 'certificates'
  };

  function reasonCopy(useCase, picked) {
    if (!useCase) return picked.mouldingWidthMm + 'mm sits well at ' + state.variant.title + '.';
    var noun = USE_CASE_NOUN[useCase] || 'this';
    return picked.mouldingWidthMm + 'mm sits well around ' + noun + ' at ' + state.variant.title + '.';
  }

  function uniqueBy(arr, key) {
    var seen = Object.create(null);
    return arr.filter(function (it) {
      if (!it) return false;
      var v = it[key];
      if (seen[v]) return false;
      seen[v] = true;
      return true;
    });
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function renderRecoAlternates(picked) {
    if (!elRecoAlt) return;
    var sameColour = root_cfg.mouldings.filter(function (m) {
      return m.productId !== picked.productId
          && m.mouldingColourLower
          && m.mouldingColourLower === picked.mouldingColourLower;
    });
    var linked = (picked.linkedFinishIds || []).map(function (id) {
      return root_cfg.mouldings.find(function (m) { return m.productId === id; });
    }).filter(Boolean);
    var alts = uniqueBy(sameColour.concat(linked), 'productId').filter(function (m) {
      var v = (m.variants || []).find(function (vv) {
        return vv.title === state.variant.title && vv.available;
      });
      return !!v;
    }).slice(0, 4);
    if (!alts.length) { elRecoAlt.innerHTML = ''; elRecoToggle && (elRecoToggle.hidden = true); return; }
    elRecoToggle && (elRecoToggle.hidden = false);
    var html = '';
    alts.forEach(function (a) {
      var v = a.variants.find(function (vv) { return vv.title === state.variant.title && vv.available; });
      html += '<button type="button" class="pfc-moulding__reco-alt-btn" data-reco-alt-id="' + a.productId + '">'
            + '<span class="pfc-moulding__reco-alt-chip" style="background-image:url(\'' + escapeAttr(a.swatchImage || '') + '\');"></span>'
            + '<span class="pfc-moulding__reco-alt-meta">'
            + '<span class="pfc-moulding__reco-alt-style">' + escapeAttr(a.framingStyleLabel || fmtMouldDisp(a.mouldingWidthMm)) + '</span>'
            + '<span class="pfc-moulding__reco-alt-price">' + formatMoney(sizeListPrice(v)) + '</span>'
            + '</span>'
            + '</button>';
    });
    elRecoAlt.innerHTML = html;
  }

  function clearUndo() {
    if (state.undoTimer) { clearTimeout(state.undoTimer); state.undoTimer = null; }
    if (elRecoUndo) { elRecoUndo.hidden = true; elRecoUndo.textContent = ''; }
  }

  function showUndo(prevCfg) {
    if (!elRecoUndo) return;
    elRecoUndo.hidden = false;
    var label = prevCfg.mouldingColour ? prevCfg.mouldingColour : 'previous';
    elRecoUndo.textContent = 'Restore your ' + label + ' pick?';
    if (state.undoTimer) clearTimeout(state.undoTimer);
    state.undoTimer = setTimeout(clearUndo, 10000);
  }

  function showRecommendationBadge(useCase, picked, replacedCfg) {
    if (!elReco) return;
    elReco.hidden = false;
    if (elRecoFamily) {
      var label = picked.framingStyleLabel || fmtMouldDisp(picked.mouldingWidthMm);
      var colour = picked.mouldingColour ? (' ' + picked.mouldingColour) : '';
      elRecoFamily.textContent = label + colour;
    }
    if (elRecoReason) elRecoReason.textContent = reasonCopy(useCase, picked);
    renderRecoAlternates(picked);
    // Reset the alternates flyout to collapsed when content changes.
    if (elRecoToggle) elRecoToggle.setAttribute('aria-expanded', 'false');
    if (elRecoAlt) elRecoAlt.hidden = true;
  }

  function evaluateRecommendation(reason) {
    if (state.swapping) return;
    if (!state.variant) return;
    var useCase = state.activeIntent;
    if (!useCase) return; // no intent → no recommendation, no badge
    var pick = recommendMoulding(state.variant.title, useCase, cfg);
    if (!pick) {
      // No moulding matches the size + use-case. Surface a soft notice.
      if (elReco) {
        elReco.hidden = false;
        if (elRecoFamily) elRecoFamily.textContent = 'no match';
        if (elRecoReason) elRecoReason.textContent = 'No frames tagged for ' + useCase + ' in ' + state.variant.title + '.';
        if (elRecoAlt) { elRecoAlt.innerHTML = ''; elRecoAlt.hidden = true; }
        if (elRecoToggle) elRecoToggle.hidden = true;
      }
      return;
    }
    if (pick.productId === cfg.productId) {
      // Already on the best moulding — refresh the badge but skip swap.
      showRecommendationBadge(useCase, pick, null);
      return;
    }
    // Respect manual override: surface undo banner instead of auto-swapping back.
    if (state.userOverride && reason === 'size-change') {
      var prev = cfg;
      state.swapping = true;
      try { swapMoulding(pick.productId); } finally { state.swapping = false; }
      showRecommendationBadge(useCase, pick, prev);
      showUndo(findMoulding(state.preOverridePick) || prev);
      return;
    }
    var replaced = cfg;
    state.swapping = true;
    try { swapMoulding(pick.productId); } finally { state.swapping = false; }
    showRecommendationBadge(useCase, pick, replaced);
  }

  /* ---------- Init ---------- */
  state.desiredSizeTitle = state.variant && state.variant.title;
  renderSizeButtons();
  applyFramingStyleFor(cfg);
  restoreFromDeepLink();   // quote hubs: re-apply glazing before the first price render
  // Hub entry mount_default_on (postcards: a 6x8" frame only holds one with a mount).
  // Ticked before the first selectVariant, whose refreshMountUi then picks the
  // default white mount; sizes with no opening hide the toggle as usual.
  if (root_cfg.mountDefaultOn === true && elAddMount && !OCC) elAddMount.checked = true;
  if (root_cfg.mountRequired === true && elAddMount) elAddMount.checked = true;   // frame_hub mount_required
  selectVariant(state.variant);
  evaluateRecommendation('init');
  // Markup ships hidden, so this must UNHIDE on conservation hubs as well as
  // keep it hidden everywhere else — assigning the boolean does both.
  if (elGlazingRow) elGlazingRow.hidden = !(cfg && cfg.enableGlazing);

  /* ---------- Mount: opening + colour (two-step) ---------- */
  // First available aperture for the current size ('small' preferred).
  function firstAperture() {
    var ap = aperturesFor(state.variant.title);
    if (!ap) return null;
    if (ap.small) return 'small';
    if (ap.large) return 'large';
    var ks = Object.keys(ap);
    return ks.length ? ks[0] : null;
  }
  // Build a mount selection for (colour, aperture). Premium = white-base
  // component at the Premium bundle price; pattern carried for the render/props.
  function resolveMount(colour, apKey) {
    var ap = aperturesFor(state.variant.title);
    if (!apKey || !ap || !ap[apKey]) return null;
    if (colour === 'premium') {
      var wmv = mountVariantFor(state.variant.title, 'white');
      var pp = premiumBundlePriceFor(apKey);
      if (!wmv || pp == null) return null;
      var keepPattern = (state.mount && state.mount.pattern) || MOUNT_PATTERNS[0].key;
      return { colour: 'premium', aperture: apKey, pattern: keepPattern, variantId: wmv.id, price: pp };
    }
    // Standard tier (2026-06-09 collapse): white + black share the WHITE
    // (standard) board component + price. The selected colour rides as a line
    // property (set in the ATC builder). Black is no longer a bundle component.
    var mv = mountVariantFor(state.variant.title, 'white');
    if (!mv) return null;
    // Price from the bundle the ATC sends, converted as Bundle Pro converts it --
    // NOT the board variant's storefront price, which carries the market
    // adjustment + rounding the cart never applies. Board price only if a
    // variant somehow has no matching bundle (then the ATC sends none either).
    var sp = bundleChargedPence(standardBundleFor(apKey, colour));
    return { colour: colour, aperture: apKey, variantId: mv.id, price: (sp != null ? sp : Number(mv.price)) };
  }
  /* ---------- Certificate mode ---------- */
  // Some rebadge hubs (diploma/certificate/graduation/scripture) fix the ARTWORK
  // at a document size (A4). Standard framing shrinks the opening INSIDE a fixed
  // frame — wrong for a certificate, where the document must stay fully visible.
  // So here a mount GROWS the frame instead: no mount = the document-size frame;
  // add a mount = swap to the smallest real frame whose opening == the document
  // size (A4 → A3 frame, large opening), with the print still the document size.
  // cfg.certTargetSize is the document/opening size (market-aware, set by the hub).
  var _certGrown; // undefined = uncomputed; { title, apKey } = found; null = none
  function sizeKey(s) { return String(s || '').replace(/\s*[x×]\s*/i, '×').replace(/\s+/g, ''); }
  function certConfig() {
    if (!cfg.certMode || !cfg.certTargetSize) return null;
    if (_certGrown !== undefined) return _certGrown;
    var target = cfg.certTargetSize;
    var byTitle = {};
    (cfg.variants || []).forEach(function (v) { byTitle[v.title] = v; });
    var best = null, bestArea = Infinity;
    Object.keys(cfg.apertureMap || {}).forEach(function (title) {
      var v = byTitle[title];
      if (!v || v.available === false || !v.widthMm || !v.heightMm) return;  // frame must exist + be buyable
      if (title === target) return;                     // the document-size frame is the NO-mount frame
      var ap = cfg.apertureMap[title];
      ['small', 'large'].forEach(function (apKey) {
        // opening must equal the document size. Titles read '8.5" x 11"', labels '8.5"×11"'.
        if (!ap[apKey] || sizeKey(ap[apKey].label) !== sizeKey(target)) return;
        var area = v.widthMm * v.heightMm;
        if (area < bestArea) { bestArea = area; best = { title: title, apKey: apKey }; }
      });
    });
    _certGrown = best;   // null if no grown frame is available → cert mode no-ops (standard behaviour)
    return _certGrown;
  }
  // The no-mount frame: the document-size variant itself.
  function certBaseVariant() {
    if (!cfg.certMode || !cfg.certTargetSize) return null;
    return (cfg.variants || []).find(function (v) { return v.title === cfg.certTargetSize; }) || null;
  }
  function certActive() { return !!(certConfig() && certBaseVariant()); }
  // Toggle the mount on a certificate hub: swap the frame (not the opening).
  function applyCertFrame(on) {
    var cg = certConfig(), base = certBaseVariant();
    if (!cg || !base) return;
    // Keep the desktop opt-in checkbox in sync so refreshMountUi's opt-in guard
    // (which clears state.mount when the box is unchecked) agrees with the mobile
    // compact path too.
    if (elAddMount) elAddMount.checked = on;
    if (on) {
      var grown = (cfg.variants || []).find(function (v) { return v.title === cg.title; });
      if (!grown) return;
      var prefColour = (state.mount && state.mount.colour) || 'white';
      var prefPattern = state.mount && state.mount.pattern;
      selectVariant(grown);                 // swaps frame + price + preview; resets mount
      var m = resolveMount(prefColour, cg.apKey) || resolveMount('white', cg.apKey);
      if (m) { if (prefPattern) m.pattern = prefPattern; commitMount(m); }  // force the document-size opening
    } else {
      selectVariant(base);                  // back to the document-size frame
    }
    updatePriceAndPhotoSize();
    renderPreview();
  }

  // Reflect state.mount on the desktop opening + colour buttons.
  function syncMountButtons() {
    if (elMountOpenings) {
      Array.from(elMountOpenings.querySelectorAll('[data-mount-opening]')).forEach(function (b) {
        b.setAttribute('aria-pressed', String(!!state.mount && state.mount.aperture === b.dataset.mountOpening));
      });
    }
    elMountColourBtns.forEach(function (b) {
      b.setAttribute('aria-pressed', String(!!state.mount && state.mount.colour === b.dataset.mountColourBtn));
    });
  }
  // Build the opening buttons for the size's available apertures.
  function renderMountOpenings() {
    if (!elMountOpenings) return;
    if (CO) {   // typed opening: the size boxes replace the opening buttons
      elMountOpenings.innerHTML = '<p class="pfc-co__note">Cut to your item\'s size - enter it below.</p>';
      return;
    }
    var ap = aperturesFor(state.variant.title) || {};
    var cg = certActive() ? certConfig() : null;   // cert hubs force the document-size opening only
    elMountOpenings.innerHTML = '';
    ['small', 'large'].forEach(function (apKey) {
      if (cg && apKey !== cg.apKey) return;
      var d = ap[apKey];
      if (!d) return;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pfc-moulding__mount-btn';
      b.dataset.mountOpening = apKey;
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML = '<span class="pfc-moulding__mount-label">' + escapeHtml(d.label) + ' opening</span>';
      elMountOpenings.appendChild(b);
    });
  }
  // Price the colour buttons for the current aperture (premium varies by
  // opening; white/black are per-size). Hide a colour with no offer.
  // Each colour chip shows its price RELATIVE to the current selection (mirrors
  // the bare-mount customiser): plain (white/black) = base tier, premium
  // (patterned) = base + uplift.
  //   • plain selected     → plain "+ £0", premium "+ £x"
  //   • premium selected   → premium "+ £0", plain "− £x"
  // When printed text is active the line is already at the Premium tier, so
  // switching to a pattern costs nothing extra → no deltas shown.
  function updateMountColourPrices() {
    var apKey = (state.mount && state.mount.aperture) || firstAperture();
    var premPrice = premiumBundlePriceFor(apKey);                 // pence | null
    var stdMv = mountVariantFor(state.variant.title, 'white');
    var stdCharged = bundleChargedPence(standardBundleFor(apKey, 'white'));
    var stdPrice = stdCharged != null ? stdCharged : (stdMv ? Number(stdMv.price) : null); // pence | null
    var uplift = (premPrice != null && stdPrice != null) ? (premPrice - stdPrice) : 0;
    if (persActive()) uplift = 0;                                  // text already premium
    var selPremium = !!(state.mount && state.mount.colour === 'premium');
    elMountColourBtns.forEach(function (b) {
      var col = b.dataset.mountColourBtn;
      var priceEl = b.querySelector('[data-mount-price]');
      if (col === 'premium') {
        if (premPrice == null) { b.hidden = true; return; }
      } else {
        // Standard tier collapsed white+black onto the WHITE component/price; price
        // from this colour's own variant if present, else the white one.
        var mv = mountVariantFor(state.variant.title, col) || stdMv;
        if (!mv) { b.hidden = true; return; }
      }
      b.hidden = false;
      if (!priceEl) return;
      if (uplift <= 0) { priceEl.textContent = ''; return; }
      var delta = uplift * ((col === 'premium' ? 1 : 0) - (selPremium ? 1 : 0));  // pence
      if (delta === 0)      priceEl.textContent = '+ ' + formatMoney(0);
      else if (delta > 0)   priceEl.textContent = '+ ' + formatMoney(delta);
      else                  priceEl.textContent = '− ' + formatMoney(-delta);
    });
  }
  function commitMount(m) {
    if (!m) return;
    state.mount = m;
    if (OCC) occReconcileMount();
    syncMountButtons();
    updateMountColourPrices();
    syncPremiumPickers();
    if (typeof updatePersWrap === 'function') { updatePersWrap(); updatePersPrice(); }
    if (OCC) { hidePremiumPickers(); if (typeof occRenderControls === 'function') occRenderControls(); }
    updatePriceAndPhotoSize();
    renderPreview();
    renderCompactMount();
  }
  if (elMountOpenings) {
    elMountOpenings.addEventListener('click', function (e) {
      var b = e.target.closest('[data-mount-opening]');
      if (!b) return;
      var col = (state.mount && state.mount.colour) || 'white';
      commitMount(resolveMount(col, b.dataset.mountOpening));
    });
  }
  elMountColourBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      var apKey = (state.mount && state.mount.aperture) || firstAperture();
      commitMount(resolveMount(b.dataset.mountColourBtn, apKey));
    });
  });

  /* ---------- Premium mount: pattern + colour pickers ---------- */
  function apertureLabelFor(apKey) {
    var ap = aperturesFor(state.variant.title);
    return ap && ap[apKey] ? ap[apKey].label : null;
  }
  function premiumBundleFor(apKey) {
    var label = apertureLabelFor(apKey);
    if (!label) return null;
    var needle = 'Premium Mount (' + label + ' opening)';
    return ((state.variant && state.variant.bundles) || []).find(function (b) {
      return b.name && b.name.indexOf(needle) !== -1;
    }) || null;
  }
  // What the cart ACTUALLY charges for a bundle, in presentment minor units.
  // Bundle Pro's cart transform adds Math.round(bundle.price * rate) whole units
  // on top of the frame line, where bundle.price is in the STORE's base currency
  // and rate is Shopify.currency.rate (window.PFCBundleRate(), layout/theme.liquid)
  // -- FX only; the market's price-list % is NOT applied to add-ons. Showing the board variant's storefront price instead made the page and the
  // cart disagree on every converted market -- US store /en-gb/ 15x20cm: page
  // £26, cart £28; UK store Australia: premium charged 15.75 AUD MORE than shown
  // (2026-09-11). Whole units, like the cart; on the base market this also
  // removes the old 25p gap (£8.25 bundle charged as £8).
  // The rate is a property of the HOST LINE, not of the page: Bundle Pro divides
  // this host's presentment cost by this host's base price, so a market rounding
  // rule that lands two variants on different multiples is reproduced exactly.
  // Pass the host whose line the add-on will ride on; defaults to the selected
  // frame variant. No `base` on that variant (not backfilled) -> FX, as before.
  function bundleRateFor(v) {
    v = v || state.variant;
    if (window.PFCBundleRate) return window.PFCBundleRate(v && v.price, v && v.base);
    var r = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    return r > 0 ? r : 1;
  }
  function bundleChargedPence(b, v) {
    if (!b) return null;
    return Math.round(addonChargeMajor(b.price, bundleRateFor(v)) * 100);
  }
  // The Standard bundle the ATC will select for this aperture -- same lookup,
  // same order (collapsed "Standard Mount" first, legacy per-colour name second),
  // so the displayed price is always the price of the bundle actually sent.
  function standardBundleFor(apKey, colour) {
    var label = apertureLabelFor(apKey);
    if (!label) return null;
    var col = (colour && colour !== 'premium') ? colour : 'white';
    var colCap = col.charAt(0).toUpperCase() + col.slice(1);
    var bundles = (state.variant && state.variant.bundles) || [];
    var stdNeedle = 'Standard Mount (' + label + ' opening)';
    var legacyNeedle = colCap + ' Mount (' + label + ' opening)';
    return bundles.find(function (b) { return b.name && b.name.indexOf(stdNeedle) !== -1; })
        || bundles.find(function (b) { return b.name && b.name.indexOf(legacyNeedle) !== -1; })
        || null;
  }
  // Price shown against a size in the pickers. On occasion pages the printed
  // Premium mount is required, so a bare-frame price would understate every size:
  // add that size's Premium bundle as the cart charges it. Premium is priced by
  // the board's external size, so every opening at a size carries the same price.
  function sizeListPrice(v) {
    var p = Number(v.price);
    if (!OCC) return p;
    var b = (v.bundles || []).find(function (x) { return x.name && x.name.indexOf('Premium Mount (') !== -1; });
    var c = bundleChargedPence(b, v);
    return c == null ? p : p + c;
  }
  function premiumBundlePriceFor(apKey) {
    return bundleChargedPence(premiumBundleFor(apKey));
  }
  function syncPremiumPickers() {
    var prem = isPremiumMount();
    elMountPatternsAll.forEach(function (el) { el.hidden = !prem; });
    elStripeWrapsAll.forEach(function (el) { el.hidden = !prem; });
    if (prem) {
      var gr = groupOf(state.mount && state.mount.pattern);
      elPatternGrids.forEach(function (g) {
        Array.from(g.querySelectorAll('[data-pattern-group]')).forEach(function (b) {
          b.setAttribute('aria-pressed', gr && b.dataset.patternGroup === gr.group ? 'true' : 'false');
        });
        var scaleRow = g.querySelector('.pfc-moulding__pattern-scales');
        if (scaleRow) renderScaleRow(scaleRow, gr);
      });
    }
  }
  // Style chip uses the 'Regular' scale (or first) as its swatch.
  function styleRepresentative(gr) {
    return gr.items.find(function (i) { return i.scaleLabel === 'Regular'; }) || gr.items[0];
  }
  // Two-tier picker: a row of STYLE buttons + a SCALE chip row for the active style.
  function renderPatternPicker() {
    elPatternGrids.forEach(function (grid) {
      grid.innerHTML = '';
      var styleRow = document.createElement('div');
      styleRow.className = 'pfc-moulding__pattern-styles';
      var scaleRow = document.createElement('div');
      scaleRow.className = 'pfc-moulding__pattern-scales';
      grid.appendChild(styleRow);
      grid.appendChild(scaleRow);

      patternGroups().forEach(function (gr) {
        var rep = styleRepresentative(gr);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pfc-moulding__pattern-btn';
        b.dataset.patternGroup = gr.group;
        var chip = document.createElement('span');
        chip.className = 'pfc-moulding__pattern-chip';
        chip.dataset.patternChip = rep.key;
        chip.style.background = patternChipGradient(rep);
        var lbl = document.createElement('span');
        lbl.className = 'pfc-moulding__pattern-name';
        lbl.textContent = gr.label;
        b.appendChild(chip);
        b.appendChild(lbl);
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', function () {
          if (!isPremiumMount()) return;
          var cur = groupOf(state.mount && state.mount.pattern);
          if (!cur || cur.group !== gr.group) state.mount.pattern = styleRepresentative(gr).key;
          renderScaleRow(scaleRow, gr);
          syncPremiumPickers();
          renderPreview();
        });
        styleRow.appendChild(b);
      });

      renderScaleRow(scaleRow, groupOf(state.mount && state.mount.pattern));
    });
  }
  // Scale sub-chips for a style; hidden when the style has a single scale.
  function renderScaleRow(scaleRow, gr) {
    scaleRow.innerHTML = '';
    if (!gr || gr.items.length < 2) { scaleRow.hidden = true; return; }
    scaleRow.hidden = false;
    gr.items.forEach(function (p) {
      var s = document.createElement('button');
      s.type = 'button';
      s.className = 'pfc-moulding__pattern-scale';
      s.dataset.patternKey = p.key;
      s.textContent = p.scaleLabel || p.label;
      s.setAttribute('aria-pressed', p.key === (state.mount && state.mount.pattern) ? 'true' : 'false');
      s.addEventListener('click', function () {
        if (!isPremiumMount()) return;
        state.mount.pattern = p.key;
        syncPremiumPickers();
        renderPreview();
      });
      scaleRow.appendChild(s);
    });
  }
  function renderStripePicker() {
    elStripeGrids.forEach(function (grid) {
      grid.innerHTML = '';
      STRIPE_PRESETS.forEach(function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pfc-moulding__stripe-swatch';
        b.dataset.stripeHex = p.hex;
        b.title = p.label;
        b.setAttribute('aria-label', p.label);
        b.setAttribute('aria-pressed', p.hex.toLowerCase() === state.stripeColour.toLowerCase() ? 'true' : 'false');
        b.style.background = p.hex;
        b.addEventListener('click', function () { setStripeColour(p.hex); });
        grid.appendChild(b);
      });
    });
    elStripeCustoms.forEach(function (inp) { inp.value = state.stripeColour; });
  }
  function setStripeColour(hex) {
    var norm = normaliseHex(hex);
    if (!norm) return;
    state.stripeColour = norm;
    elStripeGrids.forEach(function (grid) {
      Array.from(grid.querySelectorAll('[data-stripe-hex]')).forEach(function (b) {
        b.setAttribute('aria-pressed', b.dataset.stripeHex.toLowerCase() === norm.toLowerCase() ? 'true' : 'false');
      });
    });
    elStripeCustoms.forEach(function (inp) { if (inp.value.toLowerCase() !== norm.toLowerCase()) inp.value = norm; });
    updatePatternChips();
    renderPreview();
  }
  function updatePatternChips() {
    elPatternGrids.forEach(function (grid) {
      MOUNT_PATTERNS.forEach(function (p) {
        var chip = grid.querySelector('[data-pattern-chip="' + p.key + '"]');
        if (chip) chip.style.background = patternChipGradient(p);
      });
    });
  }
  elStripeCustoms.forEach(function (inp) {
    inp.addEventListener('input', function () { setStripeColour(inp.value); });
  });
  renderPatternPicker();
  renderStripePicker();

  /* ---------- Printed text / logo add-on ----------
     Prints into the bottom mount border. Premium = a pattern AND/OR printed text
     (Decision A): adding text upgrades a white/black mount to the Premium bundle
     (same price as a pattern; included if a pattern is already chosen). Only
     offered when a mount is present (needs a border to print on). */
  function persAvailable() { return !!(cfg.personalisation && cfg.personalisation.available); }
  function mountActiveWithAperture() {
    if (!state.mount) return false;
    var ap = aperturesFor(state.variant.title);
    return !!(ap && ap[state.mount.aperture]);
  }
  // Occasion pages always show their lines (placeholders until the customer types).
  function persHasText() { return !!(OCC || (state.pers.heading && state.pers.heading.trim()) || (state.pers.line2 && state.pers.line2.trim())); }
  function persHasLogo() { return !!(state.pers.logoOn && state.pers.logoImg); }
  function persHasContent() { return persHasText() || persHasLogo(); }
  function persActive() { return !!(state.pers.on && persAvailable() && persHasContent() && mountActiveWithAperture() && persFits()); }

  /* ---------- Narrow-border guard (mirrors pfc-mount.js) ----------
     The printed line sits in the BOTTOM mount border minus the rebate-safe edge
     inset and the bevel gap. A small print with a big opening leaves nothing to
     print on — e.g. a 150×200mm board with a 5×7" (127×178mm) window gives an
     11mm bottom border, so the band is 11 − 12 − 3 = NEGATIVE and the draw was
     silently skipped: the customer typed text and saw none. Block the add-on
     with a reason instead. Threshold is in mm — the old `> 8` test was canvas
     px, so the same mount passed or failed depending on viewport size. */
  var PERS_MIN_BAND_MM = 14;   // ≈4mm cap height once the 3mm box pad comes off
  function persBottomBorderMm() {
    if (!mountActiveWithAperture()) return 0;
    var d = printDimsMm(state.variant);
    if (!d || !d.h) return 0;
    var ap = aperturesFor(state.variant.title)[state.mount.aperture];
    var apW = ap.w, apH = ap.h;
    // Aperture dims are stored portrait; swap to the displayed orientation.
    if (apW !== apH && state.orientation === 'landscape') { var t = apW; apW = apH; apH = t; }
    return (d.h - apH) / 2;
  }
  function persBandMm() {
    var bb = persBottomBorderMm();
    return bb > 0 ? bb - PERS_EDGE_MM - persGapMm() : 0;
  }
  function persFits() { return persBandMm() >= PERS_MIN_BAND_MM; }
  function persFitMessage() {
    var need = Math.ceil(PERS_MIN_BAND_MM + PERS_EDGE_MM + PERS_GAP_MM);
    return 'Printed text needs a wider mount border — this size leaves '
      + Math.round(persBottomBorderMm()) + 'mm below the opening and printing needs at least '
      + need + 'mm. Choose a larger size or a smaller opening.';
  }
  // Disable the add-on (and say why) whenever the bottom border is too narrow.
  function updatePersFit() {
    var fits = persFits();
    if (elPersToggle) elPersToggle.disabled = !fits;
    if (elPersWrap) elPersWrap.classList.toggle('is-blocked', !fits);
    if (elPersPanel) elPersPanel.hidden = !fits || !state.pers.on;
    if (elPersFitNote) {
      elPersFitNote.hidden = fits;
      if (!fits) elPersFitNote.textContent = persFitMessage();
    }
  }
  function isPatternedMount() { return isPremiumMount(); }   // colour === 'premium' => a drawn pattern
  function mountBaseHex() {
    if (!state.mount) return '#fafaf6';
    if (state.mount.colour === 'black') return '#1a1a1a';
    return '#fafaf6';   // white OR premium (pattern base is white)
  }
  function persInk() {
    var hex = mountBaseHex().replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    var r = parseInt(hex.substr(0, 2), 16), g = parseInt(hex.substr(2, 2), 16), b = parseInt(hex.substr(4, 2), 16);
    return ((0.299 * r + 0.587 * g + 0.114 * b) / 255) > 0.55 ? '#1a1a1a' : '#ffffff';
  }
  function fontFamilyCss(name) { return '"' + name + '", ' + UI_FONT; }

  /* ---------- Occasion templates (only when OCC) ---------- */
  var OCC_CAP_MM = 5;   // names size; thin faces carry their own 7mm minimum
  function occLettering() { return MC.letteringByKey(state.occ.lettering); }
  function occTemplate() { return MC.persTemplateByKey(state.occ.template); }
  function occCapMm() { return Math.max(occLettering().names.minMm || 0, OCC_CAP_MM); }
  function occInkHex() { return MC.persInkHex(state.occ.ink, mountBaseHex()); }
  // The ink that actually prints: dark inks switch to white on a dark board,
  // so name it by what prints (production reads this label).
  function occInk() {
    var ink = MC.PERS_INKS.find(function (i) { return i.key === state.occ.ink; }) || MC.PERS_INKS[0];
    var hex = occInkHex();
    if (hex.toLowerCase() !== ink.hex.toLowerCase()) ink = MC.PERS_INKS.find(function (i) { return i.hex.toLowerCase() === hex.toLowerCase(); }) || ink;
    return ink;
  }
  function occKeylineClearMm() {
    var key = occTemplate().keyline; if (!key) return 0;
    var p = patternByKey(key);
    return (p.gapMm || 0) + ((p.lines || 1) - 1) * (p.spacingMm || 0) + (p.lineMm || 0);
  }
  // The two lines. The preview shows placeholders until the customer types;
  // the print file never does (forPrint), and add-to-cart requires the names.
  function occLines(forPrint) {
    var n = (state.pers.heading || '').trim(), d = (state.pers.line2 || '').trim();
    if (forPrint) return [n, d];
    return [n || OCC.namesPlaceholder || 'Emma & James', d || (n ? '' : (OCC.datePlaceholder || '12 June 2026'))];
  }
  // The v2 spec. `style` is a COPY of the resolved lettering, so an order
  // prints as it was bought even if PERS_LETTERING changes later.
  function occSpec(forPrint) {
    var L = occLettering();
    return { v: 2, style: JSON.parse(JSON.stringify({ key: L.key, names: L.names, date: L.date })),
      template: state.occ.template, lines: occLines(forPrint), capMm: occCapMm(), inkHex: occInkHex(), align: 'center', font: L.names.font };
  }
  // £ uplift of moving the current mount to the Premium tier (text on white/black).
  function persPremiumUplift() {
    if (!mountActiveWithAperture()) return 0;
    var pp = premiumBundlePriceFor(state.mount.aperture);   // pence
    if (pp == null) return 0;
    return Math.max(0, (pp - (state.mount.price || 0)) / 100);
  }
  // The mount line price including the text upgrade: premium price when text is
  // active on a non-premium mount, else the selected mount's own price.
  function mountLinePrice() {
    if (!state.mount) return 0;
    if (persActive() && state.mount.colour !== 'premium') {
      var pp = premiumBundlePriceFor(state.mount.aperture);
      if (pp != null) return pp;
    }
    return state.mount.price;
  }
  function updatePersPrice() {
    if (!elPersPrice) return;
    if (isPremiumMount()) { elPersPrice.textContent = 'included'; return; }   // pattern already premium
    var up = persPremiumUplift();
    elPersPrice.textContent = up > 0 ? ('+ ' + formatMoney(Math.round(up * 100))) : '';
  }
  function persChanged() { updatePersPrice(); updateMountColourPrices(); updatePriceAndPhotoSize(); renderPreview(); }

  function drawLogoFit(ctx, img, x, y, w, h, align) {
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height; if (!iw || !ih) return;
    var s = Math.min(w / iw, h / ih), dw = iw * s, dh = ih * s;
    var dx = align === 'left' ? x : align === 'right' ? (x + w - dw) : (x + (w - dw) / 2);
    ctx.drawImage(img, dx, y + (h - dh) / 2, dw, dh);
  }
  // The on-page canvas inherits the page's CSS letter-spacing (0.6px), which
  // canvas text honours — so the preview drew every line wider than the print
  // file (off-screen canvases have none). Mount text is always drawn unspaced.
  function drawTextBlock(ctx, lines, x, y, w, h, align, ink, capPx) {
    lines = lines.filter(function (t) { return t && t.trim(); });
    if (!lines.length) return;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    var fam = fontFamilyCss(state.pers.font), n = lines.length, lineH = h / (n + 0.4), base = lineH * 0.74;
    if (capPx) base = Math.min(base, capPx);   // cap to the chosen physical mm size
    var size = base;
    lines.forEach(function (t) {
      if (pf().exact()) return;   // fit-by-space: the chosen size is what prints
      var s = base; ctx.font = '600 ' + s + 'px ' + fam;
      while (ctx.measureText(t).width > w && s > 6) { s -= 1; ctx.font = '600 ' + s + 'px ' + fam; }
      if (s < size) size = s;
    });
    ctx.font = '600 ' + size + 'px ' + fam;
    ctx.fillStyle = ink; ctx.textAlign = align;
    // Centre each line on the font's own ascent/descent, NOT textBaseline
    // 'middle': Chrome and the print-file app (node-canvas) disagree on where
    // 'middle' is (~0.8 mm at M), but agree exactly on these metrics — and this
    // is where the print app has always put the line.
    ctx.textBaseline = 'alphabetic';
    var fm = ctx.measureText('Hxg');
    var asc = fm.fontBoundingBoxAscent != null ? fm.fontBoundingBoxAscent : (fm.emHeightAscent != null ? fm.emHeightAscent : size * 0.8);
    var desc = fm.fontBoundingBoxDescent != null ? fm.fontBoundingBoxDescent : (fm.emHeightDescent != null ? fm.emHeightDescent : size * 0.2);
    var mid = (asc - desc) / 2;
    var tx = align === 'left' ? x : align === 'right' ? (x + w) : (x + w / 2);
    var lh = size * 1.32;
    var startY = y + (h - lh * n) / 2 + lh / 2;
    lines.forEach(function (t, i) { ctx.fillText(t, tx, startY + i * lh + mid); });
  }
  function persLogoRect(x, y, w, h, align) {
    var logo = state.pers.logoImg; if (!logo) return null;
    var iw = logo.naturalWidth || logo.width, ih = logo.naturalHeight || logo.height; if (!iw || !ih) return null;
    var s = Math.min(w / iw, h / ih), dw = iw * s, dh = ih * s;
    var dx = align === 'left' ? x : align === 'right' ? (x + w - dw) : (x + (w - dw) / 2);
    return { x: dx, y: y + (h - dh) / 2, w: dw, h: dh };
  }
  function persTextRect(ctx, x, y, w, h, align, capPx) {
    var ls = [state.pers.heading, state.pers.line2].filter(function (t) { return t && t.trim(); });
    if (!ls.length) return null;
    var prevLs = ctx.letterSpacing;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    var fam = fontFamilyCss(state.pers.font), n = ls.length, lineH = h / (n + 0.4), base = lineH * 0.74, size = base;
    if (capPx) base = size = Math.min(base, capPx);
    ls.forEach(function (t) { if (pf().exact()) return; var s = base; ctx.font = '600 ' + s + 'px ' + fam; while (ctx.measureText(t).width > w && s > 6) { s -= 1; ctx.font = '600 ' + s + 'px ' + fam; } if (s < size) size = s; });
    ctx.font = '600 ' + size + 'px ' + fam;
    var tw = 0; ls.forEach(function (t) { tw = Math.max(tw, ctx.measureText(t).width); });
    var tl = align === 'left' ? x : align === 'right' ? (x + w - tw) : (x + (w - tw) / 2);
    var lh = size * 1.32;
    var startY = y + (h - lh * n) / 2 + lh / 2;
    if ('letterSpacing' in ctx) ctx.letterSpacing = prevLs;
    return { x: tl, y: startY - size / 2, w: tw, h: (n - 1) * lh + size };
  }
  function persUnion(a, b) {
    if (!a) return b; if (!b) return a;
    var x1 = Math.min(a.x, b.x), y1 = Math.min(a.y, b.y), x2 = Math.max(a.x + a.w, b.x + b.w), y2 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
  }
  function drawPersonalisation(ctx, rect, ink, capPx, boxPadPx) {
    if (OCC) {
      var op = boxPadPx || 0, box = { x: rect.x + op, y: rect.y + op, w: rect.w - op * 2, h: rect.h - op * 2 };
      if (box.w <= 4 || box.h <= 4) box = rect;
      MC.drawStyledBox(ctx, occSpec(drawPersonalisation.forPrint), box, capPx);
      return;
    }
    var align = state.pers.align || 'center', lines = [state.pers.heading, state.pers.line2];
    var hasText = persHasText(), hasLogo = persHasLogo();
    if (!hasText && !hasLogo) return;
    var pad = boxPadPx || Math.min(rect.w, rect.h) * 0.10;
    var inner = { x: rect.x + pad, y: rect.y + pad, w: rect.w - pad * 2, h: rect.h - pad * 2 };
    if (inner.w <= 4 || inner.h <= 4) inner = { x: rect.x, y: rect.y, w: rect.w, h: rect.h };

    var logoZone = null, textZone = null;
    if (hasText && hasLogo) {
      var gap = inner.h * 0.06, logoH = inner.h * 0.42, textH = inner.h - logoH - gap;
      if (state.pers.logoPos === 'below') {
        textZone = { x: inner.x, y: inner.y, w: inner.w, h: textH };
        logoZone = { x: inner.x, y: inner.y + textH + gap, w: inner.w, h: logoH };
      } else {
        logoZone = { x: inner.x, y: inner.y, w: inner.w, h: logoH };
        textZone = { x: inner.x, y: inner.y + logoH + gap, w: inner.w, h: textH };
      }
    } else if (hasText) { textZone = inner; } else {
      // Logo only — cap height (centred) so a lone crest doesn't fill the border.
      var lh = inner.h * 0.58;
      logoZone = { x: inner.x, y: inner.y + (inner.h - lh) / 2, w: inner.w, h: lh };
    }

    // Backing box ONLY over a pattern — filled with the mount's own base colour
    // (clears the pattern locally for legibility), NOT a brilliant-white label.
    if (isPatternedMount()) {
      var b = null;
      if (logoZone) b = persUnion(b, persLogoRect(logoZone.x, logoZone.y, logoZone.w, logoZone.h, align));
      if (textZone) b = persUnion(b, persTextRect(ctx, textZone.x, textZone.y, textZone.w, textZone.h, align, capPx));
      if (b && b.w > 0 && b.h > 0) {
        var bx = Math.max(rect.x, b.x - pad), by = Math.max(rect.y, b.y - pad);
        var bw = Math.min(rect.x + rect.w, b.x + b.w + pad) - bx;
        var bh = Math.min(rect.y + rect.h, b.y + b.h + pad) - by;
        var br = Math.min(bw, bh) * 0.2;
        ctx.save();
        ctx.beginPath();
        if (ctx.roundRect) { ctx.roundRect(bx, by, bw, bh, br); } else { ctx.rect(bx, by, bw, bh); }
        ctx.fillStyle = mountBaseHex();
        ctx.fill();
        ctx.restore();
      }
      ink = persInk();
    }
    ctx.save();
    if (logoZone && state.pers.logoImg) drawLogoFit(ctx, state.pers.logoImg, logoZone.x, logoZone.y, logoZone.w, logoZone.h, align);
    if (textZone) drawTextBlock(ctx, lines, textZone.x, textZone.y, textZone.w, textZone.h, align, ink, capPx);
    ctx.restore();
  }
  // Personalisation text size (physical heading em mm by S/M/L) + rebate-safe
  // geometry — mirrors pfc-mount.js so printed text matches across customisers.
  var PERS_CAP_MM = { xs: 3.5, s: 5, m: 7, l: 9 };
  // Fit-by-space (assets/pfc-pers-fit-core.js): the chosen size prints exactly,
  // the text zone is the opening width, and the room left is what varies. Off
  // unless the theme setting pers_fit_v2 is on or the URL has ?pers_fit=1 —
  // when off, pf() is a stub and every path below behaves as before.
  var PF = window.PFCPersFit || null;
  var persFit = null;
  // A function declaration, so it works during the first render — which runs
  // before this part of the file has executed (a `var` stub would be undefined).
  function pf() {
    return persFit || pf.stub || (pf.stub = {
      enabled: false, update: function () {}, addError: function () { return null; }, exact: function () { return false; }
    });
  }
  // Text zone in mm ({w, h}) for the current mount, or null when there is none.
  function persZoneMm() {
    if (!PF || !mountActiveWithAperture()) return null;
    var d = printDimsMm(state.variant); if (!d || !d.w) return null;
    var ap = aperturesFor(state.variant.title)[state.mount.aperture];
    var apW = (ap.w !== ap.h && state.orientation === 'landscape') ? ap.h : ap.w;
    var band = persBandMm(); if (band <= 0) return null;
    return { w: PF.zoneWidthMm(apW, d.w, PERS_EDGE_MM, PERS_BOXPAD_MM),
             h: PF.textZoneHeightMm(band, PERS_BOXPAD_MM, persHasLogo()) };
  }
  // The band the text is drawn in, in canvas px. Legacy: the full board width
  // inside the rebate-safe edge. Fit-by-space: centred, as wide as the opening.
  function persRect(boardLeft, boardWpx, top, bottom, pxPerMm) {
    var edge = PERS_EDGE_MM * pxPerMm;
    var z = (pf().enabled && !OCC) ? persZoneMm() : null;   // occasion lettering: full band, centred
    if (!z) return { x: boardLeft + edge, y: top, w: boardWpx - edge * 2, h: bottom - top };
    var pad = PERS_BOXPAD_MM * pxPerMm, zw = z.w * pxPerMm;
    var ins = PF.alignInsetMm(persMeasureCtx(), fontFamilyCss(state.pers.font),
      [state.pers.heading, state.pers.line2], persCapMm(), state.pers.align || 'center');
    return { x: boardLeft + (boardWpx - zw) / 2 - pad + ins.l * pxPerMm, y: top,
             w: zw + pad * 2 - (ins.l + ins.r) * pxPerMm, h: bottom - top };
  }
  function persCapMm() { return OCC ? occCapMm() : (PERS_CAP_MM[state.pers.size] || PERS_CAP_MM.m); }
  var PERS_EDGE_MM = 12, PERS_GAP_MM = 3, PERS_BOXPAD_MM = 3;
  // Gap between the opening and the text band. An occasion keyline template
  // starts the band below its outer rule; everything else is unchanged.
  function persGapMm() { return PERS_GAP_MM + (OCC ? occKeylineClearMm() : 0); }
  function persMeasureCtx() { persMeasureCtx._c = persMeasureCtx._c || document.createElement('canvas').getContext('2d'); return persMeasureCtx._c; }
  function persStepFitPx(zoneW, zoneH, capPx) {
    var lines = [state.pers.heading, state.pers.line2].filter(function (t) { return t && t.trim(); });
    var n = lines.length || 1, fam = fontFamilyCss(state.pers.font);
    var size = Math.min((zoneH / (n + 0.4)) * 0.74, capPx, zoneH / (1.32 * n));
    var ctx = persMeasureCtx();
    lines.forEach(function (t) { var s = size; ctx.font = '600 ' + s + 'px ' + fam; while (ctx.measureText(t).width > zoneW && s > 6) { s -= 1; ctx.font = '600 ' + s + 'px ' + fam; } if (s < size) size = s; });
    return Math.max(6, size);
  }
  function syncPersSizeSteps(zoneW, zoneH, pxPerMm) {
    if (pf().enabled) { pf().update(); return; }
    if (!elPersSizeBtns.length) return;
    if (!persHasText() || !zoneW || !zoneH) {
      if (elPersSizeGroup) elPersSizeGroup.hidden = false;
      if (elPersSizeNote) elPersSizeNote.hidden = true;
      elPersSizeBtns.forEach(function (b) { b.disabled = false; b.setAttribute('aria-pressed', b.dataset.persSize === state.pers.size ? 'true' : 'false'); });
      return;
    }
    var keys = ['s', 'm', 'l'], eff = {};
    keys.forEach(function (k) { eff[k] = Math.round(persStepFitPx(zoneW, zoneH, PERS_CAP_MM[k] * pxPerMm)); });
    var allSame = (eff.s === eff.m && eff.m === eff.l);
    if (elPersSizeGroup) elPersSizeGroup.hidden = allSame;
    if (elPersSizeNote) elPersSizeNote.hidden = !allSame;
    if (allSame) return;
    var prevEff = null;
    keys.forEach(function (k) {
      var btn = elPersSizeBtns.find(function (b) { return b.dataset.persSize === k; });
      if (!btn) return;
      btn.disabled = (prevEff !== null && eff[k] <= prevEff);
      btn.setAttribute('aria-pressed', k === state.pers.size ? 'true' : 'false');
      prevEff = eff[k];
    });
  }
  // Transparent PNG of the printed personalisation at mount-board aspect, mirroring
  // the preview's bottom-border placement (for fulfillment).
  // Exported at PRINT resolution (300dpi) — this is the fulfilment/cutter asset,
  // so it must be print-quality, not a screen-size bitmap. (Was 2000px wide ≈
  // 60dpi at A1, which the print-file admin tool then upscaled → blurry text.)
  var PERS_EXPORT_DPI = 300;
  function renderPersExport() {
    if (!persActive()) return null;
    var d = printDimsMm(state.variant); if (!d.w || !d.h) return null;
    var ap = aperturesFor(state.variant.title); var apd = ap && state.mount ? ap[state.mount.aperture] : null;
    if (!apd) return null;
    var apH = apd.h, apW = apd.w;
    if (apW !== apH && state.orientation === 'landscape') { apH = apd.w; }
    var s = PERS_EXPORT_DPI / 25.4, W = Math.round(d.w * s), H = Math.round(d.h * s); // px per mm @300dpi
    var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');
    var apPxH = Math.min(apH * s, H * 0.92);
    var apBottom = (H + apPxH) / 2;
    var edge = PERS_EDGE_MM * s, gap = persGapMm() * s;
    var bandTop = apBottom + gap, bandBot = H - edge;
    var rect = persRect(0, W, bandTop, bandBot, s);
    if (rect.h < 8) return null;
    drawPersonalisation.forPrint = true;
    try { drawPersonalisation(ctx, rect, persInk(), persCapMm() * s, PERS_BOXPAD_MM * s); }
    finally { drawPersonalisation.forPrint = false; }
    // Machine-readable spec so the print-file admin app can RE-RENDER the text
    // crisply at any dpi (vector), instead of upscaling this PNG. Band given in
    // mm (resolution-independent). Mirrored into _addons_custom via `carry`.
    var r1 = function (n) { return Math.round(n * 10) / 10; };
    lastPersRenderSpec = {
      v: 1,
      lines: [state.pers.heading, state.pers.line2],
      font: state.pers.font,
      align: state.pers.align || 'center',
      capMm: persCapMm(),
      exact: pf().enabled || undefined,   // fit-by-space: capMm is the printed size, text pre-fitted to the band (omitted when off)
      inkHex: persInk(),
      boardWmm: r1(d.w), boardHmm: r1(d.h),
      bandMm: { x: r1(rect.x / s), y: r1(rect.y / s), w: r1(rect.w / s), h: r1(rect.h / s) },
      boxPadMm: PERS_BOXPAD_MM,
      patterned: isPatternedMount(),
      mountBaseHex: mountBaseHex()
    };
    // Occasion: the v2 spec (resolved lettering + template) replaces the v1 text fields.
    if (OCC) {
      var ov = occSpec(true);
      ['v', 'style', 'template', 'lines', 'capMm', 'inkHex', 'align', 'font'].forEach(function (k) { lastPersRenderSpec[k] = ov[k]; });
      delete lastPersRenderSpec.exact;
    }
    return cv.toDataURL('image/png');
  }
  var lastPersRenderSpec = null;   // set by renderPersExport, read when building carry
  // Uploads the rendered overlay and, for a logo design, the ORIGINAL logo file:
  // the print-file app redraws the logo from it (the overlay is screen-res), and
  // without it every logo was dropped from the print file.
  function uploadPersImage(dataUrl) {
    var key = uploadKey('personalisation.png');
    var jobs = [uploadOne(dataUrl, key)];
    var logoKey = null;
    if (persHasLogo() && state.pers.logoDataUrl) {
      logoKey = uploadKey('personalisation-logo' + (/^data:image\/png/i.test(state.pers.logoDataUrl) ? '.png' : '.jpg'));
      jobs.push(uploadOne(state.pers.logoDataUrl, logoKey));
    }
    return Promise.all(jobs).then(function () {
      var logoUrl = logoKey ? publicS3Url(logoKey) : null;
      if (logoUrl && lastPersRenderSpec) lastPersRenderSpec.logo = { url: logoUrl, pos: state.pers.logoPos || 'above' };
      return { url: publicS3Url(key), logoUrl: logoUrl };
    });
  }
  function loadPersLogoFile(file) {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function (e) {
      state.pers.logoDataUrl = e.target.result;
      var img = new Image();
      img.onload = function () {
        state.pers.logoImg = img;
        if (elPersLogoDrop) elPersLogoDrop.dataset.hasImage = 'true';
        if (elPersLogoThumb) elPersLogoThumb.style.backgroundImage = 'url("' + state.pers.logoDataUrl + '")';
        if (elPersLogoTitle) elPersLogoTitle.textContent = 'Logo added — click to replace';
        persChanged();
      };
      img.src = state.pers.logoDataUrl;
    };
    reader.readAsDataURL(file);
  }
  function setPersFontValue() {
    if (!elPersFontValue) return;
    elPersFontValue.textContent = state.pers.font;
    elPersFontValue.style.fontFamily = fontFamilyCss(state.pers.font);
  }
  function renderPersFonts() {
    if (!elPersFontList) return;
    elPersFontList.innerHTML = '';
    PERS_FONTS.forEach(function (f) {
      var li = document.createElement('li');
      li.className = 'pfc-moulding__select-option';
      li.setAttribute('role', 'option');
      li.dataset.font = f;
      li.textContent = f;
      li.style.fontFamily = fontFamilyCss(f);
      li.setAttribute('aria-selected', f === state.pers.font ? 'true' : 'false');
      li.addEventListener('click', function () {
        state.pers.font = f;
        Array.from(elPersFontList.children).forEach(function (o) {
          o.setAttribute('aria-selected', o.dataset.font === f ? 'true' : 'false');
        });
        setPersFontValue();
        setPersFontOpen(false);
        renderPreview();
      });
      elPersFontList.appendChild(li);
    });
    setPersFontValue();
  }
  function setPersFontOpen(open) {
    if (!elPersFontList || !elPersFontTrigger) return;
    elPersFontList.hidden = !open;
    elPersFontTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (elPersFontSelect) elPersFontSelect.classList.toggle('is-open', open);
  }
  function updateLogoFields() { if (elPersLogoFields) elPersLogoFields.hidden = !state.pers.logoOn; }
  function loadPersFonts() {
    if (!document.fonts || !document.fonts.load) return;
    PERS_FONTS.forEach(function (f) { try { document.fonts.load('600 32px "' + f + '"'); } catch (_) {} });
    if (document.fonts.ready) document.fonts.ready.then(function () { renderPreview(); });
  }
  // Pers only offered with a mount (needs a border). Re-evaluated on mount change.
  function updatePersWrap() {
    if (!elPersWrap) return;
    elPersWrap.hidden = !(persAvailable() && mountActiveWithAperture());
    updatePersFit();   // size / opening / orientation can all take the border too narrow
  }
  // Wiring.
  if (elPersToggle) elPersToggle.addEventListener('change', function () {
    if (OCC && !elPersToggle.checked) { elPersToggle.checked = true; return; }   // occasion: the names are required
    state.pers.on = elPersToggle.checked;
    if (elPersPanel) elPersPanel.hidden = !elPersToggle.checked;
    syncPersSizeSteps(0, 0, 1);
    persChanged();
  });
  if (elPersFontTrigger) {
    elPersFontTrigger.addEventListener('click', function () { setPersFontOpen(elPersFontList.hidden); });
    document.addEventListener('click', function (e) {
      if (elPersFontSelect && !elPersFontSelect.contains(e.target)) setPersFontOpen(false);
    });
  }
  elPersAlignBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      state.pers.align = b.dataset.persAlign;
      elPersAlignBtns.forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
      renderPreview();
    });
  });
  elPersSizeBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.persSize === state.pers.size ? 'true' : 'false'); });
  elPersSizeBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      state.pers.size = b.dataset.persSize;
      elPersSizeBtns.forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
      renderPreview();
    });
  });
  if (PF && elPersPanel && !OCC) {   // occasion lettering lays itself out (core layoutStyled)
    persFit = PF.controller({
      state: function () { return state.pers; },
      zoneMm: persZoneMm,
      familyCss: fontFamilyCss,
      sizeBtns: elPersSizeBtns, sizeNote: elPersSizeNote,
      heading: elPersHeading, line2: elPersLine2,
      rerender: function () { renderPreview(); }
    });
  }
  if (elPersHeading) elPersHeading.addEventListener('input', function () { state.pers.heading = elPersHeading.value; persChanged(); });
  if (elPersLine2) elPersLine2.addEventListener('input', function () { state.pers.line2 = elPersLine2.value; persChanged(); });
  if (elPersLogoToggle) elPersLogoToggle.addEventListener('change', function () { state.pers.logoOn = elPersLogoToggle.checked; updateLogoFields(); persChanged(); });
  elPersLogoPosBtns.forEach(function (b) {
    b.addEventListener('click', function () {
      state.pers.logoPos = b.dataset.persLogoPos;
      elPersLogoPosBtns.forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
      renderPreview();
    });
  });
  if (elPersLogoDrop) {
    elPersLogoDrop.addEventListener('click', function (e) {
      if (e.target.closest('input')) return;
      elPersLogoInput && elPersLogoInput.click();
    });
    ['dragenter', 'dragover'].forEach(function (ev) {
      elPersLogoDrop.addEventListener(ev, function (e) { e.preventDefault(); elPersLogoDrop.dataset.dragover = 'true'; });
    });
    ['dragleave', 'dragend', 'drop'].forEach(function (ev) {
      elPersLogoDrop.addEventListener(ev, function () { elPersLogoDrop.removeAttribute('data-dragover'); });
    });
    elPersLogoDrop.addEventListener('drop', function (e) {
      e.preventDefault();
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f && /^image\/(png|jpe?g)$/.test(f.type)) loadPersLogoFile(f);
    });
  }
  if (elPersLogoInput) elPersLogoInput.addEventListener('change', function () {
    var f = elPersLogoInput.files && elPersLogoInput.files[0];
    if (f) loadPersLogoFile(f);
    elPersLogoInput.value = '';
  });
  renderPersFonts();
  loadPersFonts();
  updatePersWrap();

  // "Add a mount?" opt-in: reveal the picker (defaulting to a mount) or clear it.
  if (elAddMount) {
    elAddMount.addEventListener('change', function () {
      // Certificate hubs: adding a mount GROWS the frame instead of shrinking the
      // opening (applyCertFrame swaps the variant + forces the document opening).
      if (certActive()) { applyCertFrame(elAddMount.checked); return; }
      if (MOUNT_REQ && !elAddMount.checked) { elAddMount.checked = true; return; }   // occasion / mount_required hub: the mount is required
      state.mountUserOff = !elAddMount.checked;   // the customer's own choice (see refreshMountUi)
      if (!elAddMount.checked) { state.mount = null; }
      refreshMountUi();      // selects a default mount when opting in
      syncMountButtons();
      updatePriceAndPhotoSize();
      renderPreview();
    });
  }

  // Size buttons are rendered from the active moulding's variants so they can
  // be rebuilt on a live moulding swap (variants/prices/availability differ
  // per moulding). Click handling is delegated on the grid so it survives
  // rebuilds without re-binding.
  // Build the size dropdown listbox + sync the trigger value (function name
  // kept; it now renders the custom select instead of a button grid).
  // H1 reads "<finish> / <frame size>" — the colour/material plus the
  // customer's CHOSEN frame size (e.g. "11" x 14""), not the moulding's profile
  // dimensions. The profile (e.g. "17mm x 17mm") already shows in the gold
  // family label above the title, so repeating it after the finish just
  // confused customers (feedback 2026-07). cfg.productTitle is "<finish> /
  // <profile>"; swap the profile half for the selected variant's size title.
  function frameHeadTitle() {
    var full = cfg.productTitle || '';
    // Only frame hubs opt in (root_cfg.frameSizeInTitle). Moulding/print/mount
    // PDPs leave the title as the product title (with its profile suffix).
    if (!root_cfg.frameSizeInTitle) return full;
    var finish = full.indexOf(' / ') !== -1 ? full.split(' / ')[0] : full;
    // Cert hubs: adding a mount grows the frame (A4 doc → 30×40cm frame). No mount
    // → show the document size (frame IS A4). Mount on → show the actual (grown)
    // frame size + note the document opening, so the H1 stays honest about the
    // larger frame the customer is buying while anchoring on their certificate.
    var sz;
    if (cfg.certMode && cfg.certTargetSize) {
      var vt = (state.variant && state.variant.title) || '';
      sz = (vt && vt !== cfg.certTargetSize)
        ? (vt + ' (' + cfg.certTargetSize + ' opening)')
        : cfg.certTargetSize;
    } else {
      sz = (state.variant && state.variant.title) ? state.variant.title : '';
    }
    return sz ? (finish + ' / ' + sz) : full;
  }

  function renderSizeButtons() {
    if (!elSizeList) return;
    var html = '';
    cfg.variants.forEach(function (v) {
      if (variantUnit(v) !== state.sizeUnit) return;   // cm/inch filter (desktop dropdown)
      if (!sizeVisible(v)) return;                      // per-hub allowlist / specialist-size filter
      var sel = v.id === state.variant.id;
      html += '<li class="pfc-moulding__select-option" role="option" data-variant-id="' + v.id + '"'
        + ' aria-selected="' + (sel ? 'true' : 'false') + '"'
        + (v.available ? '' : ' aria-disabled="true"') + '>'
        + '<span>' + escapeHtml(orientedTitle(v.title)) + '</span>'
        + '<span class="pfc-moulding__select-option-price">' + formatMoney(sizeListPrice(v)) + '</span>'
        + '</li>';
    });
    elSizeList.innerHTML = html;
    // A unit with nothing left to show is a dead button — on a frameOnlySizesOnly
    // hub, aluminium's stock sizes are metric only, so the inch toggle would open
    // an empty list. Hide any unit with no visible sizes on this moulding.
    if (elSizeUnitBtns.length) {
      var unitsWithSizes = 0;
      elSizeUnitBtns.forEach(function (b) {
        var u = b.dataset.sizeUnit;
        var any = cfg.variants.some(function (v) { return variantUnit(v) === u && sizeVisible(v); });
        b.hidden = !any;
        if (any) unitsWithSizes++;
      });
      // One unit left = nothing to toggle between; hide the whole pill group.
      // Desktop size block + mobile sheet each carry a copy.
      root.querySelectorAll('[data-size-unit-toggle]').forEach(function (w) {
        w.hidden = unitsWithSizes < 2;
      });
    }
    if (elSizeValue) elSizeValue.textContent = orientedTitle(state.variant.title);
    if (elTitle) elTitle.textContent = frameHeadTitle();
  }

  function setSizeOpen(open) {
    if (!elSizeList || !elSizeTrigger) return;
    elSizeList.hidden = !open;
    elSizeTrigger.setAttribute('aria-expanded', String(open));
    if (elSizeSelect) elSizeSelect.classList.toggle('is-open', open);
  }
  if (elSizeTrigger) {
    elSizeTrigger.addEventListener('click', function () { setSizeOpen(elSizeList && elSizeList.hidden); });
  }
  if (elSizeList) {
    elSizeList.addEventListener('click', function (e) {
      var li = e.target.closest('[data-variant-id]');
      if (!li || li.getAttribute('aria-disabled') === 'true') return;
      var vid = Number(li.dataset.variantId);
      var v = cfg.variants.find(function (vv) { return vv.id === vid; });
      setSizeOpen(false);
      if (v) { state.desiredSizeTitle = v.title; selectVariant(v); evaluateRecommendation('size-change'); }
    });
  }

  // cm/inch toggle — filters the dropdown to one unit. If the current size is in
  // Display-unit formatters keyed to the cm/in size toggle so every shown
  // dimension converts to inches in inch mode. Moulding face/depth use 2dp
  // decimal inches in inch mode (US 'rabbet' convention), mm in metric.
  function dispIn() { return state.sizeUnit === 'in'; }
  function fmtDimDisp(mm) {
    if (dispIn()) { var v = mm / 25.4; return (v < 2 ? v.toFixed(2) : v.toFixed(1)) + '"'; }
    return (Math.round(mm / 10 * 10) / 10) + 'cm';
  }
  function fmtPairDisp(w, h) {
    if (dispIn()) return (w / 25.4).toFixed(1) + '×' + (h / 25.4).toFixed(1) + '"';
    return (Math.round(w / 10 * 10) / 10) + '×' + (Math.round(h / 10 * 10) / 10) + 'cm';
  }
  function fmtMouldDisp(mm) { return dispIn() ? ((mm / 25.4).toFixed(2) + '"') : (mm + 'mm'); }
  // Production dimensions for the order line (framer cut sizes). Exact — whole mm
  // in metric, 2dp inch in inch mode. NOT fmtPairDisp (rounds to cm → too coarse
  // to cut from). See docs/ORDER-MOUNT-DIMS-SPEC.md.
  // Sizes defined in INCHES are carried as whole mm (11" -> 279mm, 14" -> 356mm),
  // so converting those mm back put 10.98×14.02" on the order. When a size or
  // opening was defined in inches, take the exact inches from its label
  // ('11" x 14"', '5"×7"'), matched to the mm pair so orientation follows.
  // Metric ones (an A2 opening in an inch frame, cm sizes) keep the mm conversion.
  // Same helper in pfc-moulding.js and pfc-mount.js - keep in step.
  function exactInchPair(wMm, hMm, label) {
    var m = String(label || '').match(/(\d+(?:\.\d+)?)\s*["”]\s*[x×]\s*(\d+(?:\.\d+)?)\s*["”]/i);
    if (!m) return null;
    var a = parseFloat(m[1]), b = parseFloat(m[2]);
    var keep = Math.abs(wMm - a * 25.4) + Math.abs(hMm - b * 25.4);
    var swap = Math.abs(wMm - b * 25.4) + Math.abs(hMm - a * 25.4);
    var p = keep <= swap ? { w: a, h: b } : { w: b, h: a };
    // Only when the mm really are this size, rounded to whole mm.
    return (Math.abs(wMm - p.w * 25.4) <= 1 && Math.abs(hMm - p.h * 25.4) <= 1) ? p : null;
  }
  function fmtInch(x) { return String(Number(x.toFixed(2))); }
  // A dimension is written in the unit that DEFINED it, not the list the customer
  // browsed: an inch size in exact inches; an A-size in mm with its name, so an
  // A4 bought from the inch list reads 210×297mm (A4) rather than 8.27×11.69";
  // a cm size in mm. With no defining label (custom sizes), the display unit.
  function fmtProdDim(wMm, hMm, label) {
    var ex = exactInchPair(wMm, hMm, label);
    if (ex) return fmtInch(ex.w) + '×' + fmtInch(ex.h) + '"';
    var a = /^\s*A([0-9])\s*$/i.exec(String(label || ''));
    var metric = !!a || /\d\s*(?:cm|mm)\b/i.test(String(label || ''));
    if (metric || !dispIn()) return Math.round(wMm) + '×' + Math.round(hMm) + 'mm' + (a ? ' (A' + a[1] + ')' : '');
    return fmtInch(wMm / 25.4) + '×' + fmtInch(hMm / 25.4) + '"';
  }
  // the other unit, switch to the first size of the chosen unit so the trigger
  // value + preview stay in sync with the visible list.
  function setSizeUnit(unit) {
    if (unit !== 'cm' && unit !== 'in') return;
    state.sizeUnit = unit;
    elSizeUnitBtns.forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.sizeUnit === unit ? 'true' : 'false');
    });
    if (variantUnit(state.variant) !== unit) {
      // Restrict to sizes this hub actually offers (allowlist / non-specialist)
      // so a size-constrained hub (e.g. panoramic) doesn't jump to a hidden
      // standard size like 8" x 10" when switching units.
      var vis = visibleVariants();
      var first = vis.find(function (v) { return variantUnit(v) === unit && v.available; })
        || vis.find(function (v) { return variantUnit(v) === unit; })
        || cfg.variants.find(function (v) { return variantUnit(v) === unit && v.available; })
        || cfg.variants.find(function (v) { return variantUnit(v) === unit; });
      // Unit swap = same physical size in the other unit; keep the user's
      // orientation rather than snapping to the equivalent size's natural one.
      if (first) { state.desiredSizeTitle = first.title; selectVariant(first, true); evaluateRecommendation('size-change'); return; }
    }
    renderSizeButtons();
    renderCompactSize();
    if (typeof updateFrameSizeUi === 'function') updateFrameSizeUi();
    renderPreview();
  }
  elSizeUnitBtns.forEach(function (b) {
    b.addEventListener('click', function () { setSizeUnit(b.dataset.sizeUnit); });
  });
  // Sync the toggle's pressed state to the resolved initial unit.
  elSizeUnitBtns.forEach(function (b) {
    b.setAttribute('aria-pressed', b.dataset.sizeUnit === state.sizeUnit ? 'true' : 'false');
  });
  document.addEventListener('pointerdown', function (e) {
    if (!elSizeList || elSizeList.hidden) return;
    if (elSizeSelect && elSizeSelect.contains(e.target)) return;
    setSizeOpen(false);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && elSizeList && !elSizeList.hidden) setSizeOpen(false);
  });

  // Finish/colour swatches: live-swap the active moulding (no reload). The
  // <a href> stays as a no-JS fallback (hard nav to the moulding PDP).
  if (elSwatches) {
    elSwatches.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-swatch]');
      if (!sw) return;
      var id = Number(sw.dataset.mouldingId);
      if (!id || id === cfg.productId) { e.preventDefault(); return; }
      if (!findMouldingExists(id)) return; // unknown -> allow hard nav
      e.preventDefault();
      // Manual customer override — remember the pick + suppress immediate
      // auto-recommendation back to a "better" match on the next size click.
      state.userOverride = true;
      state.preOverridePick = id;
      clearUndo();
      swapMoulding(id);
      // Refresh the badge so the reco UI reflects the customer's choice.
      if (state.activeIntent) showRecommendationBadge(state.activeIntent, cfg, null);
    });
  }

  // Profile-tier change (hub Tier-1 picker): the shell coordinator has already
  // re-filtered the swatch row by depth group; if the current pick fell out of
  // the chosen group, swap the live preview to the first finish still visible.
  // rAF defers past the coordinator's synchronous apply() so .hidden is settled.
  document.addEventListener('pfc:profile-change', function () {
    if (!elSwatches) return;
    requestAnimationFrame(function () {
      var vis = Array.from(elSwatches.querySelectorAll('[data-swatch]')).filter(function (s) { return !s.hidden; });
      if (!vis.length) return;
      var stillVisible = vis.some(function (s) { return Number(s.dataset.mouldingId) === root_cfg.activeMouldingId; });
      if (stillVisible) return; // current pick is in the chosen group — leave it
      var id = Number(vis[0].dataset.mouldingId);
      if (!id || id === root_cfg.activeMouldingId || !findMouldingExists(id)) return;
      state.userOverride = true;
      state.preOverridePick = id;
      clearUndo();
      swapMoulding(id);
      if (state.activeIntent) showRecommendationBadge(state.activeIntent, cfg, null);
    });
  });

  // Colour filter change: same rule as the profile tier — the coordinator has
  // re-filtered the swatch row by colour family; if the current pick fell out
  // of the filtered set, swap the live preview to the first finish still
  // visible. Pick still showing ⇒ leave it (don't yank a valid selection).
  // rAF defers past the coordinator's synchronous apply() so .hidden is settled.
  document.addEventListener('pfc:colour-change', function () {
    if (!elSwatches) return;
    requestAnimationFrame(function () {
      var vis = Array.from(elSwatches.querySelectorAll('[data-swatch]')).filter(function (s) { return !s.hidden; });
      if (!vis.length) return;
      var stillVisible = vis.some(function (s) { return Number(s.dataset.mouldingId) === root_cfg.activeMouldingId; });
      if (stillVisible) return; // current pick survived the filter — keep it
      var id = Number(vis[0].dataset.mouldingId);
      if (!id || id === root_cfg.activeMouldingId || !findMouldingExists(id)) return;
      state.userOverride = true;
      state.preOverridePick = id;
      clearUndo();
      swapMoulding(id);
      if (state.activeIntent) showRecommendationBadge(state.activeIntent, cfg, null);
    });
  });

  // Reco badge interactions: toggle alternates panel, swap on alt click,
  // restore the override pick on undo click.
  if (elReco) {
    elReco.addEventListener('click', function (e) {
      var tgl = e.target.closest('[data-reco-toggle]');
      if (tgl) {
        if (!elRecoAlt) return;
        var open = tgl.getAttribute('aria-expanded') === 'true';
        tgl.setAttribute('aria-expanded', String(!open));
        elRecoAlt.hidden = open;
        return;
      }
      var alt = e.target.closest('[data-reco-alt-id]');
      if (alt) {
        var id = Number(alt.dataset.recoAltId);
        if (!id || id === cfg.productId) return;
        state.userOverride = true;
        state.preOverridePick = id;
        clearUndo();
        state.swapping = true;
        try { swapMoulding(id); } finally { state.swapping = false; }
        if (state.activeIntent) showRecommendationBadge(state.activeIntent, cfg, null);
        return;
      }
      var undo = e.target.closest('[data-reco-undo]');
      if (undo) {
        var prev = state.preOverridePick;
        if (!prev) { clearUndo(); return; }
        state.userOverride = false;
        state.preOverridePick = null;
        state.swapping = true;
        try { swapMoulding(prev); } finally { state.swapping = false; }
        clearUndo();
        if (state.activeIntent) showRecommendationBadge(state.activeIntent, cfg, null);
        return;
      }
    });
  }

  // Intent-pill changes on top-of-funnel hubs. Locked-intent hubs ignore.
  document.addEventListener('pfc:intent-change', function (e) {
    if (state.lockedIntent) return;
    var slug = e && e.detail && e.detail.slug ? String(e.detail.slug) : '';
    state.activeIntent = slug || null;
    if (state.activeIntent) {
      // New intent picked — reset override so the engine can react.
      state.userOverride = false;
      state.preOverridePick = null;
      clearUndo();
      evaluateRecommendation('intent-change');
    } else if (elReco) {
      elReco.hidden = true; // "All" cleared
      clearUndo();
    }
  });

  function findMouldingExists(id) {
    return root_cfg.mouldings.some(function (m) { return m.productId === id; });
  }

  // Swap the active moulding in place: reload texture, rebuild size buttons,
  // keep the chosen size if the new moulding offers it, preserve any uploaded
  // image + crop, refresh price/mount/SKU. No page reload.
  function swapMoulding(id) {
    var prevSizeTitle = state.variant && state.variant.title;
    var prevOrientation = state.orientation;
    cfg = applyRootCfg(findMoulding(id));
    root_cfg.activeMouldingId = id;

    // Keep the user's intended size if the new moulding offers it. Match the
    // sticky desiredSizeTitle (last EXPLICIT pick) rather than the current
    // variant — so if an earlier swap had to fall back, swapping to a capable
    // moulding restores the real choice instead of staying on the fallback.
    var wantTitle = state.desiredSizeTitle || prevSizeTitle;
    // sizeVisible matters as well as available: on a frameOnlySizesOnly hub the
    // new moulding may stock the variant but not be sellable frame-only at that
    // size, in which case keeping it strands the picker on a size the list no
    // longer offers (empty dropdown). Fall through to the visible-size search.
    var keep = cfg.variants.find(function (v) {
      return normTitle(v.title) === normTitle(wantTitle) && v.available && sizeVisible(v);
    });
    if (!keep) {
      // Desired size missing on this moulding (e.g. a profile tier whose
      // mouldings don't carry the panoramic specialist sizes). Keep the current
      // preview ORIENTATION rather than snapping to a portrait default: prefer a
      // visible size matching it, then any available size matching it.
      // desiredSizeTitle is left untouched so a later swap can restore it.
      var sameOrient = function (v) {
        var d = parseDim(v); if (!d || d.w === d.h) return false;
        return (d.w > d.h ? 'landscape' : 'portrait') === prevOrientation;
      };
      var visibleHere = cfg.variants.filter(sizeVisible);
      keep = visibleHere.find(function (v) { return v.available && sameOrient(v); })
          || visibleHere.find(function (v) { return v.available; })
          || cfg.variants.find(function (v) { return v.available && sameOrient(v); });
    }
    state.variant = keep
      || cfg.variants.filter(sizeVisible).find(function (v) { return v.id === cfg.defaultVariantId; })
      || cfg.variants.filter(sizeVisible)[0]
      || cfg.variants.find(function (v) { return v.id === cfg.defaultVariantId; })
      || cfg.variants[0];

    // Preserve the mount selection across the swap — same size ⇒ same apertures
    // and price. Captured here (from the OLD selection), re-resolved against the
    // new moulding after selectVariant (which clears it) so the bundle id/price
    // refresh. state.stripeColour is top-level and survives untouched.
    var keepMount = state.mount
      ? { colour: state.mount.colour, aperture: state.mount.aperture, pattern: state.mount.pattern }
      : null;
    state.mount = null;

    // Sync the framing-style picker to the new moulding's allowed list. The
    // helper resets state.framingStyle unless the previous pick is still in
    // the allowed list — keeps the customer's preference sticky across swaps.
    applyFramingStyleFor(cfg);

    renderSizeButtons();
    syncMountButtons();

    // Swatch active states.
    if (elSwatches) {
      Array.from(elSwatches.querySelectorAll('[data-swatch]')).forEach(function (s) {
        s.setAttribute('data-active', String(Number(s.dataset.mouldingId) === id));
      });
    }

    // Title / family / face-width / description.
    if (elTitle) elTitle.textContent = frameHeadTitle();
    if (elFamily) elFamily.textContent = cfg.mouldingFamily;

    // Below-fold band: detail / profile images + lead copy.
    var bandDetailImg = document.querySelector('[data-band-detail-img]');
    var bandProfileImg = document.querySelector('[data-band-profile-img]');
    var bandFamily = document.querySelector('[data-band-family]');
    var bandWidth = document.querySelector('[data-band-width]');
    if (bandDetailImg && cfg.detailImage) {
      bandDetailImg.src = cfg.detailImage;
      bandDetailImg.alt = cfg.productTitle + ' detail';
    }
    if (bandProfileImg && cfg.profileImage) {
      bandProfileImg.src = cfg.profileImage;
      bandProfileImg.alt = cfg.productTitle + ' profile';
    }
    if (bandFamily) bandFamily.textContent = (cfg.familyLower || cfg.mouldingFamily || '').toString();
    if (bandWidth) bandWidth.textContent = String(cfg.mouldingWidthMm || 20);

    // Gallery family title.
    var galFamily = document.querySelector('[data-gallery-family]');
    if (galFamily) galFamily.textContent = cfg.mouldingFamily || '';

    // SEO copy below.
    var seoTitle = document.querySelector('[data-seo-title]');
    var seoTitleLower = document.querySelector('[data-seo-title-lower]');
    var seoWidth = document.querySelector('[data-seo-width]');
    var seoFamLow1 = document.querySelector('[data-seo-family-lower]');
    var seoFamLow2 = document.querySelector('[data-seo-family-lower-2]');
    var seoFirst = document.querySelector('[data-seo-first-size]');
    var seoLast = document.querySelector('[data-seo-last-size]');
    if (seoTitle) seoTitle.textContent = cfg.productTitle || '';
    if (seoTitleLower) seoTitleLower.textContent = cfg.productTitleLower || (cfg.productTitle || '').toLowerCase();
    if (seoWidth) seoWidth.textContent = String(cfg.mouldingWidthMm || 20);
    if (seoFamLow1) seoFamLow1.textContent = cfg.familyLower || '';
    if (seoFamLow2) seoFamLow2.textContent = cfg.familyLower || '';
    if (seoFirst) seoFirst.textContent = cfg.firstVariantTitle || '';
    if (seoLast) seoLast.textContent = cfg.lastVariantTitle || '';

    var matSeo = document.querySelector('[data-material-seo]');
    var matTrust = document.querySelector('[data-material-trust]');
    var matSpec = document.querySelector('[data-material-spec]');
    if (matSeo) matSeo.textContent = cfg.materialSeoPhrase || 'real solid-wood moulding';
    if (matTrust) matTrust.textContent = cfg.materialTrustPhrase || 'Real solid-wood moulding';
    if (matSpec) matSpec.textContent = cfg.materialSpecLine || 'Real solid-wood moulding, cut and finished in our workshop.';

    // Product URL (keeps deep-link / refresh on the chosen moulding).
    try {
      if (cfg.handle) {
        var url = new URL(window.location.href);
        if (window.location.pathname.indexOf('/products/') === 0) {
          url.pathname = '/products/' + cfg.handle;
        }
        url.searchParams.set('variant', state.variant.id);
        window.history.replaceState({}, '', url);
      }
    } catch (_) {}

    // Reload texture then redraw (selectVariant redraws too, but texture is async).
    loadTexture(cfg, function (img) {
      state.mouldingTextureImg = img;
      // Sample the new moulding's side colour once the texture is ready.
      if (state.depthView) applyDepthVar();
      renderPreview();
    });

    // If the depth tilt is showing, refresh the side thickness immediately
    // (colour follows once the texture loads, above).
    if (state.depthView) applyDepthVar();

    // keepOrientation: orientation is display-only and was preserved above
    // (prevOrientation) — don't let selectVariant snap it to the size's natural.
    selectVariant(state.variant, true);

    // Re-apply the preserved mount on the new moulding. resolveMount rebuilds
    // from the new variant (fresh bundle id + price). If this moulding doesn't
    // offer the exact selection, fall back (premium gap → white) or drop it.
    if (keepMount && mountAvailable()) {
      var rm = resolveMount(keepMount.colour, keepMount.aperture);
      if (!rm && keepMount.colour === 'premium') rm = resolveMount('white', keepMount.aperture);
      if (rm) {
        if (keepMount.pattern) rm.pattern = keepMount.pattern;
        state.mount = rm;
        if (elAddMount) elAddMount.checked = true;
        if (elMounts) elMounts.hidden = false;
        syncMountButtons();
        updateMountColourPrices();
        syncPremiumPickers();
        updatePriceAndPhotoSize();
        renderPreview();
      }
    }
    // Map-native preset: rebuild the size tiers for the new moulding's £/m.
    if (presetActive) applyPresetTiers();
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Scene toggle (flat <-> to-scale)
  var elSceneBtns = Array.from(root.querySelectorAll('[data-scene-btn]'));
  elSceneBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      // Leaving the tilt: a scene is a flat view, so drop depth first (the
      // to-scale / plain scenes must not be rendered tilted).
      if (state.depthView) disableDepth(true);
      state.scene = btn.dataset.sceneBtn;
      elSceneBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === btn));
      });
      if (elPreview) elPreview.dataset.scene = state.scene;
      renderPreview();
    });
  });

  // Orientation toggle (portrait <-> landscape). Display-only: variant + SKU
  // unchanged. Hidden when the active size is square (no meaningful flip).
  elOrientationBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var next = btn.dataset.orientationBtn;
      if (next === state.orientation) return;
      state.orientation = next;
      elOrientationBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.orientationBtn === next));
      });
      applyPreviewAspect();
      renderPreview();
      // Size labels read in the chosen orientation (8x10 <-> 10x8).
      renderSizeButtons();
      renderCompactSize();
      updateFrameSizeUi();
    });
  });

  // Variant title as the customer sees it: 8" x 10" reads 10" x 8" in
  // landscape. Display-only, like the toggle; A-sizes and squares pass through.
  // Same helper in pfc-print-app.js + pfc-mount.js \u2014 change all three together.
  function orientedTitle(t) {
    var m = /^(\s*)(\d+(?:\.\d+)?)(\s*(?:"|\u201d|\u2033|cm|mm|in)?)(\s*[x\u00d7X]\s*)(\d+(?:\.\d+)?)(.*)$/.exec(t || '');
    if (!m) return t;
    var a = parseFloat(m[2]), b = parseFloat(m[5]);
    if (a === b || (state.orientation === 'landscape') === (a > b)) return t;
    return m[1] + m[5] + m[3] + m[4] + m[2] + m[6];
  }

  function refreshOrientationToggle() {
    if (!elOrientationWraps.length) return;
    // Preset (vintage map): orientation is map-native + automatic — stay hidden.
    if (presetActive) { elOrientationWraps.forEach(function (w) { w.hidden = true; }); return; }
    var isSquare = orientationOf(state.variant) === 'Square';
    elOrientationWraps.forEach(function (w) { w.hidden = isSquare; });
    elOrientationBtns.forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.orientationBtn === state.orientation));
    });
  }

  // Canvas: upload trigger when empty, recrop trigger when a photo is present.
  if (elCanvas) {
    elCanvas.addEventListener('click', function () {
      if (frameOnlyMode()) return;
      if (state.userImg && state.originalUrl) { reopenCropper(); return; }
      elFileInput.click();
    });
    elCanvas.addEventListener('mouseenter', function () {
      if (state.userImg || (frameOnlyMode())) return;
      state.hoverPlaceholder = true; renderPreview();
    });
    elCanvas.addEventListener('mouseleave', function () {
      if (!state.hoverPlaceholder) return;
      state.hoverPlaceholder = false; renderPreview();
    });
  }

  if (elUpload) {
    elUpload.addEventListener('click', function (e) {
      // Recrop / Replace action buttons own their own handlers — don't also
      // fire the underlying upload-zone file picker.
      if (e.target.closest('[data-recrop-btn]') || e.target.closest('[data-replace-btn]')) return;
      // When a photo is already present, a bare click on the zone should
      // recrop (matches the canvas behaviour). The Replace button still
      // routes to file picker explicitly.
      if (state.userImg && state.originalUrl) { reopenCropper(); return; }
      elFileInput.click();
    });
  }
  var elRecropBtn = root.querySelector('[data-recrop-btn]');
  var elReplaceBtn = root.querySelector('[data-replace-btn]');
  if (elRecropBtn) {
    elRecropBtn.addEventListener('click', function (e) { e.stopPropagation(); reopenCropper(); });
  }
  if (elReplaceBtn) {
    elReplaceBtn.addEventListener('click', function (e) { e.stopPropagation(); elFileInput.click(); });
  }

  // Drag + drop on canvas preview + upload zone.
  function wireDropZone(el, hoverAttr) {
    if (!el) return;
    var depth = 0;
    el.addEventListener('dragenter', function (e) {
      e.preventDefault(); depth++;
      if (frameOnlyMode()) return;
      el.setAttribute(hoverAttr, 'true');
      if (el === elPreview && !state.userImg) { state.hoverPlaceholder = true; renderPreview(); }
    });
    el.addEventListener('dragover', function (e) {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    });
    el.addEventListener('dragleave', function () {
      depth--; if (depth <= 0) {
        depth = 0;
        el.removeAttribute(hoverAttr);
        if (el === elPreview && state.hoverPlaceholder) { state.hoverPlaceholder = false; renderPreview(); }
      }
    });
    el.addEventListener('drop', function (e) {
      e.preventDefault(); depth = 0; el.removeAttribute(hoverAttr);
      if (state.hoverPlaceholder) { state.hoverPlaceholder = false; renderPreview(); }
      if (frameOnlyMode()) return;
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      var UN = window.PFCUploadNormalise;
      if (file && (UN ? UN.isSupported(file) : /^image\/(jpeg|png|jpg)$/i.test(file.type))) openCropper(file);
      else if (file) uploadNotice('Please use a ' + (UN ? UN.LABEL : 'JPG or PNG') + '.', 'error');
    });
  }
  wireDropZone(elPreview, 'data-drop-hover');
  wireDropZone(elUpload, 'data-drop-hover');

  if (elFileInput) {
    elFileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      if (file) openCropper(file);
      elFileInput.value = ''; // allow re-upload same file
    });
  }
  if (elAddPhoto) {
    elAddPhoto.addEventListener('change', function () {
      var wantsPhoto = elAddPhoto.checked;
      setUploadOpen(wantsPhoto); // show the dropzone only when adding a photo
      if (!wantsPhoto) {
        // Frame only — drop any uploaded image.
        state.fullDataUrl = null;
        state.previewDataUrl = null;
        state.userImg = null;
        state.photo = false;
        state.cropFor = null;
        if (photoSrc) photoSrc.clear();   // stop uploading a photo nobody wants
        if (elUploadTitle) elUploadTitle.textContent = 'Click to add your photo';
        elUpload.dataset.hasImage = 'false';
        if (elPreview) elPreview.dataset.hasImage = 'false';
      }
      renderPreview();
      updateAtcStatus();
    });
  }
  // Initial dropzone visibility mirrors the checkbox. Default is unchecked
  // (frame only) so the dropzone starts hidden; requirePrint mouldings force
  // the checkbox on during selectVariant -> refreshFrameOnlyUi.
  setUploadOpen(!!(elAddPhoto && elAddPhoto.checked));
  if (elCropCancel) {
    elCropCancel.addEventListener('click', cancelCrop);
  }
  if (elCropConfirm) {
    elCropConfirm.addEventListener('click', confirmCrop);
  }
  if (elAtc) {
    elAtc.addEventListener('click', addToCart);
  }

  /* ---------- Name-art mode (text → design, reuses the art pipeline) ----------
     Gated by window.PFC_MOULDING.nameArt.enabled (set from a product metafield).
     Builds a text-design panel in place of the upload dropzone and feeds
     PFCNameArt.render() output through applyPreview() — so preview, S3 upload
     and ATC all work unchanged. See docs/NAME-ART-CUSTOMISER.md. */
  var naActive = false, naReady = false, naEls = null, naRaf = null;
  // Vintage preset mode (gated): when root_cfg.preset is present the artwork is a
  // fixed public-domain map (no upload), mount is mandatory, and a separate
  // area-priced "Map Print" line carries the print premium. EVERYTHING below is
  // a no-op unless root_cfg.preset exists — existing moulding/hub pages are
  // untouched. See docs/VINTAGE-PRINT-PIPELINE.md.
  var presetActive = false;
  function presetCfg() { return root_cfg && root_cfg.preset; }
  function naCfg() { return root_cfg && root_cfg.nameArt; }
  function prettyTheme(k) { return k.charAt(0).toUpperCase() + k.slice(1); }
  function naOption(val, label, sel) { var o = document.createElement('option'); o.value = val; o.textContent = label; if (sel) o.selected = true; return o; }
  function naSelect(opts) {
    var s = document.createElement('select'); s.className = 'pfc-na__select';
    opts.forEach(function (o) { s.appendChild(naOption(o[0], o[1], o[2])); });
    return s;
  }
  function buildNamePanel() {
    var PA = window.PFCNameArt, c = naCfg();
    var wrap = document.createElement('div'); wrap.className = 'pfc-na'; wrap.setAttribute('data-name-art', '');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:10px;padding:14px;border:1px solid var(--pm-line,#e8e0d2);border-radius:12px;background:var(--pm-card,#fffdf9);';
    function field(labelText, el) {
      var f = document.createElement('label'); f.style.cssText = 'display:block;font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#7a715f;';
      f.appendChild(document.createTextNode(labelText));
      el.style.cssText = (el.style.cssText || '') + ';width:100%;margin-top:4px;padding:9px 10px;font:inherit;font-size:14px;border:1px solid var(--pm-line,#e8e0d2);border-radius:8px;background:#fff;box-sizing:border-box;';
      f.appendChild(el); return f;
    }
    var nameI = document.createElement('input'); nameI.type = 'text'; nameI.maxLength = 24; nameI.value = (c && c.defaultName) || '';
    nameI.placeholder = 'Enter a name';
    var layoutS = naSelect(PA.LAYOUTS.map(function (l) { return [l, l.replace(/-/g, ' ').replace(/\b\w/g, function (m) { return m.toUpperCase(); }), l === (c && c.defaultLayout)]; }));
    var themeS = naSelect(Object.keys(PA.THEMES).map(function (k) { return [k, prettyTheme(k)]; }));
    var scriptS = naSelect(Object.keys(PA.SCRIPTS).map(function (k) { return [PA.SCRIPTS[k], PA.SCRIPTS[k]]; }));
    var serifS = naSelect(Object.keys(PA.SERIFS).map(function (k) { return [PA.SERIFS[k], PA.SERIFS[k]]; }));
    var motifS = naSelect(PA.MOTIFS.map(function (m) { return [m, m === 'none' ? 'No motif' : prettyTheme(m)]; }));
    var initialI = document.createElement('input'); initialI.type = 'text'; initialI.maxLength = 2;
    var dateChk = document.createElement('input'); dateChk.type = 'checkbox';
    var dateI = document.createElement('input'); dateI.type = 'text'; dateI.maxLength = 18; dateI.placeholder = 'est. 2024';
    var subChk = document.createElement('input'); subChk.type = 'checkbox';
    var subI = document.createElement('input'); subI.type = 'text'; subI.maxLength = 28; subI.placeholder = 'a short line';

    wrap.appendChild(field('Name', nameI));
    var row = document.createElement('div'); row.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;';
    row.appendChild(field('Layout', layoutS)); row.appendChild(field('Colour', themeS));
    wrap.appendChild(row);
    var row2 = document.createElement('div'); row2.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:10px;';
    row2.appendChild(field('Script font', scriptS)); row2.appendChild(field('Motif', motifS));
    wrap.appendChild(row2);
    // advanced
    var adv = document.createElement('details'); adv.style.marginTop = '2px';
    var sum = document.createElement('summary'); sum.textContent = 'More options'; sum.style.cssText = 'cursor:pointer;font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#7a715f;';
    adv.appendChild(sum);
    adv.appendChild(field('Serif font (initial / caps)', serifS));
    adv.appendChild(field('Initial (auto)', initialI));
    var dl = document.createElement('label'); dl.style.cssText = 'display:flex;align-items:center;gap:7px;font-size:13px;margin-top:8px;'; dl.appendChild(dateChk); dl.appendChild(document.createTextNode('Add a date / “est.” line'));
    adv.appendChild(dl); adv.appendChild(dateI);
    var sl = document.createElement('label'); sl.style.cssText = 'display:flex;align-items:center;gap:7px;font-size:13px;margin-top:8px;'; sl.appendChild(subChk); sl.appendChild(document.createTextNode('Add a subtitle'));
    adv.appendChild(sl); adv.appendChild(subI);
    wrap.appendChild(adv);

    naEls = { wrap: wrap, name: nameI, initial: initialI, layout: layoutS, theme: themeS, script: scriptS, serif: serifS, motif: motifS, dateChk: dateChk, date: dateI, subChk: subChk, sub: subI };
    var schedule = function () { if (naRaf) cancelAnimationFrame(naRaf); naRaf = requestAnimationFrame(nameArtRegen); };
    nameI.addEventListener('input', function () { if (!initialI.value) {/* keep auto */} schedule(); });
    [layoutS, themeS, scriptS, serifS, motifS, initialI, dateI, subI].forEach(function (e) { e.addEventListener('input', schedule); });
    [dateChk, subChk].forEach(function (e) { e.addEventListener('change', schedule); });
    return wrap;
  }
  function naOpts(width, height) {
    var e = naEls;
    return {
      width: width, height: height,
      layout: e.layout.value,
      name: e.name.value, initial: e.initial.value,
      theme: e.theme.value, script: e.script.value, serif: e.serif.value, motif: e.motif.value,
      date: e.dateChk.checked ? e.date.value : '',
      subtitle: e.subChk.checked ? e.sub.value : ''
    };
  }
  function nameArtRegen() {
    if (!naActive || !naReady || !window.PFCNameArt) return;
    var ar = currentAspectRatio() || 1, LONG = 2000, w, h;
    if (ar >= 1) { w = LONG; h = Math.round(LONG / ar); } else { h = LONG; w = Math.round(LONG * ar); }
    var full = window.PFCNameArt.render(naOpts(w, h));
    var pw, ph; if (ar >= 1) { pw = 800; ph = Math.round(800 / ar); } else { ph = 800; pw = Math.round(800 * ar); }
    var prev = window.PFCNameArt.render(naOpts(pw, ph));
    state.fullDataUrl = full.toDataURL('image/png');
    state.previewDataUrl = prev.toDataURL('image/png');
    state.photo = false;
    state.naAspect = ar;       // set BEFORE applyPreview so renderPreview won't re-trigger
    applyPreview();
  }
  function initNameArt() {
    var c = naCfg();
    if (!c || !c.enabled || !window.PFCNameArt) return;
    naActive = true;
    // hide the photo-upload affordances; name-art always produces the artwork
    if (elUpload) elUpload.hidden = true;
    if (elAddPhotoWrap) elAddPhotoWrap.hidden = true;
    var panel = buildNamePanel();
    var host = (elUpload && elUpload.parentNode) || root;
    host.insertBefore(panel, elUpload || null);
    window.PFCNameArt.ensureFonts().then(function () { naReady = true; nameArtRegen(); });
  }
  initNameArt();

  /* ---------- Vintage preset mode (gated by root_cfg.preset) ---------- */
  function initPreset() {
    var pc = presetCfg();
    if (!pc || !pc.image) return;
    presetActive = true;
    // The map is the artwork + the sizes/mount are map-native and automatic — hide
    // the upload, mount, framing-style, orientation and cm/inch controls. The
    // mount is included (folded into the Custom Frame line); size is the map tier.
    if (elUpload) elUpload.hidden = true;
    if (elAddPhotoWrap) elAddPhotoWrap.hidden = true;
    if (elAddMountWrap) elAddMountWrap.hidden = true;
    if (elFramings) elFramings.hidden = true;
    if (elCompactFramingRow) elCompactFramingRow.hidden = true;
    elOrientationWraps.forEach(function (w) { w.hidden = true; });
    root.querySelectorAll('[data-size-unit-toggle]').forEach(function (w) { w.hidden = true; });
    state.framingStyle = 'standard';   // no float in map-native pilot
    state.mount = null;                // mount is folded into the frame line
    // Load the preset map into the preview exactly like an upload would: draw to
    // a canvas → data URL → state.{full,preview}DataUrl → applyPreview(). The map
    // is Shopify-CDN hosted (CORS-clean, same as the moulding textures), so the
    // canvas stays untainted. On any taint/error, fall back to the URL.
    state.photo = false;
    var im = new Image(); im.crossOrigin = 'anonymous';
    im.onload = function () {
      try {
        var c = document.createElement('canvas');
        c.width = im.naturalWidth; c.height = im.naturalHeight;
        c.getContext('2d').drawImage(im, 0, 0);
        state.fullDataUrl = c.toDataURL('image/jpeg', 0.92);
        state.previewDataUrl = state.fullDataUrl;
      } catch (e) {
        state.fullDataUrl = pc.image; state.previewDataUrl = pc.image;
      }
      presetAspect = (im.naturalHeight > 0) ? (im.naturalWidth / im.naturalHeight) : 1;
      state.orientation = presetAspect >= 1 ? 'landscape' : 'portrait';
      applyPresetTiers();   // map-native size tiers for the active moulding
      applyPreview();
    };
    im.onerror = function () {
      state.fullDataUrl = pc.image; state.previewDataUrl = pc.image; applyPreview();
    };
    im.src = pc.image;
  }
  initPreset();

  // Presentment-currency rate (matches pfc-print-app.js): the cart-transform
  // converts each bucket upcharge before adding it to the host line, so the
  // client-side bucket match must convert the same way. The host here is the
  // preset's own Map Print variant, NOT the frame, so it carries its own base:
  // pc.base is that variant's Liquid price (presentment, major units) and
  // pc.hostBase the same variant in store currency. No hostBase -> FX, as before.
  function presetPresRate() {
    var pc = presetCfg() || {};
    if (window.PFCBundleRate) return window.PFCBundleRate(Number(pc.base) * 100, pc.hostBase);
    var r = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    return r > 0 ? r : 1;
  }
  // Snap the raw (presentment) COMBINED price (print + frame + mount) UP to a
  // bucket on the single host (Map Print) ladder. Charged total = host base +
  // converted-upcharge; never undercharge. ONE bucket = ONE repriced cart line.
  function matchComboBucket(rawPresPrice) {
    var pc = presetCfg(); if (!pc) return null;
    var ladder = pc.bundles || [];
    var rate = presetPresRate();
    // pc.base is the host variant's Liquid price -- ALREADY presentment. Only the
    // bucket upcharge needs converting (multiplying base by the rate as well
    // skewed the bucket pick on every converted market).
    var base = Number(pc.base) || 0;
    var best = null, bestUp = Infinity;
    ladder.forEach(function (b) {
      var up = addonChargeMajor(b.price, rate);
      if ((up + base) >= rawPresPrice - 1e-6 && up < bestUp) { best = b; bestUp = up; }
    });
    if (!best && ladder.length) best = ladder.reduce(function (a, b) { return b.price > a.price ? b : a; });
    return best;
  }
  function presetHostVariantId() {
    var pc = presetCfg(); return pc && (pc.comboVariantId || pc.mapPrintVariantId);
  }
  // The framed-map bundle line. Host (Map Print 'Custom Size') variant selected
  // with a combo bucket whose components are the Frame + Mount — Bundle Pro EXPANDs
  // it into Frame + Mount + Print, grouped under one bundle header (title + CDN map
  // image). The chosen finish / size / mount ride in _addon_bundle_pro_personalisations
  // → _addons_custom (the only line data that survives the expand) for fulfilment.
  function buildComboLine(uploaded) {
    var hv = presetHostVariantId(); if (!hv) return null;
    var pc = presetCfg();
    var v = state.variant;
    uploaded = uploaded || {};
    var bucket = (v && v._comboBucketId) ? { id: v._comboBucketId, name: v._comboBucketName } : null;
    var outer = (v && v._outerMm) || {};
    var mapName = pc.title || 'Vintage map print';
    // Finish = the moulding's colour/material name (before the " / <profile>"),
    // e.g. "Antique Gold Wood (Lustre)" — NOT the profile family "17mm x 17mm".
    var finish = ((cfg.productTitle || '').split(' / ')[0]) || cfg.mouldingFamily || '';
    var sizeT = (v && v.options && v.options.Size) ? v.options.Size : (v ? v.title : '');
    var frameSize = (outer.w && outer.h) ? (outer.w + '×' + outer.h + 'mm') : '';
    var printFile = uploaded.fullUrl || pc.printUrl;
    // Display props are underscore-prefixed → hidden from the flat property list;
    // pfc-cart-bundle-children renders them as a pseudo-bundle (Print / Frame /
    // Mount). _addons_custom is built explicitly below so fulfilment keeps them.
    var p = {
      '_pfc_vintage_map': 'true',
      '_pfc_line_title': mapName,
      '_vm_map': mapName,
      '_vm_finish': finish,
      '_vm_size': sizeT,
      '_vm_frame_size': frameSize,
      '_vm_mount': 'White',
      '_session': state.sessionId,
    };
    if (printFile) p['_print_file'] = printFile;
    if (bucket) {
      p['_upsell_bundles_pro_selected'] = bucket.id;
      // Bundle group header. Expanded component lines can't carry Liquid-readable
      // per-order values (only the _addons_custom JSON survives, which Liquid can't
      // parse), so surface the chosen size + finish in the title where it IS shown.
      p['_addon_bundle_pro_title'] = mapName
        + (sizeT ? ' · ' + sizeT : '')
        + (finish ? ' · ' + finish : '');
      // Thumbnail. MUST be a Shopify-CDN url AND a FULL url with scheme — the
      // cart-transform expand's image.url rejects a protocol-relative `//cdn…`
      // (image_url's output) with InvalidOutputError → drops the whole transform →
      // bare $15. Force https:. (S3 urls would also break the expand.)
      if (pc.image) {
        var img = pc.image;
        if (img.indexOf('//') === 0) img = 'https:' + img;
        p['_addon_bundle_pro_image'] = img;
      }
      var carry = { 'Map': mapName, 'Finish': finish, 'Size': sizeT, 'Frame size': frameSize, 'Mount': 'White' };
      if (printFile) carry['Print file'] = printFile;
      p['_addon_bundle_pro_personalisations'] = JSON.stringify(carry);
      var vmDisplay = bundleDisplayFor(carry, null);
      if (vmDisplay) p['_addon_bundle_pro_display'] = vmDisplay;
    }
    return { id: hv, quantity: 1, properties: p };
  }

  /* ---------- Map-native custom sizes (preset only) ---------- */
  // Vintage maps are sized to the MAP, not the standard moulding sizes. In preset
  // mode we replace cfg.variants with synthetic per-map tiers (S/M/L) computed
  // from the map's aspect, so the existing size-button/preview/price machinery
  // runs on map-native dimensions. The frame+mount is cut to size (priced via the
  // shared "Custom Frame" component's perimeter ladder); the print rides the Map
  // Print line. ATC = 2 lines (Map Print leads, Custom Frame follows).
  var presetAspect = 1, presetPpm = 0;
  var PRESET_TIERS = [
    { key: 'S', shortIn: 12, borderMm: 50 },
    { key: 'M', shortIn: 18, borderMm: 60 },
    { key: 'L', shortIn: 24, borderMm: 75 },
  ];
  // Moulding £/m (store currency) reverse-derived from a standard variant:
  // price = perimeter_m × ppm × 1.5  →  ppm = price / (perimeter_m × 1.5).
  function deriveMouldingPpm(variants) {
    var ppm = 0, bestPerim = 0;
    (variants || []).forEach(function (v) {
      var w = v.widthMm, hh = v.heightMm;
      if (!w || !hh) { var d = parseDim(v); if (d) { w = d.w; hh = d.h; } }
      if (!w || !hh || !v.price) return;
      var perim = 2 * (w + hh) / 1000;
      if (perim > bestPerim) { bestPerim = perim; ppm = (Number(v.price) / 100) / (perim * 1.5); }
    });
    return ppm || 18;
  }
  function presetSizeTitle(wMm, hMm) {
    var usd = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) === 'USD';
    if (usd) return Math.round(wMm / 25.4) + '" x ' + Math.round(hMm / 25.4) + '"';
    return Math.round(wMm / 10) + 'cm x ' + Math.round(hMm / 10) + 'cm';
  }
  function buildPresetTiers(aspect, ppm, faceW) {
    var pc = presetCfg(); var IN = 25.4; var rate = presetPresRate();
    return PRESET_TIERS.map(function (t, i) {
      var shortMm = t.shortIn * IN;
      var longMm = Math.min(t.shortIn * aspect, 48) * IN;
      var pw = aspect >= 1 ? longMm : shortMm;   // print, oriented to the map
      var ph = aspect >= 1 ? shortMm : longMm;
      var b = t.borderMm;
      var mountW = pw + 2 * b, mountH = ph + 2 * b;
      var outerW = mountW + 2 * faceW, outerH = mountH + 2 * faceW;
      var framePerim = 2 * (outerW + outerH) / 1000;
      var frameMount = framePerim * ppm * 1.5 + ((mountW * mountH) / 1e6) * (Number(pc.mountPpsqm) || 40);
      var printRaw = Math.max(((pw * ph) / 1e6) * (Number(pc.ppsqm) || 100) * (Number(pc.mult) || 1), Number(pc.floor) || 0);
      // Single COMBINED price (print + frame + mount) → ONE host-ladder bucket, so
      // the displayed price is the exact charged total of the single cart line.
      var comboRaw = (frameMount + printRaw) * rate;
      var cb = matchComboBucket(comboRaw);
      var comboTotal = cb ? ((Number(pc.base) || 0) + addonChargeMajor(cb.price, rate)) : (frameMount + printRaw);
      var title = presetSizeTitle(pw, ph);
      return {
        id: -100 - i, title: title, sku: 'PRESET-' + t.key,
        price: Math.round(comboTotal * 100), available: true,
        widthMm: Math.round(pw), heightMm: Math.round(ph),
        options: { Size: title }, bundles: [],
        _presetTier: t.key,
        _comboBucketId: cb && cb.id, _comboBucketName: cb && cb.name,
        _outerMm: { w: Math.round(outerW), h: Math.round(outerH) },
        _mountMm: b,   // white mount border (mm) — drawn in the preview (preset)
      };
    });
  }
  // (Re)build the map tiers for the active moulding and re-select. Called at init
  // and after every moulding swap (each moulding has its own £/m).
  function applyPresetTiers() {
    if (!presetActive) return;
    // cfg IS the stored moulding object (applyRootCfg mutates in place), so the
    // `cfg.variants = tiers` below permanently replaces its REAL variants with the
    // synthetic map tiers. Snapshot the real variants ONCE per moulding so the £/m
    // derive always reads true moulding prices — otherwise each swap re-derives ppm
    // from the inflated tier prices and the cost compounds (was the $400+ bug).
    if (!cfg._realVariants) cfg._realVariants = cfg.variants;
    presetPpm = deriveMouldingPpm(cfg._realVariants);
    var tiers = buildPresetTiers(presetAspect, presetPpm, cfg.mouldingWidthMm || 20);
    var prevKey = state.variant && state.variant._presetTier;
    cfg.variants = tiers;
    var pick = tiers.filter(function (t) { return t._presetTier === prevKey; })[0] || tiers[1] || tiers[0];
    state.variant = pick;
    selectVariant(pick, true);
  }
  // Preset ATC: a SINGLE framed-map line (host variant repriced to print + frame +
  // mount via one combo bucket). Bypasses the standard moulding-line build.
  function postCartPreset(uploaded) {
    var items = [];
    var line = buildComboLine(uploaded); if (line) items.push(line);
    var body = { items: items, sections: 'cart-drawer,cart-icon-bubble,cart-live-region-text,header-drawer' };
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(body),
    });
  }

  /* ---------- Variant selection ---------- */
  // Whether frame-only checkbox should be available for the given variant on
  // the active moulding. Three modes per moulding:
  //   requirePrint=true            -> never allowed (Gold metal: no ready-mades)
  //   allowedFrameOnlySizesSet=true -> allowed only for sizes in the list
  //                                    (Copper/Black metal: ready-mades only)
  //   otherwise (legacy/wood)      -> always allowed
  function frameOnlyAllowed(v) {
    if (!v) return false;
    if (cfg.requirePrint) return false;
    if (cfg.allowedFrameOnlySizesSet) {
      return (cfg.allowedFrameOnlySizes || []).indexOf(v.title) !== -1;
    }
    return true;
  }

  function showToast(msg) {
    // One toast on the page: share the photo-upload one so two never overlap.
    if (window.PFCPhotoSource && window.PFCPhotoSource.notice) { window.PFCPhotoSource.notice(msg, 'info'); return; }
    var t = document.createElement('div');
    t.className = 'pfc-moulding__toast';
    t.setAttribute('role', 'status');
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('pfc-moulding__toast--in'); });
    setTimeout(function () {
      t.classList.remove('pfc-moulding__toast--in');
      setTimeout(function () { t.remove(); }, 300);
    }, 3800);
  }

  // Offer the 'frame only' choice (uncheck 'Add a photo') only where it's
  // allowed. Where it isn't, hide the choice and force 'Add a photo' on, with a
  // toast if the customer had selected frame-only.
  function refreshFrameOnlyUi() {
    if (!elAddPhotoWrap || !elAddPhoto) return;
    // Preset (vintage map): the map IS the artwork — there's no add-photo / frame-
    // only choice. Keep the whole accordion hidden (initPreset hid it; this guards
    // the reactive re-show on every variant change).
    if (presetActive) { elAddPhotoWrap.hidden = true; if (elAddPhotoHead) elAddPhotoHead.hidden = true; return; }
    var allowed = frameOnlyAllowed(state.variant);
    if (allowed) {
      elAddPhotoWrap.hidden = false;
      if (elAddPhotoHead) elAddPhotoHead.hidden = false;
    } else {
      // Photo mandatory: keep the accordion (so the dropzone panel shows) but hide
      // the toggle header — there's no frame-only choice to make.
      elAddPhotoWrap.hidden = false;
      if (elAddPhotoHead) elAddPhotoHead.hidden = true;
      if (!elAddPhoto.checked) {
        elAddPhoto.checked = true; // force 'add a photo'
        elAddPhoto.dispatchEvent(new Event('change', { bubbles: true }));
        var reason = cfg.requirePrint
          ? 'Frame only isn’t available on this moulding — please add a photo.'
          : 'Frame only isn’t available at this size. Choose a ready-made size or add a photo.';
        showToast(reason);
      }
    }
  }

  // Float-mount suspends the print inside the frame depth, so 'frame only' is
  // impossible. Force 'Add a photo' on, disable the toggle, and show the reason.
  // Leaving float restores normal frame-only availability via refreshFrameOnlyUi.
  function applyPhotoLockForFraming() {
    if (!elAddPhoto || !elAddPhotoWrap) return;
    var floatOn = state.framingStyle === 'float';
    if (floatOn) {
      elAddPhotoWrap.hidden = false; // keep visible so the customer sees why
      if (!elAddPhoto.checked) {
        elAddPhoto.checked = true;
        elAddPhoto.dispatchEvent(new Event('change', { bubbles: true }));
      }
      elAddPhoto.disabled = true;
      elAddPhotoWrap.setAttribute('data-locked', 'true');
      if (elPhotoLockHint) elPhotoLockHint.hidden = false;
    } else {
      if (elAddPhoto.disabled) elAddPhoto.disabled = false;
      elAddPhotoWrap.removeAttribute('data-locked');
      if (elPhotoLockHint) elPhotoLockHint.hidden = true;
      refreshFrameOnlyUi();
    }
  }

  function selectVariant(v, keepOrientation) {
    state.variant = v;
    // Auto-orient to the size's natural orientation: panoramic (landscape, w>h)
    // sizes open wide, standard portrait sizes stay tall. Square keeps current.
    // (refreshOrientationToggle + renderPreview below pick this up.)
    // keepOrientation=true on a moulding swap: orientation is display-only and
    // the caller (swapMoulding) already preserved the user's choice, so don't
    // snap it back to the size's natural reading orientation.
    var od = parseDim(v);
    if (!keepOrientation && !PIN_ORIENT && od && od.w !== od.h) state.orientation = od.w > od.h ? 'landscape' : 'portrait';
    // Keep the cm/inch toggle on the unit of the selected size, so a size picked
    // via the mobile sheet or a ?variant deep-link stays visible in the desktop
    // list and the toggle reflects it.
    if (variantUnit(v) !== state.sizeUnit) {
      state.sizeUnit = variantUnit(v);
      elSizeUnitBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.dataset.sizeUnit === state.sizeUnit ? 'true' : 'false');
      });
    }
    renderSizeButtons(); // re-sync the size dropdown selection + trigger value
    if (elCompare) {
      if (v.compareAtPrice && v.compareAtPrice > v.price) {
        elCompare.textContent = formatMoney(v.compareAtPrice);
        elCompare.style.display = '';
      } else {
        elCompare.style.display = 'none';
      }
    }
    // Update URL ?variant=
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    } catch (_) {}
    syncDeepLink();   // quote hubs only: keep the shareable design URL current

    // Reset mount selection on size change (apertures differ per size), but
    // keep the customer's colour/aperture/pattern preference sticky. Captured
    // from the OLD selection, re-resolved against the new size below so the
    // bundle id/price refresh. Falls back (premium gap → white, missing
    // aperture → firstAperture) before refreshMountUi defaults to white.
    var keepMount = state.mount
      ? { colour: state.mount.colour, aperture: state.mount.aperture, pattern: state.mount.pattern }
      : null;
    state.mount = null;
    if (keepMount && mountAvailable()) {
      var rmv = resolveMount(keepMount.colour, keepMount.aperture)
        || resolveMount(keepMount.colour, firstAperture())
        || (keepMount.colour === 'premium' ? resolveMount('white', keepMount.aperture) : null);
      if (rmv) {
        if (keepMount.pattern) rmv.pattern = keepMount.pattern;
        state.mount = rmv;
      }
    }
    syncMountButtons();
    refreshMountUi();
    if (OCC && state.mount) {   // keep room for the names; drop a template the new size can't fit
      if (!occTemplateFits(state.occ.template)) state.occ.template = 'plain';
      commitMount(occFitMount(state.mount));
    }
    updatePriceAndPhotoSize();

    // Update preview aspect ratio AND redraw canvas
    applyPreviewAspect();
    refreshOrientationToggle();
    refreshFrameOnlyUi();
    renderPreview();
    updateAtcStatus();
    renderCompactSize();
  }

  // Map (frame variant title, colour) -> mount variant from cfg.mountOptions.
  function mountVariantFor(sizeTitle, colour) {
    if (!cfg.mountOptions) return null;
    var col = (colour || '').toLowerCase();
    return cfg.mountOptions.find(function (m) {
      return m.size === sizeTitle && (m.colour || '').toLowerCase() === col;
    }) || null;
  }

  // Typed opening (CO hubs only). Stored portrait (w <= h) like the aperture map;
  // the preview and order props orient it with the frame, as for every opening.
  function coNorm(w, h) { w = Number(w) || 0; h = Number(h) || 0; return { w: Math.min(w, h), h: Math.max(w, h) }; }
  // The frame's inside size (mm) for a size title, orientation-free.
  function coFrameMm(sizeTitle) {
    var v = (state.variant && state.variant.title === sizeTitle) ? state.variant
      : (cfg.variants || []).find(function (x) { return x.title === sizeTitle; });
    var d = v ? printDimsMm(v) : null;
    return (d && d.w && d.h) ? coNorm(d.w, d.h) : null;
  }
  // Why the typed opening can't be cut in this frame, or '' when it can.
  function coError(sizeTitle) {
    if (!CO || !state.customOp) return '';
    var op = state.customOp, f = coFrameMm(sizeTitle || state.variant.title);
    if (!op.w || !op.h) return 'Enter your item\'s width and height.';
    if (op.w < CO_MIN_MM) return 'The opening must be at least ' + fmtCoLen(CO_MIN_MM) + ' on each side.';
    if (f && (op.w > f.w - 2 * CO_MIN_BORDER_MM || op.h > f.h - 2 * CO_MIN_BORDER_MM)) {
      return 'Too big for this frame - choose a larger frame size, or check the measurements.';
    }
    return '';
  }
  function fmtCoLen(mm) { return dispIn() ? (fmtInch(mm / 25.4) + '"') : (Math.round(mm) / 10 + 'cm'); }
  function coToInput(mm) { return mm ? (dispIn() ? String(Number((mm / 25.4).toFixed(2))) : String(Math.round(mm) / 10)) : ''; }
  function coFromInput(val) { var n = parseFloat(val); return n > 0 ? (dispIn() ? n * 25.4 : n * 10) : 0; }

  // The "your item's size" block. Built once, in JS, only on CO hubs, and placed
  // as a SIBLING of the mount opt-in (next to the printed-text block) because the
  // mobile sheet hides the mount panel itself - see the note on [data-pers-wrap].
  var elCo = null, elCoW = null, elCoH = null, elCoErr = null, elCoUnit = null;
  function buildCustomOpUi() {
    elCo = document.createElement('div');
    elCo.className = 'pfc-co';
    elCo.setAttribute('data-co', '');
    var html = '<p class="pfc-co__title">' + escapeHtml(CO.title || 'Your item\'s size') + '</p>';
    if (CO.presets && CO.presets.length) {
      html += '<div class="pfc-co__presets">';
      CO.presets.forEach(function (p, i) {
        html += '<button type="button" class="pfc-co__chip" data-co-preset="' + i + '" aria-pressed="false">' + escapeHtml(p.label) + '</button>';
      });
      html += '</div>';
    }
    html += '<div class="pfc-co__inputs">'
      + '<label class="pfc-co__field"><span>Width</span><input type="number" inputmode="decimal" min="0" step="0.1" data-co-w></label>'
      + '<span class="pfc-co__x" aria-hidden="true">×</span>'
      + '<label class="pfc-co__field"><span>Height</span><input type="number" inputmode="decimal" min="0" step="0.1" data-co-h></label>'
      + '<span class="pfc-co__unit" data-co-unit></span></div>'
      + '<p class="pfc-co__hint">' + escapeHtml(CO.hint || 'Measure your item edge to edge. We cut the opening a few millimetres smaller so the mount holds it.') + '</p>'
      + '<p class="pfc-co__err" role="alert" data-co-err hidden></p>';
    elCo.innerHTML = html;
    var anchor = elPersWrap || elAddMountWrap;
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(elCo, anchor);
    else root.appendChild(elCo);
    elCoW = elCo.querySelector('[data-co-w]');
    elCoH = elCo.querySelector('[data-co-h]');
    elCoErr = elCo.querySelector('[data-co-err]');
    elCoUnit = elCo.querySelector('[data-co-unit]');
    function onType() { setCustomOp(coFromInput(elCoW.value), coFromInput(elCoH.value), false); }
    elCoW.addEventListener('input', onType);
    elCoH.addEventListener('input', onType);
    Array.prototype.forEach.call(elCo.querySelectorAll('[data-co-preset]'), function (b) {
      b.addEventListener('click', function () {
        var p = CO.presets[Number(b.dataset.coPreset)];
        if (p) setCustomOp(p.w, p.h, true);
      });
    });
  }
  // w,h as the customer reads them (width across): a wide item turns the frame
  // landscape, a tall one portrait, via the normal orientation toggle.
  function setCustomOp(w, h, fromPreset) {
    state.customOp = coNorm(w, h);
    state.coShown = { w: w, h: h };
    if (w > 0 && h > 0 && w !== h) {
      var want = w > h ? 'landscape' : 'portrait';
      if (want !== state.orientation) {
        var ob = Array.prototype.find.call(elOrientationBtns, function (b) { return b.dataset.orientationBtn === want; });
        if (ob && !ob.hidden && !(ob.closest && ob.closest('[hidden]'))) ob.click();
      }
    }
    if (fromPreset) state.coFromPreset = true;
    refreshMountUi();
    syncMountButtons();
    updatePriceAndPhotoSize();
    renderPreview();
    if (fromPreset) { state.coFromPreset = false; renderCustomOpUi(true); }
  }
  // Show/hide with the mount and keep the boxes, unit and error in step.
  // force = rewrite the boxes even if the customer is typing (preset picked).
  function renderCustomOpUi(force) {
    if (!CO) return;
    if (!elCo) buildCustomOpUi();
    elCo.hidden = !state.mount;
    if (!state.mount) return;
    elCoUnit.textContent = dispIn() ? 'inches' : 'cm';
    var step = dispIn() ? '0.01' : '0.1';
    elCoW.step = step; elCoH.step = step;
    var typing = document.activeElement === elCoW || document.activeElement === elCoH;
    if (force || !typing) {
      var sh = state.coShown || { w: CO.default.w, h: CO.default.h };
      elCoW.value = coToInput(sh.w); elCoH.value = coToInput(sh.h);
    }
    Array.prototype.forEach.call(elCo.querySelectorAll('[data-co-preset]'), function (b) {
      var p = CO.presets[Number(b.dataset.coPreset)], o = state.customOp, n = p ? coNorm(p.w, p.h) : null;
      b.setAttribute('aria-pressed', String(!!(n && o && Math.abs(n.w - o.w) < 0.5 && Math.abs(n.h - o.h) < 0.5)));
    });
    var err = coError(state.variant.title);
    elCoErr.textContent = err; elCoErr.hidden = !err;
  }

  function customAperturesFor(sizeTitle) {
    if (!state.customOp) return null;
    // Same guard as hub-only openings: offered ONLY where this size carries the
    // "Standard Mount (Custom opening)" bundle, so the cart can never take the
    // frame alone while the page shows and prices a mount.
    var v = (state.variant && state.variant.title === sizeTitle) ? state.variant
      : (cfg.variants || []).find(function (x) { return x.title === sizeTitle; });
    var needle = 'Standard Mount (' + CO_LABEL + ' opening)';
    if (!((v && v.bundles) || []).some(function (b) { return b.name && b.name.indexOf(needle) !== -1; })) return null;
    // An opening too big for the frame is drawn at the largest that fits; the
    // cart refuses it (coError), so the clamp only keeps the preview sane.
    var f = coFrameMm(sizeTitle), w = state.customOp.w, h = state.customOp.h;
    if (f) { w = Math.min(w, f.w - 2 * CO_MIN_BORDER_MM); h = Math.min(h, f.h - 2 * CO_MIN_BORDER_MM); }
    return { small: { w: Math.max(w, 1), h: Math.max(h, 1), label: CO_LABEL, custom: true } };
  }

  function aperturesFor(sizeTitle) {
    if (CO) return customAperturesFor(sizeTitle);
    var ap = (cfg.apertureMap && cfg.apertureMap[sizeTitle]) || null;
    if (!ap || !root_cfg.openingOverrides || !root_cfg.openingOverrides[sizeTitle]) return ap;
    // A hub-only opening is offered ONLY where this moulding's variant carries its
    // "Standard Mount (<label> opening)" bundle. Without one the cart would take the
    // frame alone while the page showed (and priced) a mount.
    var v = (state.variant && state.variant.title === sizeTitle) ? state.variant
      : (cfg.variants || []).find(function (x) { return x.title === sizeTitle; });
    var bundles = (v && v.bundles) || [];
    var out = null;
    ['small', 'large'].forEach(function (k) {
      if (!ap[k]) return;
      var needle = 'Standard Mount (' + ap[k].label + ' opening)';
      if (bundles.some(function (b) { return b.name && b.name.indexOf(needle) !== -1; })) { out = out || {}; out[k] = ap[k]; }
    });
    return out;
  }

  // A mount is offered only off float-mount, with an aperture for the size and
  // configured mount options.
  function mountAvailable() {
    if (state.framingStyle === 'float') return false;
    var ap = aperturesFor(state.variant.title);
    return !!(ap && cfg.mountOptions && cfg.mountOptions.length);
  }

  function refreshMountUi() {
    if (!elMounts) return;
    var avail = mountAvailable();
    // The "Add a mount?" opt-in only appears when a mount is offered for the
    // current size/framing; otherwise hide it and clear the opt-in.
    if (elAddMountWrap) elAddMountWrap.hidden = !avail;
    if (!avail) {
      elMounts.hidden = true;
      if (elAddMount) elAddMount.checked = false;
      state.mount = null;
      hidePremiumPickers();
      if (typeof updatePersWrap === 'function') updatePersWrap();
      renderCompactMount();
      return;
    }
    // mount_default_on hubs (postcard, magazine...): a size with no opening unticks the
    // mount above, so re-tick it on the next size that has one - unless the customer
    // turned the mount off themselves. Without this, passing through a frame-only size
    // (A4 on the magazine hub) silently dropped the mount for the rest of the visit.
    if (elAddMount && !elAddMount.checked && root_cfg.mountDefaultOn === true && !state.mountUserOff && !OCC && !certActive()) elAddMount.checked = true;
    if (elAddMount && !elAddMount.checked && root_cfg.mountRequired === true && !certActive()) elAddMount.checked = true;
    // Picker stays hidden until the customer opts in. Opting in selects a
    // default mount (White, first opening); the mobile sheet has its own row.
    if (elAddMount && !elAddMount.checked) { elMounts.hidden = true; state.mount = null; hidePremiumPickers(); if (typeof updatePersWrap === 'function') updatePersWrap(); renderCompactMount(); return; }
    elMounts.hidden = false;
    if (!state.mount) { var def = resolveMount('white', firstAperture()); if (def) state.mount = def; }
    renderMountOpenings();
    updateMountColourPrices();
    syncMountButtons();
    syncPremiumPickers();
    if (typeof updatePersWrap === 'function') { updatePersWrap(); updatePersPrice(); }
    renderCompactMount();
  }
  function hidePremiumPickers() {
    elMountPatternsAll.forEach(function (el) { el.hidden = true; });
    elStripeWrapsAll.forEach(function (el) { el.hidden = true; });
  }
  // Paint the mount board into (x,y,w,h): solid for white/black, or the chosen
  // pattern (stripes/checkers) on a white base for Premium. boardWmm = the board
  // width in mm, used to size stripe bands / checker cells.
  // Pure, DOM-free, resolution-independent mount-board fill — the shared render
  // core for the print-file admin app (docs/PRINT-FILE-ADMIN-APP.md). Takes ANY
  // CanvasRenderingContext2D (browser OR node-canvas), the fill box in px, the
  // real board width in mm (sets pattern density so it holds at any DPI), and a
  // plain spec object — no `state`, no DOM. drawMountFill() below adapts live
  // state to this. Keep this function and PREMIUM_PATTERNS / STRIPE_BASE_HEX /
  // stripeBandCount together when lifting into the shared module.
  // spec = { premium:bool, pattern:{type,...}|null, stripeColour:hex, plainHex:hex }
  /* watercolour "Brushstroke Stripe" — KEEP IN LOCKSTEP with pfc-mount-pattern-core.js */
  function _wcRand(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function _wcNoise(seed) { var rnd = _wcRand(seed >>> 0), g = new Float32Array(512); for (var i = 0; i < 512; i++) g[i] = rnd() * 2 - 1; return function (x) { var i = Math.floor(x), f = x - i, a = g[(i & 511)], b = g[((i + 1) & 511)], u = f * f * (3 - 2 * f); return a + (b - a) * u; }; }
  function _wcDisp(noise, t, phase) { return noise(t * 1.0 + phase) * 0.7 + noise(t * 2.7 + phase * 1.7) * 0.3; }
  function _wcRgb(hex) { hex = String(hex || '#888888').replace('#', ''); if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]; var n = parseInt(hex, 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; }
  function _wcShade(hex, amt) { var c = _wcRgb(hex), f = amt < 0 ? 0 : 255, p = Math.abs(amt); return 'rgb(' + (Math.round((f - c[0]) * p) + c[0]) + ',' + (Math.round((f - c[1]) * p) + c[1]) + ',' + (Math.round((f - c[2]) * p) + c[2]) + ')'; }
  function paintWatercolourStripes(c, x, y, w, h, boardWmm, pat, stripeColour) {
    var pxPerMm = w / (boardWmm || 210);
    var bandPx = (pat.bandMm || 26) * pxPerMm;
    var gapPx = (pat.gapMm || 14) * pxPerMm;
    var period = bandPx + gapPx; if (period <= 0) { bandPx = period = 1; }
    var nBands = Math.ceil(w / period) + 1;
    var ampPx = (pat.wobbleMm || 3) * pxPerMm;
    var vFreqMm = 52, pool = (pat.poolAmt != null ? pat.poolAmt : 0.55);
    var noise = _wcNoise((((pat.seed || 7) * 2654435761) >>> 0));
    var step = Math.max(4, h / 160);
    // Overscan the band path past the top/bottom edges so it fully covers them
    // under fractional coords / devicePixelRatio scaling (caller clips to the
    // board rect, so the bleed is trimmed cleanly — no white hairline at edges).
    var OV = Math.max(step, ampPx) + 2, yTop = y - OV, yBot = y + h + OV;
    function edgeX(baseX, phase, yy) { var t = ((yy - y) / pxPerMm) / vFreqMm; return baseX + _wcDisp(noise, t, phase) * ampPx; }
    for (var b = 0; b < nBands; b++) {
      var lBase = x + b * period, rBase = x + b * period + bandPx, lPh = (2 * b) * 11.37, rPh = (2 * b + 1) * 11.37, yy;
      function bandPath() {
        c.beginPath();
        for (yy = yTop; yy <= yBot; yy += step) { var px = edgeX(lBase, lPh, yy); if (yy === yTop) c.moveTo(px, yy); else c.lineTo(px, yy); }
        c.lineTo(edgeX(lBase, lPh, yBot), yBot);
        for (yy = yBot; yy >= yTop; yy -= step) c.lineTo(edgeX(rBase, rPh, yy), yy);
        c.closePath();
      }
      bandPath();
      c.fillStyle = _wcShade(stripeColour, (noise(b * 3.1) * 0.5) * 0.06); c.fill();
      if (pool > 0) {
        c.save(); c.clip();
        c.lineWidth = Math.max(2, ampPx * 0.9 + 3); c.strokeStyle = _wcShade(stripeColour, -0.35); c.globalAlpha = 0.16 * pool; c.stroke();
        c.lineWidth = Math.max(1, ampPx * 0.4 + 1.5); c.globalAlpha = 0.22 * pool; c.stroke();
        c.globalAlpha = 1; c.restore();
      }
      c.save(); bandPath(); c.clip();
      var streaks = 2 + ((b * 7) % 3);
      for (var s = 0; s < streaks; s++) {
        var sx = lBase + (0.2 + 0.6 * noise(b * 5 + s * 13)) * bandPx;
        var grd = c.createLinearGradient(sx - 6, 0, sx + 6, 0);
        grd.addColorStop(0, 'rgba(0,0,0,0)'); grd.addColorStop(0.5, _wcShade(stripeColour, -0.25)); grd.addColorStop(1, 'rgba(0,0,0,0)');
        c.globalAlpha = 0.05; c.fillStyle = grd; c.fillRect(sx - 6, yTop, 12, yBot - yTop);
      }
      c.globalAlpha = 1; c.restore();
    }
  }
  function paintMountFill(c, x, y, w, h, boardWmm, spec) {
    if (spec && spec.premium && spec.pattern) {
      var pat = spec.pattern;
      var stripeColour = spec.stripeColour || '#888888';
      c.fillStyle = STRIPE_BASE_HEX;
      c.fillRect(x, y, w, h);
      if (pat.type === 'stripes') {
        var n = stripeBandCount(boardWmm, pat);
        var bw = w / n;
        for (var i = 0; i < n; i++) {
          c.fillStyle = (i % 2 === 0) ? STRIPE_BASE_HEX : stripeColour;
          c.fillRect(x + i * bw, y, Math.ceil(bw) + 1, h);
        }
      } else if (pat.type === 'checkers') {
        var pxPerMm = w / (boardWmm || 210);
        var cell = Math.max(6, (pat.checkMm || 15) * pxPerMm);
        var cols = Math.ceil(w / cell), rows = Math.ceil(h / cell);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour;
        for (var ry = 0; ry < rows; ry++) {
          for (var cx = 0; cx < cols; cx++) {
            if ((cx + ry) % 2 === 0) continue;
            c.fillRect(x + cx * cell, y + ry * cell, Math.ceil(cell) + 1, Math.ceil(cell) + 1);
          }
        }
        c.restore();
      } else if (pat.type === 'gingham') {
        // Two semi-transparent band passes (V + H); overlaps read darkest.
        var gpx = w / (boardWmm || 210);
        var gcell = Math.max(6, (pat.cellMm || 16) * gpx);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour; c.globalAlpha = 0.42;
        var gcols = Math.ceil(w / gcell), grows = Math.ceil(h / gcell);
        for (var gx = 0; gx < gcols; gx += 2) c.fillRect(x + gx * gcell, y, Math.ceil(gcell) + 1, h);
        for (var gy = 0; gy < grows; gy += 2) c.fillRect(x, y + gy * gcell, w, Math.ceil(gcell) + 1);
        c.globalAlpha = 1; c.restore();
      } else if (pat.type === 'polka') {
        var ppx = w / (boardWmm || 210);
        var pcell = Math.max(8, (pat.dotMm || 18) * ppx);
        var prd = pcell * 0.3;
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.fillStyle = stripeColour;
        var prows = Math.ceil(h / pcell) + 1, pcols = Math.ceil(w / pcell) + 1;
        for (var pry = 0; pry < prows; pry++) {
          for (var pcx = 0; pcx < pcols; pcx++) {
            var ox = x + pcx * pcell + (pry % 2 ? pcell / 2 : 0) + pcell / 2;
            var oy = y + pry * pcell + pcell / 2;
            c.beginPath(); c.arc(ox, oy, prd, 0, Math.PI * 2); c.fill();
          }
        }
        c.restore();
      } else if (pat.type === 'chevron') {
        var cpx = w / (boardWmm || 210);
        var amp = Math.max(5, (pat.bandMm || 14) * cpx);
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        c.strokeStyle = stripeColour; c.lineWidth = Math.max(2, amp * 0.5); c.lineCap = 'butt';
        for (var cyy = y - h; cyy < y + h * 2; cyy += amp * 2) {
          c.beginPath();
          var up = true;
          for (var cxx = x; cxx <= x + w + amp; cxx += amp) {
            var cpy = cyy + (up ? 0 : amp);
            if (cxx === x) c.moveTo(cxx, cpy); else c.lineTo(cxx, cpy);
            up = !up;
          }
          c.stroke();
        }
        c.restore();
      } else if (pat.type === 'watercolour') {
        c.save(); c.beginPath(); c.rect(x, y, w, h); c.clip();
        paintWatercolourStripes(c, x, y, w, h, boardWmm, pat, stripeColour);
        c.restore();
      }
      // 'accent' is a keyline ring drawn around the aperture in renderPreview,
      // not a board fill — base colour only here.
      return;
    }
    c.fillStyle = (spec && spec.plainHex) || '#fafaf6';
    c.fillRect(x, y, w, h);
  }

  // Live-preview adapter: pulls the chosen mount/pattern/colour out of `state`
  // and delegates to the pure paintMountFill core above.
  function drawMountFill(c, x, y, w, h, boardWmm) {
    var premium = isPremiumMount();
    paintMountFill(c, x, y, w, h, boardWmm, {
      premium: premium,
      pattern: premium ? patternByKey(state.mount.pattern) : null,
      stripeColour: state.stripeColour,
      plainHex: (state.mount && state.mount.colour === 'black') ? '#1a1a1a' : '#fafaf6',
    });
  }

  /* ---------- Compact (mobile sheet) controls ---------- */
  // Custom-select-style triggers + popovers that replace the chunky size /
  // mount button grids when the bottom-sheet is active. State lives in the
  // existing handlers; compact UI just drives them by simulating clicks or
  // calling selectVariant directly.
  // compactState declared near the top of the IIFE so it's defined when
  // renderCompactMount runs during the initial selectVariant call.

  function renderCompactSize() {
    if (!elCompactSizeList) return;
    var html = '';
    cfg.variants.forEach(function (v) {
      // Same filters as the desktop dropdown: cm/inch toggle + the hub's size
      // allowlist / frame-only gate / specialist sizes. Without sizeVisible the
      // sheet offered sizes the hub deliberately hides on desktop.
      if (variantUnit(v) !== state.sizeUnit) return;
      if (!sizeVisible(v)) return;
      var sel = v.id === state.variant.id;
      html += '<li class="pfc-moulding__compact-option" role="option" data-vid="' + v.id + '"'
        + ' aria-selected="' + (sel ? 'true' : 'false') + '"'
        + (v.available ? '' : ' aria-disabled="true"')
        + '>' + escapeHtml(orientedTitle(v.title))
        + '<span class="pfc-moulding__compact-option-aux">' + formatMoney(sizeListPrice(v)) + '</span>'
        + '</li>';
    });
    elCompactSizeList.innerHTML = html;
    if (elCompactSizeValue) elCompactSizeValue.textContent = orientedTitle(state.variant.title);
  }

  function renderCompactMount() {
    if (CO) renderCustomOpUi(false);
    if (!elCompactMountRow) return;
    if (state.framingStyle === 'float') { elCompactMountRow.hidden = true; return; }
    var ap = aperturesFor(state.variant.title);
    if (!ap || !cfg.mountOptions || !cfg.mountOptions.length) {
      elCompactMountRow.hidden = true;
      return;
    }
    elCompactMountRow.hidden = false;

    // Build aperture list (small / large / no mount). Cert hubs collapse this to
    // a single document-size opening (the mount grows the frame, not the opening).
    var _cg = certActive() ? certConfig() : null;
    var html = MOUNT_REQ ? '' : '<li class="pfc-moulding__compact-option" role="option" data-aperture=""'
      + ' aria-selected="' + (state.mount ? 'false' : 'true') + '">No mount</li>';   // occasion: the mount is required
    if (_cg) {
      html += '<li class="pfc-moulding__compact-option" role="option" data-aperture="' + _cg.apKey + '"'
        + ' aria-selected="' + (state.mount ? 'true' : 'false') + '">'
        + escapeHtml(cfg.certTargetSize) + ' opening</li>';
    } else {
      ['small', 'large'].forEach(function (key) {
        var d = ap[key]; if (!d) return;
        var sel = state.mount && state.mount.aperture === key;
        html += '<li class="pfc-moulding__compact-option" role="option" data-aperture="' + key + '"'
          + ' aria-selected="' + (sel ? 'true' : 'false') + '">'
          + escapeHtml(d.label) + ' opening'
          + '</li>';
      });
    }
    if (elCompactMountList) elCompactMountList.innerHTML = html;

    // Sync value label + colour visibility.
    if (state.mount) {
      var apData = ap[state.mount.aperture];
      var colCap = state.mount.colour.charAt(0).toUpperCase() + state.mount.colour.slice(1);
      if (elCompactMountValue) elCompactMountValue.textContent = colCap + ' · ' + (apData ? apData.label : '');
      if (elCompactColour) elCompactColour.hidden = false;
    } else {
      if (elCompactMountValue) elCompactMountValue.textContent = 'No mount';
      if (elCompactColour) elCompactColour.hidden = true;
    }
    if (compactState && compactState.aperture) {
      if (elCompactColour) elCompactColour.hidden = false;
    }
    elCompactColourBtns.forEach(function (b) {
      var on = state.mount && state.mount.colour === b.dataset.compactColourBtn;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function setCompactPopover(name) {
    compactState.open = name || null;
    [['size', elCompactSizeTrigger, elCompactSizeList],
     ['mount', elCompactMountTrigger, elCompactMountList],
     ['framing', elCompactFramingTrigger, elCompactFramingList]].forEach(function (row) {
      var n = row[0], trig = row[1], list = row[2];
      var on = compactState.open === n;
      if (trig) trig.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (list) list.hidden = !on;
    });
  }

  function applyCompactColour(colour) {
    var apKey = (state.mount && state.mount.aperture) || compactState.aperture;
    var m = resolveMount(colour, apKey);
    if (!m) return;
    // Route through commitMount so the sheet takes the SAME path as the desktop
    // picker. Hand-rolling the steps here dropped updatePersWrap/updatePersPrice,
    // which left the printed text/logo add-on hidden on mobile forever.
    commitMount(m);
  }

  if (elCompactSizeTrigger) {
    elCompactSizeTrigger.addEventListener('click', function () {
      setCompactPopover(compactState.open === 'size' ? null : 'size');
    });
  }
  if (elCompactSizeList) {
    elCompactSizeList.addEventListener('click', function (e) {
      var li = e.target.closest('[data-vid]');
      if (!li || li.getAttribute('aria-disabled') === 'true') return;
      var v = cfg.variants.find(function (vv) { return vv.id === Number(li.dataset.vid); });
      if (!v) return;
      state.desiredSizeTitle = v.title;
      selectVariant(v);
      renderCompactSize();
      setCompactPopover(null);
      evaluateRecommendation('size-change');
    });
  }
  if (elCompactMountTrigger) {
    elCompactMountTrigger.addEventListener('click', function () {
      setCompactPopover(compactState.open === 'mount' ? null : 'mount');
    });
  }
  if (elCompactMountList) {
    elCompactMountList.addEventListener('click', function (e) {
      var li = e.target.closest('[data-aperture]');
      if (!li) return;
      var apKey = li.dataset.aperture; // '' | 'small' | 'large'
      // Cert hubs: adding/removing a mount grows/shrinks the FRAME, not the opening.
      if (certActive()) {
        applyCertFrame(!!apKey);
        compactState.aperture = apKey || null;
        renderCompactMount();
        setCompactPopover(null);
        return;
      }
      state.mountUserOff = !apKey;   // the customer's own choice (see refreshMountUi)
      if (!apKey) {
        // No mount.
        state.mount = null;
        compactState.aperture = null;
        syncMountButtons();
        syncPremiumPickers();
        if (typeof updatePersWrap === 'function') { updatePersWrap(); updatePersPrice(); }
        updatePriceAndPhotoSize();
        renderPreview();
      } else {
        compactState.aperture = apKey;
        // Default to white if no current colour.
        var col = (state.mount && state.mount.colour) || 'white';
        applyCompactColour(col);
      }
      renderCompactMount();
      setCompactPopover(null);
    });
  }
  elCompactColourBtns.forEach(function (b) {
    b.addEventListener('click', function () { applyCompactColour(b.dataset.compactColourBtn); });
  });

  // Close popovers when tapping outside the compact controls.
  document.addEventListener('pointerdown', function (e) {
    if (!compactState.open) return;
    if (elCompact && elCompact.contains(e.target)) return;
    setCompactPopover(null);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && compactState.open) { e.preventDefault(); setCompactPopover(null); }
  });

  function updatePriceAndPhotoSize() {
    var v = state.variant;
    // Float-mount bills as the nearest larger size point (the grown frame needs
    // the bigger moulding); otherwise the selected variant.
    var priceVar = floatPriceVariant(state.floatGapMm);
    // Float on the custom-framing ladder: one price for the exact frame built.
    var fq = floatQuote(state.floatGapMm);
    if (fq) priceVar = { id: cfg.floatPricing.hostId, title: 'float-mount, ' + floatOpeningText(fq.opening) + ' frame', price: fq.priceCents };
    var glazing = glazingLinePrice();
    var total = Number(priceVar.price) + mountLinePrice() + glazing;
    var priceText = formatMoney(total);
    refreshGapPrices();
    refreshGlazingPrices();
    renderBreakdown(priceVar, glazing, total);
    if (elPrice) elPrice.textContent = priceText;
    // Running total on the CTA. Quote hubs never add to cart, so the button
    // stays a quote request and the estimate rides alongside it.
    if (elAtc && !elAtc.disabled && !(photoSrc && photoSrc.failed())) {
      elAtc.textContent = (cfg && cfg.quoteMode)
        ? 'Request a quote · est. ' + priceText
        : 'Add to cart · ' + priceText;
    }
    // Mirror price + active moulding/size into the mobile bottom-sheet handle.
    if (elSheetPrice) elSheetPrice.textContent = priceText;
    if (elSheetSummary) {
      var family = (cfg && cfg.mouldingFamily) || (cfg && cfg.productTitle) || '';
      elSheetSummary.textContent = family ? (family + ' · ' + v.title) : v.title;
    }

    // Photo size label (visible only if mount selected).
    if (elMountPhotosize) {
      if (state.mount) {
        var ap = aperturesFor(v.title);
        var d = ap && ap[state.mount.aperture];
        elMountPhotosize.textContent = d ? ('· photo opening ' + d.label) : '';
      } else {
        elMountPhotosize.textContent = '';
      }
    }

    updateFrameSizeUi();
  }

  // Frame outer-size readout — shown for every framing style. Outer = print +
  // 2× float surround (0 unless float) + 2× moulding face width. The print
  // (image) stays the selected size; only float grows the frame.
  function updateFrameSizeUi() {
    updateSpecStrip();
    if (!elExtDims.length) return;
    var v = state.variant;
    var pm = printDimsMm(v);
    if (!pm.w || !pm.h) { elExtDims.forEach(function (el) { el.hidden = true; }); return; }
    var fw = Math.round(faceWidthMm());
    var surr = floatSurroundMm();
    // Mount border (per side) — PRESET/vintage-map only. Non-preset variants have
    // no _mountMm, so mountMm = 0 → the line is just the external dims for them.
    var mountMm = (presetActive && v && v._mountMm) ? v._mountMm : 0;
    var outerW = Math.round(pm.w + surr * 2 + fw * 2 + mountMm * 2);
    var outerH = Math.round(pm.h + surr * 2 + fw * 2 + mountMm * 2);
    // Every value below is a formatted number, so innerHTML is safe here.
    var html = 'Frame external dims <b>' + fmtPairDisp(outerW, outerH) + '</b>';
    // Float grows the frame beyond the print — say by how much.
    if (surr > 0) html += ' <span class="pfc-moulding__extdims-badge">+' + fmtDimDisp(surr * 2) + ' float</span>';
    if (mountMm > 0) {
      var mW = Math.round(pm.w + mountMm * 2), mH = Math.round(pm.h + mountMm * 2);
      var usVocab = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) === 'USD';
      html += ' · ' + (usVocab ? 'Mat' : 'Mount') + ' <b>' + fmtPairDisp(mW, mH) + '</b>, hand-cut and included';
    }
    elExtDims.forEach(function (el) { el.innerHTML = html; el.hidden = false; });
  }

  /* ---------- Spec strip ---------- */
  // Face-width word bands. Mirror the Liquid first-paint bands in
  // pfc-moulding-shell.liquid — change both together.
  function specFaceWord(mm) { return mm < 20 ? 'slim' : (mm < 35 ? 'classic' : 'wide'); }
  // Service hubs (cfg.enableGlazing) let the customer upgrade the glazing, so
  // the cell names what they chose; everywhere else it is cast acrylic.
  function specGlazeKey() {
    return (cfg && cfg.enableGlazing && (state.glazing === 'acrylic' || state.glazing === 'glass')) ? state.glazing : '';
  }
  function specText(key) {
    var face = cfg.mouldingWidthMm || 20, depth = cfg.mouldingDepthMm || 0;
    if (key === 'face') {
      var w = specFaceWord(face);
      return '<b>' + fmtMouldDisp(face) + ' face width.</b> ' + (
        w === 'slim' ? 'A slim, modern border that keeps the focus on your picture.'
        : w === 'classic' ? 'A classic border width that suits most pictures and rooms.'
        : 'A bold, gallery-style border that gives your picture real presence.');
    }
    if (key === 'depth') {
      return '<b>' + fmtMouldDisp(depth) + ' deep.</b> ' + (
        depth < 20 ? 'Sits close to the wall.'
        : depth < 30 ? 'A standard depth off the wall.'
        : 'Stands well off the wall for a deeper, shadow-box look.');
    }
    if (key === 'glaze') {
      var g = specGlazeKey();
      if (g === 'glass') return '<b>Museum / UV glass.</b> Filters UV to protect the artwork. Collection or local delivery only.';
      if (g === 'acrylic') return '<b>Conservation acrylic.</b> Filters UV to protect the artwork, and ships UK-wide.';
      return '<b>Cast-acrylic glazing.</b> Optically clear, about half the weight of glass, and shatter-resistant, so it arrives safely.';
    }
    if (key === 'hang') return '<b>Ready to hang.</b> Hanging fittings come attached, so it can go straight on the wall.';
    return '';
  }
  // Specification table in the details panel: same values as the strip, in
  // the active unit. Runs before the strip guard so it works without one.
  function updateDetailsSpec() {
    if (!cfg) return;
    var face = cfg.mouldingWidthMm || 20, depth = cfg.mouldingDepthMm || 0;
    var g = specGlazeKey();
    var elFace = root.querySelector('[data-details-face]');
    var elDepth = root.querySelector('[data-details-depth]');
    var elDepthRow = root.querySelector('[data-details-depth-row]');
    var elGlaze = root.querySelector('[data-details-glaze]');
    if (elFace) elFace.textContent = fmtMouldDisp(face);
    if (elDepth) { elDepth.hidden = !depth; if (depth) elDepth.textContent = fmtMouldDisp(depth); }
    if (elDepthRow) elDepthRow.hidden = !depth;
    if (elGlaze) elGlaze.textContent = g === 'glass' ? 'Museum UV glass'
      : (g === 'acrylic' ? 'Conservation UV acrylic' : 'Clear cast acrylic, shatter-resistant');
  }
  function updateSpecStrip() {
    updateDetailsSpec();
    if (!elSpecStrip || !cfg) return;
    var face = cfg.mouldingWidthMm || 20, depth = cfg.mouldingDepthMm || 0;
    var g = specGlazeKey();
    elSpecBtns.forEach(function (b) {
      var lab = b.querySelector('[data-spec-label]');
      if (!lab) return;
      var k = b.dataset.spec;
      if (k === 'face') lab.textContent = fmtMouldDisp(face) + ' ' + specFaceWord(face);
      else if (k === 'depth') { b.hidden = !depth; if (depth) lab.textContent = fmtMouldDisp(depth) + ' deep'; }
      else if (k === 'glaze') lab.textContent = g === 'glass' ? 'UV glass' : (g === 'acrylic' ? 'UV acrylic' : 'Cast acrylic');
    });
    if (specOpen === 'depth' && !depth) { setSpecOpen(null); return; }
    if (specOpen && elSpecText && !specSwapTimer) elSpecText.innerHTML = specText(specOpen);
    placeSpecBar();
  }
  // Slide the active-cell underline to the open cell. Appears in place (no
  // slide from the left edge) when nothing was open before.
  function placeSpecBar() {
    if (!elSpecBar || !elSpecStrip) return;
    var b = specOpen ? elSpecStrip.querySelector('[data-spec="' + specOpen + '"]') : null;
    if (!b || b.hidden) { elSpecBar.classList.remove('is-on'); return; }
    var fresh = !elSpecBar.classList.contains('is-on');
    if (fresh) elSpecBar.classList.add('is-snap');
    elSpecBar.style.width = b.offsetWidth + 'px';
    elSpecBar.style.transform = 'translateX(' + b.offsetLeft + 'px)';
    if (fresh) { void elSpecBar.offsetWidth; elSpecBar.classList.remove('is-snap'); }
    elSpecBar.classList.add('is-on');
  }
  // Drawer motion: height eases between explicit px values (auto cannot be
  // transitioned), then is released to auto so a resize reflows it. Switching
  // cells fades the old line out, swaps it, and eases to the new height while
  // fading in — so the row below never jumps.
  function specSetHeight(px) {
    elSpecDrawer.style.height = px + 'px';
    if (specAutoTimer) clearTimeout(specAutoTimer);
    specAutoTimer = setTimeout(function () {
      specAutoTimer = null;
      if (specOpen) elSpecDrawer.style.height = 'auto';
    }, 320);
  }
  function setSpecOpen(key) {
    if (!elSpecDrawer || !elSpecText) return;
    var wasOpen = !!specOpen;
    specOpen = key;
    elSpecBtns.forEach(function (b) {
      b.setAttribute('aria-expanded', String(b.dataset.spec === key));
    });
    placeSpecBar();
    if (specSwapTimer) { clearTimeout(specSwapTimer); specSwapTimer = null; }
    // Pin the current height so every transition starts from a real px value.
    if (!elSpecDrawer.hidden) elSpecDrawer.style.height = elSpecDrawer.offsetHeight + 'px';
    if (!key) {
      void elSpecDrawer.offsetHeight;
      elSpecDrawer.classList.remove('is-open');
      specSetHeight(0);
      specSwapTimer = setTimeout(function () { specSwapTimer = null; elSpecDrawer.hidden = true; }, 300);
      return;
    }
    if (!wasOpen) {
      elSpecText.innerHTML = specText(key);
      if (elSpecDrawer.hidden) { elSpecDrawer.hidden = false; elSpecDrawer.style.height = '0px'; }
      void elSpecDrawer.offsetHeight;
      elSpecDrawer.classList.add('is-open');
      specSetHeight(elSpecText.offsetHeight);
      return;
    }
    elSpecDrawer.classList.remove('is-open');
    specSwapTimer = setTimeout(function () {
      specSwapTimer = null;
      elSpecText.innerHTML = specText(key);
      elSpecDrawer.classList.add('is-open');
      specSetHeight(elSpecText.offsetHeight);
    }, 140);
  }

  /* ---------- Canvas live preview ---------- */
  // Pixel-space renderer adapted from dropshipping-project's
  // CustomerFramingWidget. The moulding texture is TILED (createPattern,
  // 'repeat') not stretched: each rail scales the texture so its height fills
  // the frame depth (fw), then repeats along the rail length — preserving the
  // pattern's natural proportions at every aspect ratio.
  // Deterministic pseudo-random for the deckled-edge polygon. Sin-based so
  // the same (i, seed) returns the same jitter across redraws — keeps the
  // torn edge stable when the customer toggles size / scene etc.
  function decklePseudoRand(i, seed) {
    var s = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
    return s - Math.floor(s);
  }
  function drawDecklePath(ctx, x, y, w, h, amp) {
    var steps = 24;
    var seed = ((w * 13 + h * 7) | 0) >>> 0;
    ctx.beginPath();
    var px, py, t;
    for (var i = 0; i <= steps; i++) {
      t = i / steps;
      px = x + w * t;
      py = y + (decklePseudoRand(i, seed) - 0.5) * amp;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    for (var j = 1; j <= steps; j++) {
      t = j / steps;
      px = x + w + (decklePseudoRand(j + steps, seed) - 0.5) * amp;
      py = y + h * t;
      ctx.lineTo(px, py);
    }
    for (var k = 1; k <= steps; k++) {
      t = k / steps;
      px = x + w - w * t;
      py = y + h + (decklePseudoRand(k + steps * 2, seed) - 0.5) * amp;
      ctx.lineTo(px, py);
    }
    for (var l = 1; l < steps; l++) {
      t = l / steps;
      px = x + (decklePseudoRand(l + steps * 3, seed) - 0.5) * amp;
      py = y + h - h * t;
      ctx.lineTo(px, py);
    }
    ctx.closePath();
  }

  // Up close section (sections/pfc-moulding-upclose.liquid) follows the active
  // moulding, mount and framing style. Fired from renderPreview, which runs on
  // every one of those changes, but only when one of the three actually moved.
  var lastUpclose = '';
  function emitUpcloseState() {
    var d = { mouldingId: cfg && cfg.productId, mount: !!state.mount, style: state.framingStyle || 'standard' };
    var key = d.mouldingId + '|' + d.mount + '|' + d.style;
    if (key === lastUpclose) return;
    lastUpclose = key;
    root_cfg.upcloseState = d;   // for a listener that attaches after the first event
    try { root.dispatchEvent(new CustomEvent('pfc:upclose-state', { bubbles: true, detail: d })); } catch (_) {}
  }

  function renderPreview() {
    emitUpcloseState();
    // Size / opening / orientation all land here — re-test the printed-text fit
    // before drawing so the add-on blocks the moment the border goes too narrow.
    if (typeof updatePersFit === 'function') updatePersFit();
    pf().update();
    if (!elCanvas) return;
    // Name-art mode: regenerate the design whenever the print aspect changes
    // (size/orientation/mount swap), so it fills the window without cover-cropping.
    if (naActive && naReady && state.naAspect !== currentAspectRatio()) { nameArtRegen(); return; }
    // Uploaded photo: the same idea, for the customer's own crop.
    if (cropAspectDrifted()) scheduleRecrop();
    var ctx = elCanvas.getContext('2d');
    if (!ctx) return;

    // Use the layout size (clientWidth/Height), NOT getBoundingClientRect —
    // when the 3D tilt is active getBoundingClientRect returns the perspective-
    // projected (skewed/larger) rect, which would re-fit the frame to the wrong
    // size on any redraw (hover/resize/texture load) and misalign it with the
    // static DOM side faces.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Logical canvas size MUST keep the element's true aspect ratio: in depth
    // view the box is sized to the frame (sizeDepthBox) and the static DOM side
    // faces pin to the canvas edge, so any W:H mismatch letterboxes the drawn
    // frame and opens a gap between the front face and the sides. Floor the
    // resolution UNIFORMLY (scale both axes by one factor) rather than clamping
    // each axis to 320 independently — the per-axis floor squared the buffer on
    // small/portrait phones and caused exactly that gap.
    var cw = elCanvas.clientWidth || elCanvas.getBoundingClientRect().width || 440;
    var ch = elCanvas.clientHeight || elCanvas.getBoundingClientRect().height || 440;
    var bigDim = Math.max(cw, ch);
    if (bigDim < 320) { var kUp = 320 / bigDim; cw *= kUp; ch *= kUp; }
    var W = Math.round(cw);
    var H = Math.round(ch);
    var BW = W * dpr, BH = H * dpr;
    if (elCanvas.width !== BW || elCanvas.height !== BH) {
      elCanvas.width = BW;
      elCanvas.height = BH;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    var v = state.variant;
    var d = effectiveDim(v);
    if (!d) return;
    // Print (image) stays the selected size. The frame grows by the float
    // surround (state.animSurr, tweened) — 0 for standard/gallery/mount so
    // those paths render exactly as before.
    var pm = printDimsMm(v);
    var printW_mm = pm.w, printH_mm = pm.h;
    var fw_mm = faceWidthMm();
    var surr_mm = state.framingStyle === 'float' ? Math.max(0, state.animSurr) : 0;
    // Preset (vintage map): a white mount surrounds the print. state.mount is null
    // (no aperture UI), so carry the mount border on the tier and render it here.
    var presetMount_mm = (presetActive && state.variant && state.variant._mountMm) ? state.variant._mountMm : 0;

    var totalW_mm = printW_mm + presetMount_mm * 2 + surr_mm * 2 + fw_mm * 2;
    var totalH_mm = printH_mm + presetMount_mm * 2 + surr_mm * 2 + fw_mm * 2;

    var scaleScene = state.scene === 'scale';
    var depthMode = state.depthView || (elPreview && elPreview.classList.contains('is-3d'));

    var scale, outerW, outerH, fw, x0, y0, floorY;

    if (scaleScene) {
      // To-scale comparison: frame + a person silhouette share one mm->px scale.
      var PADpx = 26;
      var PERSON_H = 1680, PERSON_W = 520, GAP = 260, FRAME_CENTRE = 1480;
      var topNeeded = Math.max(PERSON_H, FRAME_CENTRE + totalH_mm / 2) + 130;
      var sceneW_mm = PERSON_W + GAP + totalW_mm;
      var sceneH_mm = topNeeded + 70;
      scale = Math.min((W - PADpx * 2) / sceneW_mm, (H - PADpx * 2) / sceneH_mm);
      var sceneWpx = sceneW_mm * scale, sceneHpx = sceneH_mm * scale;
      var originX = (W - sceneWpx) / 2;
      floorY = (H + sceneHpx) / 2 - 70 * scale;
      outerW = totalW_mm * scale; outerH = totalH_mm * scale;
      fw = fw_mm * scale;
      var fcx = originX + (PERSON_W + GAP + totalW_mm / 2) * scale;
      var fcy = floorY - FRAME_CENTRE * scale;
      x0 = fcx - outerW / 2; y0 = fcy - outerH / 2;
      drawScaleWall(ctx, W, H, floorY);
      drawPerson(ctx, originX + (PERSON_W / 2) * scale, floorY, PERSON_H * scale);
      ctx.fillStyle = 'rgba(74,62,48,0.6)';
      ctx.font = '600 12px ' + UI_FONT;
      ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      ctx.fillText('Shown to scale · person ≈ 1.7 m', W / 2, H - 12);
    } else {
      // Plain backdrop.
      var bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#f9f4ec');
      bg.addColorStop(1, '#f1e9da');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      var pad = depthMode ? 0 : 30;
      scale = Math.min((W - pad * 2) / totalW_mm, (H - pad * 2) / totalH_mm);
      outerW = totalW_mm * scale; outerH = totalH_mm * scale;
      fw = fw_mm * scale;
      x0 = (W - outerW) / 2;
      y0 = (H - outerH) / 2;
    }

    var innerX = x0 + fw, innerY = y0 + fw;
    var innerW = outerW - fw * 2, innerH = outerH - fw * 2;

    var tex = state.mouldingTextureImg;
    var hasTex = !!(tex && tex.complete && tex.naturalWidth > 0);
    var frameColour = '#3a2c1d';

    // Outer drop shadow (stronger in the to-scale scene so the frame reads as
    // hung on the wall).
    ctx.save();
    ctx.shadowColor = scaleScene ? 'rgba(40,28,12,0.34)' : 'rgba(60,45,25,0.22)';
    ctx.shadowBlur = scaleScene ? 34 : 26;
    ctx.shadowOffsetX = scaleScene ? 2 : 3;
    ctx.shadowOffsetY = scaleScene ? 20 : 14;
    ctx.fillStyle = frameColour;
    ctx.fillRect(x0, y0, outerW, outerH);
    ctx.restore();

    // ----- Mitred rails, texture tiled -----
    // ox/oy = outer corner start, the rail runs to the next outer corner;
    // ix/iy = matching inner corners. tx/ty/angle place the tiling space so
    // +x runs along the rail, +y goes from inner edge outward.
    function drawRail(ox1, oy1, ox2, oy2, ix1, iy1, ix2, iy2, gradDir, tx, ty, angle) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(ox1, oy1); ctx.lineTo(ox2, oy2);
      ctx.lineTo(ix2, iy2); ctx.lineTo(ix1, iy1);
      ctx.closePath();
      ctx.clip();

      if (hasTex) {
        ctx.translate(tx, ty);
        ctx.rotate(angle);
        var sc = fw / tex.naturalHeight;     // height fills the frame depth
        ctx.scale(sc, sc);
        var pat = ctx.createPattern(tex, 'repeat');
        if (pat) {
          ctx.fillStyle = pat;
          var maxDim = Math.max(outerW, outerH) / sc;
          ctx.fillRect(-maxDim, -2, maxDim * 3, tex.naturalHeight + 4);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      } else {
        ctx.fillStyle = frameColour;
        ctx.fill();
      }

      // Bevel sheen — soft mock-lighting across the rail. Kept gentle so light
      // mouldings (white/cream) don't read as grey/over-shadowed.
      var grad = (gradDir === 'tb')
        ? ctx.createLinearGradient(ox1, oy1, ix1, iy1)
        : ctx.createLinearGradient(ox1, oy1, ox2, oy2);
      grad.addColorStop(0, 'rgba(255,255,255,0.09)');
      grad.addColorStop(0.4, 'rgba(255,255,255,0.02)');
      grad.addColorStop(1, 'rgba(0,0,0,0.10)');
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.restore();
    }

    // Top, Bottom, Left, Right.
    drawRail(x0, y0, x0 + outerW, y0, innerX, innerY, innerX + innerW, innerY, 'tb', x0, y0, 0);
    drawRail(x0 + outerW, y0 + outerH, x0, y0 + outerH, innerX + innerW, innerY + innerH, innerX, innerY + innerH, 'tb', x0 + outerW, y0 + outerH, Math.PI);
    drawRail(x0, y0 + outerH, x0, y0, innerX, innerY + innerH, innerX, innerY, 'lr', x0, y0 + outerH, -Math.PI / 2);
    drawRail(x0 + outerW, y0, x0 + outerW, y0 + outerH, innerX + innerW, innerY, innerX + innerW, innerY + innerH, 'lr', x0 + outerW, y0, Math.PI / 2);

    // Rabbet edge
    ctx.strokeStyle = 'rgba(0,0,0,0.22)';
    ctx.lineWidth = 1;
    ctx.strokeRect(innerX + 0.5, innerY + 0.5, innerW - 1, innerH - 1);

    // ----- Mount + photo area -----
    // If mount selected, fill the whole inner area with mount colour, then
    // cut/paint a smaller aperture rect in the middle for the photo.
    var ap = state.mount ? aperturesFor(v.title) : null;
    var apData = ap && state.mount ? ap[state.mount.aperture] : null;

    var photoX = innerX, photoY = innerY, photoW = innerW, photoH = innerH;

    if (apData) {
      // Solid (white/black) or patterned (Premium) board fill. The aperture
      // window is painted over the centre below, so the pattern reads on the
      // visible mount border.
      drawMountFill(ctx, innerX, innerY, innerW, innerH, printDimsMm(v).w);

      // mm -> pixel scale uses overall renderer scale. Aperture dims are
      // stored portrait in the map; swap to match the displayed orientation
      // so the opening follows the print (landscape print => landscape window).
      var apW = apData.w, apH = apData.h;
      if (apW !== apH && state.orientation === 'landscape') { var apT = apW; apW = apH; apH = apT; }
      var apPxW = apW * scale;
      var apPxH = apH * scale;
      photoX = innerX + (innerW - apPxW) / 2;
      photoY = innerY + (innerH - apPxH) / 2;
      photoW = apPxW; photoH = apPxH;

      // Inner bevel shadow inside the aperture
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(photoX, photoY, photoW, photoH);
      ctx.strokeStyle = 'rgba(0,0,0,0.32)';
      ctx.lineWidth = 1;
      ctx.strokeRect(photoX + 0.5, photoY + 0.5, photoW - 1, photoH - 1);
      ctx.restore();

      // Accent keyline: a thin printed ring offset out from the opening (the
      // Premium 'accent' pattern prints on the mount face, no physical under-mount).
      if (isPremiumMount()) {
        var apat = patternByKey(state.mount.pattern);
        if (apat.type === 'accent') {
          // N rules growing outward (French-line keylines); outer rules drop
          // first on a narrow border, exactly as the core's print path does.
          var alw = Math.max(1, (apat.lineMm || 1.4) * scale);
          var arings = [];
          for (var ari = 0; ari < (apat.lines || 1); ari++) {
            var agap = Math.max(3, (apat.gapMm || 10) * scale + ari * (apat.spacingMm || 0) * scale);
            var alx = photoX - agap, aly = photoY - agap;
            if (!(alx > innerX + 2 && aly > innerY + 2)) break;
            arings.push({ x: alx, y: aly, w: photoW + agap * 2, h: photoH + agap * 2 });
          }
          if (arings.length) {
            ctx.save();
            if (apat.washAlpha && arings.length >= 2) {
              ctx.globalAlpha = apat.washAlpha; ctx.fillStyle = state.stripeColour;
              ctx.beginPath();
              ctx.rect(arings[1].x, arings[1].y, arings[1].w, arings[1].h);
              ctx.rect(arings[0].x, arings[0].y, arings[0].w, arings[0].h);
              ctx.fill('evenodd');
              ctx.globalAlpha = 1;
            }
            ctx.strokeStyle = state.stripeColour;
            ctx.lineWidth = alw;
            arings.forEach(function (r) { ctx.strokeRect(r.x, r.y, r.w, r.h); });
            ctx.restore();
          }
        }
      }
    } else if (presetMount_mm > 0) {
      // Preset white mount: fill the inner area, inset the print by the mount
      // border, and add a soft inner bevel so the opening reads as a real mat.
      ctx.fillStyle = '#fbfaf7';
      ctx.fillRect(innerX, innerY, innerW, innerH);
      var mPx = presetMount_mm * scale;
      photoX = innerX + mPx; photoY = innerY + mPx;
      photoW = Math.max(1, innerW - mPx * 2); photoH = Math.max(1, innerH - mPx * 2);
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.22)';
      ctx.lineWidth = 1;
      ctx.strokeRect(photoX + 0.5, photoY + 0.5, photoW - 1, photoH - 1);
      ctx.restore();
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(innerX, innerY, innerW, innerH);
    }

    // ----- Framing-style (Gallery spacer / Float-mount) inset -----
    // Applied AFTER mount geometry so the spacer band sits inside the mount
    // window when present, or directly inside the rabbet when there's no
    // mount. Standard framing leaves photo dims unchanged.
    // Gallery (spacer) framing is invisible from the front — the spacer slots
    // into the rabbet between glazing and print to add depth only. So gallery
    // renders exactly like standard (no visible band); no geometry change here.
    if (state.framingStyle === 'float') {
      // Float-mount: the frame already grew by surr_mm (totalW_mm), so the
      // inner area = print + the surround ring. The mount board behind the
      // floating print is white; the print sits proud and casts a soft shadow
      // onto it to read as depth. The surround ring (innerW - print) is the gap.
      var surrPx = surr_mm * scale;
      ctx.fillStyle = '#fbfaf7';
      ctx.fillRect(innerX, innerY, innerW, innerH);
      photoX = innerX + surrPx;
      photoY = innerY + surrPx;
      photoW = innerW - surrPx * 2;
      photoH = innerH - surrPx * 2;
      {
        // 1px paper edge around the print (paper-edge reveal). A soft, slightly
        // offset drop shadow under the print sells the float depth on white.
        var edge = 1;
        ctx.save();
        ctx.shadowColor = 'rgba(40,30,18,0.38)';
        ctx.shadowBlur = Math.max(8, Math.min(22, surrPx * 0.9 + 8));
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = Math.max(3, Math.min(9, surrPx * 0.4 + 3));
        ctx.fillStyle = '#ffffff';
        if (state.edgeType === 'deckled') {
          // Pseudo-random jittered polygon — stable across redraws for a given
          // size via deterministic sin-based pseudo-rand.
          var paperAmp = Math.max(2, Math.min(7, Math.round(Math.min(photoW, photoH) * 0.05)));
          drawDecklePath(ctx, photoX - edge, photoY - edge, photoW + edge * 2, photoH + edge * 2, paperAmp);
          ctx.fill();
        } else {
          ctx.fillRect(photoX - edge, photoY - edge, photoW + edge * 2, photoH + edge * 2);
        }
        ctx.restore();
      }
    }

    // ----- Artwork or placeholder (inside photo area) -----
    var ui = state.userImg;
    var floatDeckled = state.framingStyle === 'float' && state.edgeType === 'deckled';
    if (ui && ui.complete && ui.naturalWidth > 0) {
      var sx = 0, sy = 0, sw = ui.naturalWidth, sh = ui.naturalHeight;
      var srcRatio = sw / sh, dstRatio = photoW / photoH;
      if (srcRatio > dstRatio) { var nsw = sh * dstRatio; sx = (sw - nsw) / 2; sw = nsw; }
      else { var nsh = sw / dstRatio; sy = (sh - nsh) / 2; sh = nsh; }
      if (floatDeckled) {
        // Clip the artwork to the deckle polygon so its edges follow the
        // torn paper outline drawn just above.
        ctx.save();
        var amp = Math.max(2, Math.min(7, Math.round(Math.min(photoW, photoH) * 0.05)));
        drawDecklePath(ctx, photoX - 1, photoY - 1, photoW + 2, photoH + 2, amp);
        ctx.clip();
        ctx.drawImage(ui, sx, sy, sw, sh, photoX, photoY, photoW, photoH);
        ctx.restore();
      } else {
        ctx.drawImage(ui, sx, sy, sw, sh, photoX, photoY, photoW, photoH);
      }
    } else {
      var frameOnlyOn = frameOnlyMode();
      var hovering = state.hoverPlaceholder && !frameOnlyOn;
      ctx.fillStyle = hovering ? '#eceae6' : '#f3f2ef';
      ctx.fillRect(photoX, photoY, photoW, photoH);

      var cx = photoX + photoW / 2, cy = photoY + photoH / 2;
      var unit = Math.min(photoW, photoH);
      ctx.save();
      ctx.strokeStyle = hovering ? '#8a5d28' : '#bdb6a8';
      ctx.fillStyle = hovering ? '#8a5d28' : '#a99e8c';

      var art = frameOnlyOn ? previewArtFor(photoW / photoH) : null;
      if (art) {
        // Hub sample art (e.g. a finished puzzle), cover-cropped like a user image,
        // with a small tag so nobody reads it as included with the frame.
        var asx = 0, asy = 0, asw = art.naturalWidth, ash = art.naturalHeight;
        if (asw / ash > photoW / photoH) { var anw = ash * (photoW / photoH); asx = (asw - anw) / 2; asw = anw; }
        else { var anh = asw / (photoW / photoH); asy = (ash - anh) / 2; ash = anh; }
        if (floatDeckled) {
          drawDecklePath(ctx, photoX - 1, photoY - 1, photoW + 2, photoH + 2, Math.max(2, Math.min(7, Math.round(unit * 0.05))));
          ctx.clip();
        }
        ctx.drawImage(art, asx, asy, asw, ash, photoX, photoY, photoW, photoH);
        var tagFs = Math.max(10, Math.min(14, unit * 0.045));
        ctx.font = '600 ' + tagFs + 'px ' + UI_FONT;
        var tagTxt = 'Sample image', tagPad = tagFs * 0.6;
        var tagW = ctx.measureText(tagTxt).width + tagPad * 2, tagH = tagFs * 1.8;
        var tagX = photoX + tagFs * 0.6, tagY = photoY + photoH - tagH - tagFs * 0.6;
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.fillRect(tagX, tagY, tagW, tagH);
        ctx.fillStyle = '#4a4339';
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText(tagTxt, tagX + tagPad, tagY + tagH / 2);
      } else if (frameOnlyOn) {
        ctx.font = '600 ' + Math.max(14, unit * 0.07) + 'px ' + UI_FONT;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('Frame only', cx, cy);
      } else {
        var s = unit * 0.10, ay = cy - unit * 0.06;
        ctx.lineWidth = Math.max(2, unit * 0.016);
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(cx, ay + s); ctx.lineTo(cx, ay - s);
        ctx.moveTo(cx - s * 0.6, ay - s * 0.4); ctx.lineTo(cx, ay - s); ctx.lineTo(cx + s * 0.6, ay - s * 0.4);
        ctx.moveTo(cx - s, ay + s); ctx.lineTo(cx - s, ay + s * 1.5); ctx.lineTo(cx + s, ay + s * 1.5); ctx.lineTo(cx + s, ay + s);
        ctx.stroke();
        ctx.font = '600 ' + Math.max(12, unit * 0.06) + 'px ' + UI_FONT;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('Click to add photo', cx, cy + unit * 0.2);
      }
      ctx.restore();
    }

    // Printed personalisation in the bottom mount border (prints on the mount,
    // not the photo). Skipped on float-mount (no flat mount border there). The
    // glass sheen below sits over it, reading as glazing over the printed mount.
    if (apData && state.framingStyle !== 'float' && persActive()) {
      var pbTop = photoY + photoH;
      var pbEdge = PERS_EDGE_MM * scale, pbGap = persGapMm() * scale;
      var pbBandTop = pbTop + pbGap, pbBandBot = (innerY + innerH) - pbEdge;
      var pbRect = persRect(innerX, innerW, pbBandTop, pbBandBot, scale);
      if (pbRect.h > 8 && pbRect.w > 8) {
        drawPersonalisation(ctx, pbRect, persInk(), persCapMm() * scale, PERS_BOXPAD_MM * scale);
        var pbBpad = PERS_BOXPAD_MM * scale;
        syncPersSizeSteps(pbRect.w - pbBpad * 2, pbRect.h - pbBpad * 2, scale);
      }
    }

    // ----- Fillet recess (depth view only) -----
    // Gallery and float frames hold the glazing off the artwork on a white fillet
    // (custom.fillet_depth_mm: F2WH 12mm on 20x32, F1WH 6mm on 13x22). Face-on it
    // hides under the rebate lip, so only the tilted depth view shows it: looking
    // in from the left and above, the fillet's inner RIGHT and BOTTOM walls face
    // the viewer. Band widths follow the CSS tilt (rotateY 18deg, rotateX 7deg).
    if (depthMode && (state.framingStyle === 'gallery' || state.framingStyle === 'float') && cfg.filletDepthMm > 0) {
      var fdPx = cfg.filletDepthMm * scale;
      // True geometry is only ~4mm of wall at 18deg, a few px on a large frame, so
      // floor it (as applyDepthVar floors the outer sides at 8px) keeping the ratio.
      var tR = Math.tan(18 * Math.PI / 180), tB = Math.tan(7 * Math.PI / 180);
      var wR = Math.max(6, fdPx * tR), wB = wR * tB / tR;
      var fR = innerX + innerW, fB = innerY + innerH;
      ctx.save();
      ctx.beginPath(); ctx.rect(innerX, innerY, innerW, innerH); ctx.clip();
      // The lip shades the artwork's top and left edges, where it sits deepest.
      var shL = ctx.createLinearGradient(innerX, 0, innerX + wR * 1.4 + 2, 0);
      shL.addColorStop(0, 'rgba(0,0,0,0.22)'); shL.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = shL; ctx.fillRect(innerX, innerY, wR * 1.4 + 2, innerH);
      var shT = ctx.createLinearGradient(0, innerY, 0, innerY + wB * 2.5 + 2);
      shT.addColorStop(0, 'rgba(0,0,0,0.18)'); shT.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = shT; ctx.fillRect(innerX, innerY, innerW, wB * 2.5 + 2);
      // Right wall (lit) and bottom wall (a touch darker), white fillet.
      ctx.fillStyle = '#f1efea';
      ctx.beginPath(); ctx.moveTo(fR, innerY); ctx.lineTo(fR, fB); ctx.lineTo(fR - wR, fB - wB); ctx.lineTo(fR - wR, innerY); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#e2dfd8';
      ctx.beginPath(); ctx.moveTo(innerX, fB); ctx.lineTo(fR, fB); ctx.lineTo(fR - wR, fB - wB); ctx.lineTo(innerX, fB - wB); ctx.closePath(); ctx.fill();
      // Hairline where the fillet meets the artwork.
      ctx.strokeStyle = 'rgba(0,0,0,0.18)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(fR - wR, innerY); ctx.lineTo(fR - wR, fB - wB); ctx.lineTo(innerX, fB - wB); ctx.stroke();
      ctx.restore();
    }

    // Glass sheen over the whole inner area.
    var sheen = ctx.createLinearGradient(innerX, innerY, innerX + innerW * 0.6, innerY + innerH * 0.5);
    sheen.addColorStop(0, 'rgba(255,255,255,0.12)');
    sheen.addColorStop(0.3, 'rgba(255,255,255,0.03)');
    sheen.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(innerX, innerY, innerW, innerH);

    // ----- Indicator A: float frame-growth dimension annotation -----
    // After a float change we briefly draw outer dimension ticks + the mm size
    // so the customer sees the frame is now larger than their print. Fades over
    // the last ~400ms of the 1.8s window. Self-schedules repaints while live.
    if (state.framingStyle === 'float') {
      var remain = state.dimAnnoUntil - Date.now();
      if (remain > 0) {
        var alpha = Math.min(1, remain / 400);
        drawDimAnno(ctx, x0, y0, outerW, outerH, totalW_mm, totalH_mm, alpha);
        if (!dimRaf) dimRaf = requestAnimationFrame(function () { dimRaf = null; renderPreview(); });
      }
    }
  }

  // Faint dimension ticks along the bottom + right of the frame, with the outer
  // size in mm — drawn during the post-change annotation window (Indicator A).
  var dimRaf = null;
  function drawDimAnno(ctx, x0, y0, outerW, outerH, totalW_mm, totalH_mm, alpha) {
    var off = Math.max(10, outerW * 0.04);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = '#8a5d28';
    ctx.fillStyle = '#5b4324';
    ctx.lineWidth = 1.25;
    // Width line below the frame.
    var wy = y0 + outerH + off;
    ctx.beginPath();
    ctx.moveTo(x0, wy); ctx.lineTo(x0 + outerW, wy);
    ctx.moveTo(x0, wy - 4); ctx.lineTo(x0, wy + 4);
    ctx.moveTo(x0 + outerW, wy - 4); ctx.lineTo(x0 + outerW, wy + 4);
    // Height line right of the frame.
    var hx = x0 + outerW + off;
    ctx.moveTo(hx, y0); ctx.lineTo(hx, y0 + outerH);
    ctx.moveTo(hx - 4, y0); ctx.lineTo(hx + 4, y0);
    ctx.moveTo(hx - 4, y0 + outerH); ctx.lineTo(hx + 4, y0 + outerH);
    ctx.stroke();
    ctx.font = '700 12px ' + UI_FONT;
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(fmtDimDisp(totalW_mm), x0 + outerW / 2, wy - 6);
    ctx.save();
    ctx.translate(hx + 6, y0 + outerH / 2);
    ctx.rotate(Math.PI / 2);
    ctx.textBaseline = 'bottom';
    ctx.fillText(fmtDimDisp(totalH_mm), 0, 0);
    ctx.restore();
    ctx.restore();
  }

  // Redraw on window resize (canvas backing depends on rect.width). Recompute
  // the contained depth-box size first so it tracks the new stage size.
  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(function () {
      if (state.depthView) sizeDepthBox();
      renderPreview();
    });
  });

  // Refit whenever the canvas box itself changes size — notably when the
  // 'View depth' toggle swaps the figure aspect ratio (print <-> frame-outer),
  // so the frame re-fits edge-to-edge once the new layout has settled.
  if (window.ResizeObserver && elCanvas) {
    var roRaf = null;
    var ro = new ResizeObserver(function () {
      if (roRaf) cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(renderPreview);
    });
    ro.observe(elCanvas);
  }

  /* ---------- Cropper ---------- */
  function openCropper(file) {
    var UN = window.PFCUploadNormalise;
    if (UN && UN.needsConversion(file)) {
      // HEIC / PDF → JPEG first; the converted file re-enters as a plain JPEG.
      uploadNotice(UN.busyText(file), 'busy');
      UN.normalise(file).then(function (r) { uploadNotice(r.note || '', 'info'); openCropper(r.file); },
        function (err) { uploadNotice(err.message, 'error'); });
      return;
    }
    // Replace any previous original-image objectURL.
    if (photoSrc && !photoSrc.start(file)) {
      uploadNotice('That image is over ' + Math.round(window.PFCPhotoSource.MAX_BYTES / 1048576) + 'MB — please choose a smaller file.', 'error');
      return;
    }
    flashStatus('');   // a previous upload error no longer applies
    // Provisional until Confirm: keep what was confirmed before so Cancel can put
    // it back (see cancelCrop). A second pick before confirming keeps the first
    // stash — Cancel always returns to the last CONFIRMED photo. The old object
    // URL is revoked on commit, not here, so Cancel can still reopen it.
    if (!state.pendingPrev) {
      state.pendingPrev = { originalUrl: state.originalUrl, cropData: state.cropData, srcImg: state.srcImg };
    } else if (state.originalUrl) {
      try { URL.revokeObjectURL(state.originalUrl); } catch (_) {}
    }
    var url = URL.createObjectURL(file);
    state.originalUrl = url;
    // New image -> previous crop data is for a different source, clear it.
    state.cropData = null;
    state.srcImg = null;
    openCropperWithUrl(url);
  }

  // The crop on screen now belongs to the photo being uploaded.
  function commitPhoto() {
    var p = state.pendingPrev;
    state.pendingPrev = null;
    if (p && p.originalUrl && p.originalUrl !== state.originalUrl) {
      try { URL.revokeObjectURL(p.originalUrl); } catch (_) {}
    }
    state.photo = true;
    state.fullDataUrl = null;
    if (photoSrc) { photoSrc.commit(); state.cropFor = photoSrc.state().url; }
  }

  function cancelCrop() {
    var p = state.pendingPrev;
    if (p) {
      state.pendingPrev = null;
      if (state.originalUrl && state.originalUrl !== p.originalUrl) {
        try { URL.revokeObjectURL(state.originalUrl); } catch (_) {}
      }
      state.originalUrl = p.originalUrl; state.cropData = p.cropData; state.srcImg = p.srcImg;
      if (photoSrc) photoSrc.revert();
    }
    closeCropper();
  }

  // Re-open the cropper using the previously stashed original image. Used by
  // "Recrop" button + canvas click when a photo is already present. Lets the
  // user adjust the crop without re-uploading and without quality loss.
  function reopenCropper() {
    if (!state.originalUrl) return;
    openCropperWithUrl(state.originalUrl);
  }

  function openCropperWithUrl(url) {
    // Hold a decoded copy of the ORIGINAL. Cropper owns elCropImg and tears it
    // down on close, and the object URL dies with the tab — without this there is
    // nothing left to re-cut from when the print ratio changes later.
    if (!state.srcImg || state.srcImg.src !== url) {
      var srcProbe = new Image();
      // Only if this is still the photo in play — a cancelled pick must not land.
      srcProbe.onload = function () { if (state.originalUrl === url) { state.srcImg = srcProbe; state.cropStale = false; } };
      srcProbe.src = url;
    }
    // A file the browser can't decode never fires load: without this the cropper
    // sat open and blank. Close it, put the previous photo back and say why. The
    // stale load listener of a failed open disarms itself on the next load.
    var failed = false;
    elCropImg.onerror = function () {
      elCropImg.onerror = null;
      failed = true;
      if (state.originalUrl !== url) return;
      cancelCrop();
      uploadNotice('We couldn’t open that file. Please use a JPG, PNG, HEIC or PDF.', 'error');
    };
    elCropImg.src = url;
    elCropModal.dataset.open = 'true';
    elCropImg.addEventListener('load', function once() {
      elCropImg.removeEventListener('load', once);
      if (failed) return;
      elCropImg.onerror = null;
      if (state.cropper) try { state.cropper.destroy(); } catch (_) {}
      if (typeof Cropper === 'undefined') {
        // Cropper not loaded — bail with plain upload (no crop)
        state.previewDataUrl = url;
        commitPhoto();
        applyPreview();
        closeCropper();
        return;
      }
      var cropOpts = {
        aspectRatio: currentAspectRatio(),
        viewMode: 1,
        autoCropArea: 1,
        responsive: true,
        background: false,
        zoomable: true,
        movable: true,
      };
      // Restore the previous crop region on reopen so users see the same
      // selection they confirmed last time, not an auto-centred default.
      if (state.cropData) {
        var saved = state.cropData;
        cropOpts.ready = function () {
          try { state.cropper.setData(saved); } catch (_) {}
        };
      }
      state.cropper = new Cropper(elCropImg, cropOpts);
    });
  }

  function confirmCrop() {
    if (!state.cropper) {
      closeCropper();
      return;
    }
    // Stash the crop region (in source-image coordinates) so a later Recrop
    // can restore the same selection instead of resetting to the auto-centred
    // box. Aspect ratio may change on size/mount swap -- restore is best-effort.
    try { state.cropData = state.cropper.getData(true); } catch (_) { state.cropData = null; }
    // fillColor flattens any transparency onto white BEFORE the JPEG encode —
    // JPEG has no alpha, so a transparent PNG would otherwise turn black.
    // Preview canvas only — 800px longest edge. The print is cut server-side from
    // the uploaded original with cropData, so there is no full-res encode here.
    var previewCanvas = state.cropper.getCroppedCanvas({
      maxWidth: 800,
      maxHeight: 800,
      fillColor: '#ffffff',
      imageSmoothingQuality: 'medium',
    });
    state.previewDataUrl = previewCanvas.toDataURL('image/jpeg', 0.82);
    state.croppedAspect = currentAspectRatio();
    state.cropStale = false;
    commitPhoto();
    applyPreview();
    closeCropper();
  }

  /*
   * Reshape the crop when the PRINT RATIO changes under a crop that is already
   * confirmed — orientation flip, size swap, mount/opening change.
   *
   * Without this the order's `Orientation` (computed live at add-to-cart) and the
   * crop (frozen at Confirm) disagree, and renderPreview hides it by
   * cover-cropping the stale JPEG into the new window. Same fix as
   * pfc-print-app.js / pfc-mount.js; see order PFC-UK-1338458. This mirrors what
   * nameArtRegen already does for name-art in renderPreview.
   */
  function cropToDataUrl(img, box, maxPx, q) {
    var sc = Math.min(1, maxPx / Math.max(box.width, box.height));
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(box.width * sc));
    c.height = Math.max(1, Math.round(box.height * sc));
    var ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingQuality = 'high';
    // JPEG has no alpha — flatten onto white exactly as confirmCrop's fillColor does.
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, box.x, box.y, box.width, box.height, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', q);
  }
  // Re-cut off the back of a short debounce: a typed size or a dragged opening
  // fires renderPreview on every keystroke, and a canvas encode per keystroke
  // would stutter. The on-canvas preview is already correct meanwhile
  // (it cover-crops), and add-to-cart re-checks, so nothing escapes on the gap.
  var recropTimer = null;
  function scheduleRecrop() {
    if (recropTimer) clearTimeout(recropTimer);
    recropTimer = setTimeout(function () {
      recropTimer = null;
      if (!cropAspectDrifted()) return;
      if (recropToAspect()) applyPreview();
      else state.cropStale = true;   // original gone (reload) — add-to-cart will ask
    }, 150);
  }

  function recropToAspect() {
    var img = state.srcImg, d = state.cropData, ar = currentAspectRatio();
    if (!img || !img.naturalWidth || !d || !d.width || !d.height || !ar) return false;
    var iw = img.naturalWidth, ih = img.naturalHeight;
    // Keep the crop's height and reshape the width to the new ratio, shrinking to
    // fit when that runs past an edge.
    var nh = Math.min(d.height, ih), nw = nh * ar;
    if (nw > iw) { nw = iw; nh = nw / ar; }
    if (nh > ih) { nh = ih; nw = nh * ar; }
    var cx = d.x + d.width / 2, cy = d.y + d.height / 2;
    var box = {
      x: Math.max(0, Math.min(iw - nw, cx - nw / 2)),
      y: Math.max(0, Math.min(ih - nh, cy - nh / 2)),
      width: nw, height: nh,
    };
    var prev = cropToDataUrl(img, box, 800, 0.82);
    if (!prev) return false;
    state.cropData = box;
    state.previewDataUrl = prev;
    state.croppedAspect = ar;
    state.cropStale = false;
    return true;
  }
  // True when the confirmed crop no longer matches the print ratio.
  function cropAspectDrifted() {
    var ar = currentAspectRatio();
    return !!(state.photo && state.previewDataUrl && state.croppedAspect && ar
      && Math.abs(state.croppedAspect - ar) > 0.001);
  }

  function closeCropper() {
    elCropModal.dataset.open = 'false';
    if (state.cropper) {
      try { state.cropper.destroy(); } catch (_) {}
      state.cropper = null;
    }
    elCropImg.src = '';
  }

  function applyPreview() {
    elUpload.dataset.hasImage = 'true';
    setUploadOpen(true); // reveal so Recrop / Replace are reachable
    if (elPreview) elPreview.dataset.hasImage = 'true';
    elUploadTitle.textContent = 'Tap the preview or use the actions below';
    if (elAddPhoto) elAddPhoto.checked = true; // a photo was added -> 'add a photo' on
    // Load preview dataURL into an Image for canvas drawing.
    var img = new Image();
    img.onload = function () {
      state.userImg = img;
      renderPreview();
    };
    img.src = state.previewDataUrl;
    updateAtcStatus();
  }

  function currentAspectRatio() {
    if (state.mount) {
      var ap = aperturesFor(state.variant.title);
      var d = ap && ap[state.mount.aperture];
      // Aperture stored portrait; orient to the displayed orientation so the
      // crop ratio matches the window (matches renderPreview swap).
      if (d) return (d.w !== d.h && state.orientation === 'landscape') ? d.h / d.w : d.w / d.h;
    }
    var pd = effectiveDim(state.variant);
    return pd ? (pd.w / pd.h) : 1;
  }

  /* ---------- ATC ---------- */
  // Roll-fed print limit: when we print the customer's photo it must fit the
  // roll — shorter side ≤ 610mm (24"), longer ≤ 1300mm. Frame-only = no print,
  // so unconstrained. (All catalogue sizes fit today; this guards future ones.)
  var PRINT_MAX_SHORT_MM = 610, PRINT_MAX_LONG_MM = 1300;
  function printRollError() {
    if (frameOnlyMode() && frameOnlyAllowed(state.variant)) return null;
    var d = printDimsMm(state.variant); if (!d.w || !d.h) return null;
    var lo = Math.min(d.w, d.h), hi = Math.max(d.w, d.h);
    if (lo > PRINT_MAX_SHORT_MM || hi > PRINT_MAX_LONG_MM) {
      return 'Too large to print — we print up to ' + fmtDimDisp(PRINT_MAX_SHORT_MM) + ' on the shorter side and ' + fmtDimDisp(PRINT_MAX_LONG_MM) + ' on the longer side.';
    }
    return null;
  }
  // Human-readable spec of the current design, for the quote email.
  function quoteSpecText() {
    var v = floatPriceVariant(state.floatGapMm);
    var fq = floatQuote(state.floatGapMm);
    var bits = [];
    bits.push((cfg.mouldingFamily || cfg.productTitle || 'Frame'));
    bits.push(state.variant ? state.variant.title : '');
    if (fq) bits.push('frame ' + floatOpeningText(fq.opening));
    else if (v.id !== state.variant.id) bits.push('built to ' + v.title);
    bits.push(state.mount ? ('mount: ' + (state.mount.colour || '') + ' / ' + (state.mount.aperture || '')) : 'no mount');
    if (state.glazing) {
      var spec = glazingSpec(state.glazing);
      bits.push('glazing: ' + ((spec && spec.label) || state.glazing));
    }
    if (state.framingStyle && state.framingStyle !== 'standard') {
      bits.push(state.framingStyle === 'float'
        ? ('float-mount ' + state.floatGapMm + 'mm, ' + state.edgeType + ' edge')
        : 'gallery (spacer)');
    }
    return bits.filter(Boolean).join(' · ');
  }

  function quoteEstimateText() {
    var framePrice = floatLinePriceCents(state.floatGapMm);
    if (state.framingStyle !== 'float') framePrice = Number(state.variant.price);
    var g = glazingLinePrice(), m = mountLinePrice();
    var parts = ['frame ' + formatMoney(framePrice)];
    if (m > 0) parts.push('mount ' + formatMoney(m));
    if (g > 0) parts.push('glazing ' + formatMoney(g));
    return formatMoney(framePrice + m + g) + ' (' + parts.join(' · ') + ')';
  }

  // Quote CTA: stamp the design into the contact form's hidden fields, then send
  // the customer to it. The Design link reopens this exact configuration, which
  // is how the customer's work reaches us without any cart or datastore.
  function requestQuote() {
    if (!elQuoteForm) { flashStatus('Quote form unavailable — please contact us.'); return; }
    syncDeepLink();
    var set = function (name, value) {
      var el = elQuoteForm.querySelector('[name="contact[' + name + ']"]');
      if (el) el.value = value;
    };
    set('Design link', window.location.href);
    set('Spec', quoteSpecText());
    set('Estimate', quoteEstimateText());
    elQuoteForm.hidden = false;
    elQuoteForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    var first = elQuoteForm.querySelector('input:not([type=hidden]), textarea');
    if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (_) { first.focus(); } }, 400);
  }

  function addToCart() {
    // Quote hubs never transact. Conservation glazing is collection-only (glass
    // cannot ship and Shopify cannot gate fulfilment per cart line), the estimate
    // can exceed the size ladder's ceiling, and the real price depends on the
    // artwork — so the CTA raises an enquiry instead of a cart line.
    if (cfg && cfg.quoteMode) return requestQuote();
    // Preset (vintage) mode: the moulding line is frame+mount only — the print
    // premium rides a separate Map Print line — so it's always "frame only" here.
    if (adding) return;
    if (photoSrc && photoSrc.failed()) { flashStatus('Retrying your image upload…'); photoSrc.retry(); return; }
    if (photoSrc && photoSrc.busy()) return;   // button is disabled; belt and braces
    var frameOnly = presetActive || (frameOnlyMode() && frameOnlyAllowed(state.variant));
    if (!frameOnly && !hasArtwork()) {
      flashStatus(frameOnlyAllowed(state.variant)
        ? 'Upload an image first, or tick frame-only.'
        : 'Upload an image first.');
      return;
    }
    var rErr = printRollError();
    if (rErr) { flashStatus(rErr); return; }
    if (MOUNT_REQ && !OCC && !state.mount) { flashStatus('This frame needs a mount - choose a size that offers one.'); return; }
    if (CO && state.mount) { var coErr = coError(state.variant.title); if (coErr) { flashStatus(coErr); renderCustomOpUi(false); return; } }
    if (persActive() && !OCC) { var pErr = pf().addError(); if (pErr) { flashStatus(pErr); return; } }
    if (OCC && !(state.pers.heading || '').trim()) {
      flashStatus('Add the names to print on the mount.');
      if (elPersHeading) elPersHeading.focus();
      return;
    }
    if (OCC && !persActive()) {
      flashStatus(state.mount ? 'This size leaves no room to print on the mount. Choose a larger size or smaller opening.' : 'Add a mount to print the names on.');
      return;
    }
    // Ratio moved and the original was gone, so the files on hand are cut to the
    // wrong shape. Never let that reach the cart — it prints the wrong picture.
    if (!frameOnly && !presetActive && (state.cropStale || (cropAspectDrifted() && !recropToAspect()))) {
      if (state.originalUrl) { flashStatus('Please re-crop your image for this size.'); reopenCropper(); }
      else { flashStatus('Please add your image again for this size.'); elFileInput && elFileInput.click(); }
      return;
    }

    // A photo: the order needs the original and the exact box it was cut to,
    // measured against the displayed (EXIF-applied) image, and that crop must
    // belong to THIS upload.
    var photo = null;
    if (!frameOnly && !presetActive && state.photo) {
      if (!photoSrc || !photoSrc.ready() || !state.srcImg || !state.srcImg.naturalWidth
          || state.cropFor !== photoSrc.state().url) {
        flashStatus('Please add your image again.'); elFileInput && elFileInput.click(); return;
      }
      photo = {
        url: photoSrc.state().url,
        crop: window.PFCPhotoSource.cropSpec(state.cropData, state.srcImg.naturalWidth, state.srcImg.naturalHeight),
      };
    }

    // Mint this add's design id BEFORE any upload key is built. Frame-only and
    // preset lines carry no uploaded artwork, so they keep the session id and
    // merge as before.
    state.designId = (frameOnly || presetActive) ? null : cryptoRandom();

    adding = true;
    elAtc.disabled = true;
    flashStatus('Adding to cart…');

    Promise.resolve()
      .then(function () {
        // Preset (vintage): upload the FRAMED canvas so the cart thumbnail shows
        // the framed + mounted map, not the bare print.
        if (presetActive) return uploadPresetPreview();
        if (frameOnly) return { fullUrl: null, previewUrl: null };
        if (photo) return uploadPhotoPreview(photo);
        return uploadBothImages();
      })
      .then(function (uploaded) {
        uploaded = uploaded || {};
        // Printed text/logo: render the artwork PNG + upload alongside the photo.
        if (persActive()) {
          var persPng = renderPersExport();
          if (persPng) return uploadPersImage(persPng).then(function (u) { uploaded.pers = u; return uploaded; });
        }
        return uploaded;
      })
      .then(function (uploaded) {
        return postCart(uploaded);
      })
      .then(function (res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(function (data) {
        injectCartSections(data);
        flashStatus('Added to cart.');
        openCartDrawer();
      })
      .catch(function (err) {
        console.error('PFC moulding ATC error', err);
        flashStatus('Could not add to cart — please try again.');
      })
      .finally(function () {
        adding = false;
        syncAtcGate();
      });
  }

  // Preset: the bundle EXPANDS into Frame + Mount + Print components, so the host
  // line's own image is consumed — the visible bundle thumbnail is the CDN map
  // (_addon_bundle_pro_image, must be Shopify-CDN; an S3 render would break the
  // expand). So no canvas upload here — just hand back the CDN map + print master.
  function uploadPresetPreview() {
    var pc = presetCfg() || {};
    return Promise.resolve({ previewUrl: pc.image || null, fullUrl: pc.printUrl || null });
  }

  // Photo: only the small preview goes up here — the original is already
  // uploaded (photoSrc) and the print is cut from it using `photo.crop`.
  function uploadPhotoPreview(photo) {
    var previewKey = uploadKey('preview.jpg');
    return uploadOne(state.previewDataUrl, previewKey)
      .then(function (r) { if (r && r.ok === false) throw new Error('preview upload ' + r.status); })
      .then(function () { return { fullUrl: null, previewUrl: publicS3Url(previewKey), photo: photo }; });
  }

  // Add-to-cart is unusable while the original is still uploading: an order must
  // never reference an original that is not in S3. Failed → the button retries.
  function syncAtcGate() {
    if (!elAtc) return;
    var ph = photoSrc ? photoSrc.state() : { status: 'idle' };
    if (ph.status === 'uploading') {
      elAtc.disabled = true;
      elAtc.textContent = 'Uploading image… ' + Math.floor(ph.progress * 100) + '%';
      return;
    }
    if (ph.status === 'error') {
      elAtc.disabled = adding;
      elAtc.textContent = 'Image upload failed — tap to retry';
      return;
    }
    elAtc.disabled = adding;
    if (!adding) updatePriceAndPhotoSize();   // restores the "Add to cart · £x" label
  }

  // Generated artwork (name-art): full-res + preview, as before.
  function uploadBothImages() {
    var fullKey = uploadKey('full.jpg');
    var previewKey = uploadKey('preview.jpg');
    return Promise.all([
      uploadOne(state.fullDataUrl, fullKey),
      uploadOne(state.previewDataUrl, previewKey),
    ]).then(function () {
      return {
        fullUrl: publicS3Url(fullKey),
        previewUrl: publicS3Url(previewKey),
      };
    });
  }

  // What the customer sees of their order at checkout, in the order email and
  // on the order status page. Bundle Pro's expand drops every plain property on
  // a bundled line and keeps only the hidden `_addons_custom` blob; since app
  // v69 it re-attaches the keys listed in `_addon_bundle_pro_display` as
  // VISIBLE properties. Default-deny: only these keys, in this order, under
  // these labels. Print file, Photo original/crop, Personalisation image, Frame
  // Only, Mount external, Opening size and the session id stay hidden; the
  // full record still goes to `_addons_custom` untouched.
  // Same list in pfc-moulding.js, pfc-mount.js and pfc-print-app.js — keep in step.
  var BUNDLE_DISPLAY = [
    ['Image Preview', 'Your design'], ['Board preview', 'Board preview'],
    ['Map', 'Map'], ['Size', 'Size'], ['Orientation', 'Orientation'], ['Print size', 'Print size'], ['Finished size', 'Finished size'],
    ['Frame size', 'Frame size'], ['Frame', 'Frame'], ['Moulding', 'Moulding'], ['Finish', 'Finish'],
    ['Framing style', 'Framing style'], ['Float border', 'Float border'], ['Paper edge', 'Paper edge'], ['Frame opening', 'Frame opening'], ['Mount', 'Mount'], ['Mount colour', 'Mount colour'],
    ['Mount border', 'Mount border'], ['Mount opening', 'Mount opening'], ['Board colour', 'Board colour'],
    ['Border', 'Border'], ['Style', 'Style'], ['Pattern', 'Pattern'], ['Pattern colour', 'Pattern colour'],
    ['Glazing', 'Glazing'], ['Paper', 'Paper'], ['Fit', 'Fit'], ['Print', 'Print'], ['Supplied as', 'Supplied as'],
    ['Personalisation', 'Personalisation'], ['Printed text', 'Printed text'], ['Font', 'Font'], ['Alignment', 'Alignment']
  ];
  /** `_addon_bundle_pro_display` for one line (JSON string), or null if nothing qualifies. */
  function bundleDisplayFor(src, logoUrl) {
    var out = {}, n = 0;
    BUNDLE_DISPLAY.forEach(function (pair) {
      var v = src[pair[0]];
      if (typeof v !== 'string' && typeof v !== 'number') return;
      v = String(v).trim();
      if (v) { out[pair[1]] = v; n++; }
    });
    // The customer's ORIGINAL logo file (uploaded with the personalisation).
    if (logoUrl) { out['Your logo'] = logoUrl; n++; }
    return n ? JSON.stringify(out) : null;
  }
  /** The uploaded original logo, when the printed personalisation has one. */
  function persLogoUrl(spec) { return spec && spec.logo && spec.logo.url ? spec.logo.url : null; }

  // What Bundle Pro charges for an add-on, in presentment MAJOR units. Mirrors
  // the cart transform's addonCharge (app v69+): with no conversion at all
  // (rate exactly 1: the shop's own currency, no price-list adjustment) the
  // add-on is charged to the penny; converted markets stay whole units. Quoting
  // Math.round(price * rate) everywhere showed a £8.25 mount as £8 while the
  // cart charged £8.25. Same function in pfc-moulding.js, pfc-mount.js and
  // pfc-print-app.js, and in the studio's convertBundle — keep in step.
  function addonChargeMajor(price, rate) {
    var p = Number(price) || 0, r = Number(rate);
    if (r === 1) return Math.round(p * 100) / 100;
    return Math.round(p * r);
  }

  function uploadKey(suffix) {
    var d = new Date();
    var ym = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    return cfg.uploadPrefix + '/' + ym + '/' + designKey() + '/' + cfg.mouldingCode + '-' + state.variant.id + '-' + suffix;
  }

  function uploadOne(dataUrl, key) {
    var form = new URLSearchParams();
    form.append('bucket', cfg.uploadBucket);
    form.append('key', key);
    form.append('type', 'put');
    return fetch(cfg.gcfUploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: form,
    })
    .then(function (r) { return r.text(); })
    .then(function (signedUrl) {
      var blob = dataURLToBlob(dataUrl);
      return fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      });
    });
  }

  function publicS3Url(key) {
    return 'https://' + cfg.uploadBucket + '.s3.eu-west-2.amazonaws.com/' + key;
  }

  function postCart(uploaded) {
    // Map-native preset: build the single combined framed-map line and skip the
    // standard moulding-line path entirely.
    if (presetActive) return postCartPreset(uploaded);
    var v = state.variant;
    var frameOnly = frameOnlyMode();

    var props = {
      // The orientation the customer CHOSE — the toggle, not the variant title.
      // The title is always written one way round ("15cm x 20cm"), so reading it
      // labelled every landscape order Portrait while the print file, preview
      // and Print size were all landscape.
      'Orientation': (function () { var e = effectiveDim(v); return !e || e.w === e.h ? 'Square' : (e.w > e.h ? 'Landscape' : 'Portrait'); })(),
      'Size': v.options && v.options.Size ? v.options.Size : v.title,
      'Finish': cfg.mouldingFamily || cfg.productTitle,
      'id': designKey(),
    };
    if (naActive && naEls) {
      props['Personalised name'] = naEls.name.value;
      props['Design'] = naEls.layout.value + ' · ' + naEls.theme.value;
    }
    // Mount-only bundle. The frame IS the variant added below, so the bundle
    // metafield only carries the mount component + its price. With no mount
    // selected, no bundle reference is sent — plain variant ATC.
    var ap = aperturesFor(v.title);
    var apData = state.mount && ap ? ap[state.mount.aperture] : null;

    if (!frameOnly) {
      props['Image Preview'] = uploaded.previewUrl;
      if (uploaded.photo) {
        // The untouched original + the box to cut from it. The print-files page
        // cuts the master from these (no cropped full-res is stored).
        props['_addon_bundle_pro_image'] = uploaded.previewUrl; // Bundle Pro line-image input
        props['_photo_src'] = uploaded.photo.url;
        props['_photo_crop'] = uploaded.photo.crop;
      } else {
        props['_addon_bundle_pro_image'] = uploaded.fullUrl; // Bundle Pro line-image input
        props['_print_file'] = uploaded.fullUrl;             // full-res for fulfillment (hidden)
      }
      props['Add-ons'] = 'Custom Image';
    } else {
      props['Frame Only'] = 'true';
    }

    if (state.mount && apData) {
      // Premium tier = a patterned mount AND/OR printed text (Decision A). Printed
      // text upgrades a white/black mount to the Premium bundle (same price as a
      // pattern); if a pattern is already chosen the text is included.
      var wantPremium = state.mount.colour === 'premium' || persActive();
      var pmatch = null;
      if (wantPremium) {
        var pneedle = 'Premium Mount (' + apData.label + ' opening)';
        pmatch = (v.bundles || []).find(function (b) {
          return b.name && b.name.indexOf(pneedle) !== -1;
        });
      }
      if (pmatch) {
        props['Mount'] = 'Premium';
        props['_addon_bundle_pro_title'] = pmatch.name;
        props['_upsell_bundles_pro_selected'] = pmatch.id;
        if (state.mount.colour === 'premium') {
          props['Pattern'] = patternByKey(state.mount.pattern).label;
          // Premium = white base board + coloured pattern; this is the PATTERN
          // colour, not the board colour. Key matches the "Pattern colour" UI
          // picker (was "Mount colour" — caused order-admin confusion).
          props['Pattern colour'] = stripePresetLabel(state.stripeColour) + ' (' + state.stripeColour + ')';
        } else {
          // Plain board + printed text: record the base colour; the Premium tier
          // is paying for the printed text, not a pattern.
          props['Mount colour'] = state.mount.colour.charAt(0).toUpperCase() + state.mount.colour.slice(1);
        }
      } else {
        // Standard tier — single "Standard Mount" bundle (white component); the
        // white/black choice is a line PROPERTY, not a separate bundle.
        // MIGRATION-SAFE: prefer the collapsed "Standard Mount" bundle, fall back
        // to the legacy per-colour name. This deploys LIVE before the bundle data
        // is collapsed, so it must work for products whose bundles are still
        // White/Black AND for products already collapsed to Standard.
        var colCap = state.mount.colour.charAt(0).toUpperCase() + state.mount.colour.slice(1);
        var stdNeedle = 'Standard Mount (' + apData.label + ' opening)';
        var legacyNeedle = colCap + ' Mount (' + apData.label + ' opening)';
        var match = (v.bundles || []).find(function (b) {
          return b.name && b.name.indexOf(stdNeedle) !== -1;
        }) || (v.bundles || []).find(function (b) {
          return b.name && b.name.indexOf(legacyNeedle) !== -1;
        });
        if (match) {
          props['Mount'] = 'Standard · ' + apData.label + ' opening';
          props['Mount colour'] = colCap;
          props['_addon_bundle_pro_title'] = match.name;
          props['_upsell_bundles_pro_selected'] = match.id;
        }
      }
    }

    // Printed text / logo — rides the mount line (priced via the Premium mount
    // tier above). Captured as human-readable props + the rendered artwork URL;
    // mirrored into _addon_bundle_pro_personalisations below so it survives the
    // Bundle Pro expand. _print_file is left for the photo when present.
    if (persActive() && uploaded && uploaded.pers) {
      var persText = [state.pers.heading, state.pers.line2].filter(function (s) { return s && s.trim(); }).join(' / ');
      props['Personalisation'] = persHasLogo() ? ('Text + logo (' + state.pers.logoPos + ')') : 'Text';
      props['Printed text'] = persText || '(logo only)';
      props['Alignment'] = state.pers.align.charAt(0).toUpperCase() + state.pers.align.slice(1);
      props['Font'] = state.pers.font;
      if (OCC) {
        props['Mount template'] = occTemplate().label;
        props['Lettering'] = occLettering().label;
        props['Font'] = occLettering().names.font;
        props['Ink'] = occInk().label + ' (' + occInkHex() + ')';
      }
      props['Personalisation image'] = uploaded.pers.url;
      if (!props['_print_file']) props['_print_file'] = uploaded.pers.url;
      if (!props['_addon_bundle_pro_image']) props['_addon_bundle_pro_image'] = uploaded.pers.url;
    }

    // Framing style — only emitted when not Standard (Standard is implicit
    // default, keeps admin order lines clean for shallow mouldings). On
    // Float-mount, also capture the paper-edge sub-choice when Deckled.
    if (state.framingStyle && state.framingStyle !== 'standard') {
      props['Framing style'] = state.framingStyle === 'float' ? 'Float-mount' : 'Gallery (spacer)';
      if (state.framingStyle === 'float' && state.edgeType === 'deckled') {
        props['Paper edge'] = 'Deckled';
      }
      if (state.framingStyle === 'float') {
        props['Float border'] = state.floatGapMm + 'mm';
      }
    }

    // Production dimensions the framer cuts to. Print size = what we print; with a
    // mount the crop is locked to the opening (currentAspectRatio), so nominal
    // print == opening. Mount external = the variant size point (board fills the
    // frame rebate). No mount → print fills the size point, no mount fields.
    // The framer cuts the opening a few mm undersize so the print can't fall
    // through — physical tolerance, deliberately NOT surfaced here.
    // See docs/ORDER-MOUNT-DIMS-SPEC.md.
    var fpm = printDimsMm(v);
    if (fpm.w && fpm.h) {
      if (state.mount && apData) {
        // apData stored portrait → orient to display (mirror currentAspectRatio swap).
        var oLand = state.orientation === 'landscape' && apData.w !== apData.h;
        var opW = oLand ? Math.max(apData.w, apData.h) : Math.min(apData.w, apData.h);
        var opH = oLand ? Math.min(apData.w, apData.h) : Math.max(apData.w, apData.h);
        // A typed opening is the customer's own measurement: always exact mm (inches alongside).
        var coDim = apData.custom ? (Math.round(opW) + '×' + Math.round(opH) + 'mm'
          + (dispIn() ? ' (' + fmtInch(opW / 25.4) + '×' + fmtInch(opH / 25.4) + '")' : '')) : null;
        props['Print size'] = coDim || fmtProdDim(opW, opH, apData.label);
        props['Mount external'] = fmtProdDim(fpm.w, fpm.h, v && v.title);
        props['Mount opening'] = coDim || fmtProdDim(opW, opH, apData.label);
      } else {
        props['Print size'] = fmtProdDim(fpm.w, fpm.h, v && v.title);
      }
      // Cosmetic outer moulding face (print + 2× face width, float grows by 2× gap).
      // Not a framer measurement → hidden ('_'), retained for debug only.
      var ffw = Math.round(faceWidthMm());
      var fsurr = floatSurroundMm();
      props['_frame_size'] = Math.round(fpm.w + fsurr * 2 + ffw * 2) + '×'
        + Math.round(fpm.h + fsurr * 2 + ffw * 2) + 'mm';
    }

    // Float-mount is built to the EXACT opening (print + gap each side) and the
    // order says so in `Frame opening` — the size the framer cuts and the size the
    // print-files page reads (a bundled line loses `_frame_size`). It is sold on
    // this moulding's Custom Size variant with a custom-framing ladder bucket
    // (floatQuote), so the line is headed by the print size, never a stock frame
    // size it is not built to. No float pricing → the old size-snap line.
    var lineVar = v;
    if (state.framingStyle === 'float') {
      var fo = floatOpeningMm(state.floatGapMm);
      if (fo) props['Frame opening'] = floatOpeningText(fo);
      var fq = floatQuote(state.floatGapMm);
      if (fq) {
        lineVar = { id: cfg.floatPricing.hostId };
        props['_upsell_bundles_pro_selected'] = fq.id;
        props['_addon_bundle_pro_title'] = (cfg.productTitle || 'Frame') + ' — ' + v.title + ', float-mounted';
        props['_true_price'] = fq.raw.toFixed(2);
      } else {
        lineVar = floatPriceVariant(state.floatGapMm);
      }
    }

    // Bundle Pro's cart-transform EXPAND replaces this source line with fresh
    // component lines (frame + mount); only `_addon_bundle_pro_personalisations`
    // survives the split — every plain line property is dropped. So when a
    // bundle is attached, mirror all human-readable props into that JSON or
    // they never reach the Shopify order (admin / exports / packing slips).
    // No bundle => no expand => plain props survive untouched, skip.
    if (props['_upsell_bundles_pro_selected']) {
      // Bundle Pro's cart-transform EXPAND `image` property accepts ONLY a
      // Shopify-CDN URL (per Shopify's cart-transform docs). Our uploads are S3
      // URLs, so an `_addon_bundle_pro_image` on a bundle line makes the expand
      // fail and the whole transform is dropped — the mount bundle never
      // reprices and the line stays at the bare frame-variant base. Drop it on
      // bundle lines (it's only Bundle Pro's cart thumbnail); the upload still
      // travels via `_print_file` + `Image Preview`, both mirrored below.
      delete props['_addon_bundle_pro_image'];
      var carry = {};
      Object.keys(props).forEach(function (k) {
        if (k.charAt(0) !== '_') carry[k] = props[k];
      });
      // Full-res print file: only the personalisations JSON survives the expand
      // (it lands as the underscore `_addons_custom` blob => already admin-only),
      // so include the full-res URL there for fulfillment. Sourced from the real
      // upload, NOT from `_addon_bundle_pro_image` (that's Bundle Pro's line-image).
      if (props['_print_file']) carry['Print file'] = props['_print_file'];
      // Same for the photo: the copy above skips underscore keys.
      if (props['_photo_src']) carry['Photo original'] = props['_photo_src'];
      if (props['_photo_crop']) carry['Photo crop'] = props['_photo_crop'];
      // Vector text re-render spec for the print-file admin app (see renderPersExport).
      if (persActive() && lastPersRenderSpec) carry['_pers_render'] = lastPersRenderSpec;
      props['_addon_bundle_pro_personalisations'] = JSON.stringify(carry);
      var display = bundleDisplayFor(carry, persActive() ? persLogoUrl(lastPersRenderSpec) : null);
      if (display) props['_addon_bundle_pro_display'] = display;
    }

    var items = [{ id: lineVar.id, quantity: 1, properties: props }];
    // Preset (vintage) builds a single combined line via postCartPreset (returned
    // early above), so this standard path never runs in preset mode.
    var body = {
      items: items,
      sections: 'cart-drawer,cart-icon-bubble,cart-live-region-text,header-drawer'
    };
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/add.js'
        : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function injectCartSections(data) {
    if (!data.sections) return;
    if (data.sections['cart-drawer'] && document.querySelector('cart-drawer')) {
      try {
        var virtualEl = document.createElement('div');
        virtualEl.innerHTML = data.sections['cart-drawer'];
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.outerHTML = virtualEl.querySelector('cart-drawer').outerHTML || drawer.outerHTML;
        virtualEl.remove();
      } catch (_) {}
    }
    if (data.sections['cart-icon-bubble'] && document.getElementById('cart-icon-bubble')) {
      try {
        var virtualEl2 = document.createElement('div');
        virtualEl2.innerHTML = data.sections['cart-icon-bubble'];
        var iconBubble = document.getElementById('cart-icon-bubble');
        var newBubbleContent = virtualEl2.querySelector('.shopify-section');
        iconBubble.innerHTML = (newBubbleContent && newBubbleContent.innerHTML) || virtualEl2.innerHTML;
        virtualEl2.remove();
      } catch (_) {}
    }
  }

  function openCartDrawer() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      try { bubble.dispatchEvent(new Event('click')); } catch (_) {}
    }
  }

  /* ---------- Helpers ---------- */
  function updateAtcStatus() {
    var frameOnly = frameOnlyMode();
    if (frameOnly) {
      flashStatus('Frame only — no image required.');
    } else if (hasArtwork()) {
      flashStatus('Image ready.');
    } else {
      flashStatus('');
    }
  }

  function flashStatus(text) {
    if (elAtcStatus) elAtcStatus.textContent = text || '';
  }
  // Photo-upload messages: a toast the customer will actually see, plus the
  // status line for errors so the reason is still there after the toast goes.
  function uploadNotice(text, kind) {
    if (kind === 'error') flashStatus(text); else if (kind === 'busy') flashStatus('');
    if (window.PFCPhotoSource && window.PFCPhotoSource.notice) window.PFCPhotoSource.notice(text, kind);
  }

  function dataURLToBlob(dataUrl) {
    var parts = dataUrl.split(',');
    var mime = parts[0].match(/:(.*?);/)[1];
    var bin = atob(parts[1]);
    var len = bin.length;
    var u8 = new Uint8Array(len);
    for (var i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }

  function cryptoRandom() {
    try {
      var arr = new Uint8Array(16);
      crypto.getRandomValues(arr);
      return Array.from(arr).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
    } catch (_) {
      return String(Date.now()) + Math.random().toString(16).slice(2);
    }
  }

  // The id that identifies ONE design. Per-add while an add carries artwork;
  // otherwise the page-load sessionId, so identical frame-only adds still merge
  // into a single qty-2 line (they have no design to tell apart).
  function designKey() { return state.designId || state.sessionId; }

  function parseDim(v) {
    var t = (v && v.options && v.options.Size) || (v && v.title) || '';
    // Match "12" x 16"" or "30cm x 40cm" etc. Capture each number's unit so the
    // result is real MILLIMETRES (cm×10, inch×25.4), not the raw title number —
    // printDimsMm falls back to this when a variant carries no widthMm, so it
    // must be true mm or the frame-size readout/preview scale break (a "90cm"
    // read as 90mm drew the moulding hugely oversized on panoramic sizes).
    var m = t.match(/(\d+(?:\.\d+)?)\s*(cm|mm|"|in)?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(cm|mm|"|in)?/i);
    if (m) {
      var unit = (m[2] || m[4] || '').toLowerCase();
      var f = unit === 'cm' ? 10 : (unit === '"' || unit === 'in') ? 25.4 : 1;
      return { w: Math.round(Number(m[1]) * f), h: Math.round(Number(m[3]) * f) };
    }
    // A-paper -> canonical ratio 1:sqrt(2) ~ 5:7 (orientation only; A-sizes carry widthMm)
    if (/^A\d/.test(t.trim())) return { w: 5, h: 7 };
    return null;
  }

  function orientationOf(v) {
    var d = parseDim(v);
    if (!d) return 'Square';
    if (d.w === d.h) return 'Square';
    return d.w > d.h ? 'Landscape' : 'Portrait';
  }

  // Returns parseDim swapped to match state.orientation. Used everywhere we
  // need the *displayed* aspect (preview, canvas, crop). Variant data
  // (widthMm/heightMm/SKU) stays untouched — orientation is display-only.
  function effectiveDim(v) {
    var d = parseDim(v);
    if (!d) return null;
    if (d.w === d.h) return d;
    if (state.orientation === 'landscape') return { w: Math.max(d.w, d.h), h: Math.min(d.w, d.h) };
    return { w: Math.min(d.w, d.h), h: Math.max(d.w, d.h) };
  }

  // Real print dimensions in mm (variant widthMm/heightMm), swapped to match
  // the displayed orientation. Falls back to the parsed-ratio dims when a
  // variant carries no mm. Shared by renderPreview + updateFrameSizeUi so the
  // canvas and the frame-size readout never diverge.
  function printDimsMm(v) {
    var d = effectiveDim(v);
    var baseW = (v && v.widthMm) || (d && d.w) || 0;
    var baseH = (v && v.heightMm) || (d && d.h) || 0;
    if (baseW === baseH) return { w: baseW, h: baseH };
    var landscape = state.orientation === 'landscape';
    return {
      w: landscape ? Math.max(baseW, baseH) : Math.min(baseW, baseH),
      h: landscape ? Math.min(baseW, baseH) : Math.max(baseW, baseH),
    };
  }

  function formatMoney(cents) {
    var locale = (document.documentElement.getAttribute('lang') || 'en').toLowerCase().startsWith('en') ? 'en-GB' : undefined;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (_) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  /* ---------- Customer gallery lightbox ---------- */
  (function initGallery() {
    var gal = document.querySelector('[data-gallery]');
    if (!gal) return;
    var box = gal.querySelector('[data-gallery-lightbox]');
    var boxImg = gal.querySelector('[data-gallery-lightbox-img]');
    var closeBtn = gal.querySelector('[data-gallery-close]');
    function open(src) { boxImg.src = src; box.hidden = false; document.body.style.overflow = 'hidden'; }
    function close() { box.hidden = true; boxImg.src = ''; document.body.style.overflow = ''; }
    gal.querySelectorAll('[data-gallery-item]').forEach(function (b) {
      b.addEventListener('click', function () { open(b.dataset.full); });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (box) box.addEventListener('click', function (e) { if (e.target === box) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !box.hidden) close(); });
  })();

  /* ---------- Mobile bottom-sheet ---------- */
  // At <=920px and while the customiser intersects the viewport, the controls
  // sheet floats over the bottom of the screen with two snap states:
  //   peek -> only the handle (price + drag grabber) visible
  //   full -> sheet at ~88vh, body scrolls internally
  // Drag/tap on the handle toggles between states. Section-scoped via
  // IntersectionObserver so hero/FAQ/siblings above and below are unaffected.
  (function initSheet() {
    if (!elSheet || !elSheetHandle) return;
    var MOBILE_BP = 920;
    var DRAG_THRESHOLD = 6;       // px before pointer counts as a drag (vs tap)
    var FLICK_VELOCITY = 0.6;     // px/ms; above this snap follows direction

    var sheetState = {
      mode: 'peek',               // 'peek' | 'full'
      dragging: false,
      pointerId: null,
      startY: 0,
      lastY: 0,
      lastT: 0,
      velocity: 0,
      moved: false,
      sheetH: 0,
      peekH: 0
    };

    function measure() {
      // Sheet height + peek height (peek = handle visible only).
      sheetState.sheetH = elSheet.offsetHeight || (window.innerHeight * 0.88);
      sheetState.peekH = elSheetHandle.offsetHeight || Math.round(window.innerHeight * 0.08);
      // Expose peek height to CSS so the surrounding .pfc-moulding can reserve room.
      document.documentElement.style.setProperty('--peek-h', sheetState.peekH + 'px');
    }

    function setBodyState() {
      document.body.setAttribute('data-sheet-state', sheetState.mode);
      elSheetHandle.setAttribute('aria-expanded', sheetState.mode === 'full' ? 'true' : 'false');
    }

    function setMode(mode) {
      sheetState.mode = mode === 'full' ? 'full' : 'peek';
      // Clear inline drag transform so the CSS state class takes over.
      elSheet.style.transform = '';
      setBodyState();
      // Mode flips invalidate the condensed-handle gate (expanded sheets
      // always show normal chrome). Re-evaluate immediately to avoid a flash
      // of the wrong size when collapsing while scrolled past the preview.
      if (typeof applyScrolledPast === 'function') applyScrolledPast();
    }

    function isMobile() { return window.innerWidth <= MOBILE_BP; }

    var __dbg = (window.PFC_DEBUG_SHEET = window.PFC_DEBUG_SHEET || []);
    function dbg(tag, extra) {
      var e = { t: Date.now(), tag: tag };
      if (extra) for (var k in extra) e[k] = extra[k];
      __dbg.push(e);
      if (__dbg.length > 200) __dbg.shift();
      try { console.log('[sheet]', tag, extra || ''); } catch (_) {}
    }
    function activate() {
      if (document.body.getAttribute('data-sheet-active') === 'true') return;
      document.body.setAttribute('data-sheet-active', 'true');
      measure();
      setBodyState();
      dbg('activate', { peekH: sheetState.peekH, sheetH: sheetState.sheetH });
      maybeOnboard();
    }

    // First-time discovery cue: pulse the handle once per session so the
    // collapsed sheet announces itself without auto-opening (auto-open felt
    // intrusive for users who land on the page without intent to customise).
    var onboardKey = 'pfcSheetOnboarded';
    var pulseTimer = 0;
    function cancelOnboard() {
      document.body.removeAttribute('data-sheet-pulse');
      if (pulseTimer) { clearTimeout(pulseTimer); pulseTimer = 0; }
      try { sessionStorage.setItem(onboardKey, '1'); } catch (_) {}
    }
    function maybeOnboard() {
      try { if (sessionStorage.getItem(onboardKey) === '1') return; } catch (_) {}
      document.body.setAttribute('data-sheet-pulse', 'true');
      pulseTimer = setTimeout(function () {
        document.body.removeAttribute('data-sheet-pulse');
      }, 4200);
    }
    function deactivate() {
      if (sheetState.dragging) return;
      if (document.body.getAttribute('data-sheet-active') !== 'true') return;
      document.body.removeAttribute('data-sheet-active');
      document.body.removeAttribute('data-sheet-state');
      document.body.removeAttribute('data-sheet-dragging');
      document.body.removeAttribute('data-sheet-scrolled-past');
      elSheet.style.transform = '';
      dbg('deactivate');
    }

    // rAF-throttled scroll check. Sheet is always active on mobile (never
    // hides while the user is on this page) so users always have access to
    // the controls. The preview-intersection observer now drives a different
    // signal: data-sheet-scrolled-past flips true once the preview has left
    // the viewport, which CSS uses to render a condensed handle (smaller,
    // less screen real-estate). Only applies while mode === 'peek'; if the
    // user has the sheet open (full), scroll position is ignored.
    var sentinel = root.querySelector('[data-preview]') || root.querySelector('[data-preview-canvas]') || root;
    try { console.log('[sheet] sentinel =', sentinel.className || sentinel.tagName, sentinel); } catch (_) {}
    var previewVisible = true;
    var rafQueued = false;

    function applyScrolledPast() {
      // Condensed handle only when peek + preview off-screen. Full state
      // always uses normal chrome regardless of scroll.
      var condense = !previewVisible && sheetState.mode === 'peek';
      if (condense) document.body.setAttribute('data-sheet-scrolled-past', 'true');
      else document.body.removeAttribute('data-sheet-scrolled-past');
      // Handle height changes between normal/condensed, so --peek-h (used by
      // page padding + sheet translateY) must be recomputed.
      requestAnimationFrame(measure);
    }

    function checkInView() {
      rafQueued = false;
      document.body.setAttribute('data-sheet-debug-mobile', isMobile() ? 'y' : 'n');
      if (!isMobile()) {
        if (document.body.getAttribute('data-sheet-active') === 'true') deactivate();
        document.body.removeAttribute('data-sheet-scrolled-past');
        return;
      }
      // Always-active on mobile.
      if (document.body.getAttribute('data-sheet-active') !== 'true') activate();
      var r = sentinel.getBoundingClientRect();
      document.body.setAttribute('data-sheet-debug-rect', Math.round(r.top) + ',' + Math.round(r.bottom) + ',' + Math.round(window.innerHeight));
      var vh = document.documentElement.clientHeight || window.innerHeight;
      var visTop = Math.max(0, r.top);
      var visBot = Math.min(vh, r.bottom);
      var visible = Math.max(0, visBot - visTop);
      // Hysteresis: switch to condensed once preview almost fully off-screen,
      // switch back once a meaningful slice (>=120px) is on screen again.
      // Avoids flicker at the boundary.
      var CONDENSE_AT = 40;   // visible px <= this → condense
      var EXPAND_AT = 120;    // visible px >= this → normal
      dbg('check', {
        top: Math.round(r.top), bot: Math.round(r.bottom),
        vh: vh, vis: Math.round(visible), previewVisible: previewVisible
      });
      if (previewVisible) {
        if (visible <= CONDENSE_AT) { previewVisible = false; applyScrolledPast(); }
      } else {
        if (visible >= EXPAND_AT) { previewVisible = true; applyScrolledPast(); }
      }
    }

    function queueCheck() {
      if (rafQueued) return;
      rafQueued = true;
      requestAnimationFrame(checkInView);
    }

    // Run once on init + on scroll/resize. Drag in flight blocks deactivate
    // (see deactivate()), so we still drive scroll even while dragging --
    // user might continue scrolling once they release.
    // Defer first check until next frame so layout has settled (avoids
    // first-paint rects of 0/0 returning false negatives).
    document.body.setAttribute('data-sheet-init', isMobile() ? 'mobile' : 'desktop');
    requestAnimationFrame(function () { requestAnimationFrame(queueCheck); });
    window.addEventListener('load', queueCheck);
    window.addEventListener('scroll', queueCheck, { passive: true });
    var resizeT = 0;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(queueCheck, 120);
    });

    // Drag + tap on handle (Pointer Events for unified mouse/touch).
    elSheetHandle.addEventListener('pointerdown', function (e) {
      if (!isMobile()) return;
      cancelOnboard();
      sheetState.dragging = true;
      sheetState.moved = false;
      sheetState.pointerId = e.pointerId;
      sheetState.startY = e.clientY;
      sheetState.lastY = e.clientY;
      sheetState.lastT = performance.now();
      sheetState.velocity = 0;
      measure();
      try { elSheetHandle.setPointerCapture(e.pointerId); } catch (_) {}
      document.body.setAttribute('data-sheet-dragging', 'true');
    });

    elSheetHandle.addEventListener('pointermove', function (e) {
      if (!sheetState.dragging || e.pointerId !== sheetState.pointerId) return;
      var dy = e.clientY - sheetState.startY;
      if (Math.abs(dy) > DRAG_THRESHOLD) sheetState.moved = true;
      // Translate from the current snap origin.
      var originOffset = sheetState.mode === 'full' ? 0 : (sheetState.sheetH - sheetState.peekH);
      var clamped = Math.max(0, Math.min(sheetState.sheetH - sheetState.peekH, originOffset + dy));
      elSheet.style.transform = 'translateY(' + clamped + 'px)';
      // Velocity sample (last segment).
      var now = performance.now();
      var dt = Math.max(1, now - sheetState.lastT);
      sheetState.velocity = (e.clientY - sheetState.lastY) / dt;
      sheetState.lastY = e.clientY;
      sheetState.lastT = now;
    });

    function endDrag(e) {
      if (!sheetState.dragging) return;
      if (e && e.pointerId !== sheetState.pointerId) return;
      sheetState.dragging = false;
      document.body.removeAttribute('data-sheet-dragging');
      try { elSheetHandle.releasePointerCapture(sheetState.pointerId); } catch (_) {}

      // Tap (no real movement) -> toggle.
      if (!sheetState.moved) {
        setMode(sheetState.mode === 'full' ? 'peek' : 'full');
        return;
      }
      // Flick wins direction.
      if (Math.abs(sheetState.velocity) > FLICK_VELOCITY) {
        setMode(sheetState.velocity < 0 ? 'full' : 'peek');
        return;
      }
      // Snap to nearest based on final visible portion.
      var matrix = elSheet.style.transform.match(/translateY\((-?[\d.]+)px\)/);
      var ty = matrix ? parseFloat(matrix[1]) : 0;
      var midpoint = (sheetState.sheetH - sheetState.peekH) / 2;
      setMode(ty < midpoint ? 'full' : 'peek');
    }
    elSheetHandle.addEventListener('pointerup', endDrag);
    elSheetHandle.addEventListener('pointercancel', endDrag);
    if (elSheetBody) elSheetBody.addEventListener('scroll', cancelOnboard, { passive: true, once: true });

    // Keyboard: Enter / Space toggle.
    elSheetHandle.addEventListener('keydown', function (e) {
      if (!isMobile()) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setMode(sheetState.mode === 'full' ? 'peek' : 'full');
      }
      if (e.key === 'Escape' && sheetState.mode === 'full') {
        e.preventDefault();
        setMode('peek');
      }
    });

    // Initialise the peek-state attribute so first paint matches CSS expectations.
    setBodyState();
  })();


  /* ---------- Occasion mount templates: controls + preset (only when OCC) ---------- */
  // Keep the mount consistent with the template: a keyline template needs the
  // Premium (patterned) board with that keyline in the ink colour; leaving it
  // drops back to a plain white board (the text still prices it as Premium).
  function occReconcileMount() {
    if (!OCC || !state.mount) return;
    var key = occTemplate().keyline;
    if (key) {
      if (state.mount.colour === 'black') { state.occ.template = 'plain'; return; }   // keylines print on the white Premium board only
      if (state.mount.colour !== 'premium') { var pm = resolveMount('premium', state.mount.aperture); if (!pm) { state.occ.template = 'plain'; return; } state.mount = pm; }
      state.mount.pattern = key;
      state.stripeColour = occInkHex();
    } else if (state.mount.colour === 'premium' && patternByKey(state.mount.pattern).hidden) {
      var wm = resolveMount('white', state.mount.aperture); if (wm) state.mount = wm;
    }
  }
  // The names need a real strip of board under the opening. Keep the mount's
  // colour/pattern but move to the largest opening that leaves room to print.
  function occFitMount(m) {
    if (!m) return m;
    var ap = aperturesFor(state.variant.title); if (!ap) return m;
    var prev = state.mount, best = m;
    state.mount = m;
    if (!persFits()) {
      var keys = Object.keys(ap).sort(function (a, b) { return ap[b].w * ap[b].h - ap[a].w * ap[a].h; });
      for (var i = 0; i < keys.length; i++) {
        var c = resolveMount(m.colour, keys[i]); if (!c) continue;
        if (m.pattern) c.pattern = m.pattern;
        state.mount = c;
        if (persFits()) { best = c; break; }
      }
    }
    state.mount = prev;
    return best;
  }
  // First size (in the shown unit) where some opening leaves room for the names.
  function occFirstFittingVariant() {
    var gap = PERS_EDGE_MM + persGapMm() + PERS_MIN_BAND_MM;
    return visibleVariants().find(function (v) {
      if (!v.available) return false;
      var ap = aperturesFor(v.title), d = printDimsMm(v); if (!ap || !d || !d.h) return false;
      return Object.keys(ap).some(function (k) { var a = ap[k], h = (a.w !== a.h && state.orientation === 'landscape') ? Math.min(a.w, a.h) : Math.max(a.w, a.h); return (d.h - h) / 2 >= gap; });
    }) || null;
  }
  // Can this template print at the current size with some opening? (A keyline
  // template needs ~11mm more border than Plain.)
  function occTemplateFits(key) {
    if (!state.mount) return true;
    var prevT = state.occ.template, prevM = state.mount;
    state.occ.template = key;
    state.mount = occFitMount(prevM);
    var ok = persFits();
    state.occ.template = prevT; state.mount = prevM;
    return ok;
  }
  var elOcc = null;
  function occButton(label, pressed, onClick, styleFont) {
    var b = document.createElement('button');
    b.type = 'button'; b.textContent = label;
    b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    if (styleFont) { b.style.fontFamily = fontFamilyCss(styleFont); b.className = 'pfc-occ__letter'; }
    b.addEventListener('click', onClick);
    return b;
  }
  function occSet(k, v) {
    state.occ[k] = v;
    if (state.mount) commitMount(occFitMount(state.mount)); else { occRenderControls(); renderPreview(); }
  }
  function occRenderControls() {
    if (!elOcc) return;
    var dark = MC.persInkHex('black', mountBaseHex()) === '#ffffff';
    var tg = elOcc.querySelector('[data-occ-template]'), lg = elOcc.querySelector('[data-occ-lettering]'), ig = elOcc.querySelector('[data-occ-ink]');
    var bg = elOcc.querySelector('[data-occ-board]');
    tg.innerHTML = ''; lg.innerHTML = ''; ig.innerHTML = '';
    // Board colour. The mount row (and its colour picker) is hidden on occasion
    // pages, so the choice lives here. A keyline board ('premium') is white.
    if (bg) {
      bg.innerHTML = '';
      var onBlack = !!state.mount && state.mount.colour === 'black';
      [['white', 'White'], ['black', 'Black']].forEach(function (c) {
        bg.appendChild(occButton(c[1], (c[0] === 'black') === onBlack, function () {
          if (!state.mount || (c[0] === 'black') === onBlack) return;
          var m = resolveMount(c[0], state.mount.aperture || firstAperture());
          if (m) commitMount(occFitMount(m));
        }));
      });
    }
    MC.PERS_TEMPLATES.forEach(function (t) {
      var b = occButton(t.label, state.occ.template === t.key, function () { occSet('template', t.key); });
      if (t.keyline && state.mount && state.mount.colour === 'black') { b.disabled = true; b.title = 'Available on white and Premium mounts'; }
      else if (state.occ.template !== t.key && !occTemplateFits(t.key)) { b.disabled = true; b.title = 'Needs a larger size or a smaller opening'; }
      tg.appendChild(b);
    });
    MC.PERS_LETTERING.forEach(function (L) {
      lg.appendChild(occButton(L.label, state.occ.lettering === L.key, function () { occSet('lettering', L.key); }, L.names.font));
    });
    var shown = occInk().key;
    MC.PERS_INKS.forEach(function (i) {
      if (i.dark && !dark) return;   // white ink only on a dark board
      if (dark && MC.persInkHex(i.key, mountBaseHex()) !== i.hex) return;   // would print white anyway
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'pfc-moulding__stripe-swatch';
      b.title = i.label; b.setAttribute('aria-label', i.label);
      b.setAttribute('aria-pressed', shown === i.key ? 'true' : 'false');
      b.style.background = MC.persInkHex(i.key, mountBaseHex());
      b.addEventListener('click', function () { occSet('ink', i.key); });
      ig.appendChild(b);
    });
    var cur = elOcc.querySelector('[data-occ-ink-name]');
    if (cur) cur.textContent = occInk().label;
  }
  function occLoadFonts() {
    if (!document.fonts || !document.fonts.load) return;
    var jobs = [];
    MC.PERS_LETTERING.forEach(function (L) {
      [L.names, L.date].forEach(function (f) { try { jobs.push(document.fonts.load((f.weight || 400) + ' 32px "' + f.font + '"')); } catch (_) {} });
    });
    Promise.all(jobs).catch(function () {}).then(function () { renderPreview(); });
  }
  if (OCC && elPersWrap && elPersPanel) {
    // Copy + layout: names and date, then template / lettering / ink. The
    // free-form controls (font, alignment, size, logo) are not offered here.
    var occTitle = elPersWrap.querySelector('.pfc-extra__title'), occSub = elPersWrap.querySelector('.pfc-extra__sub');
    if (occTitle) occTitle.textContent = OCC.title || 'Names and date on the mount';
    if (occSub) occSub.textContent = OCC.subtitle || 'Printed in archival ink in the border below your photo';
    Array.prototype.forEach.call(elPersPanel.querySelectorAll('.pfc-extra__row, .pfc-extra__logo-toggle, [data-pers-logo-fields]'), function (el) { el.hidden = true; el.style.display = 'none'; });
    if (elPersHeading) { elPersHeading.placeholder = OCC.namesPlaceholder || 'Emma & James'; var lh = elPersHeading.previousElementSibling; if (lh) lh.textContent = 'Names'; }
    if (elPersLine2) { elPersLine2.placeholder = OCC.datePlaceholder || '12 June 2026'; var l2 = elPersLine2.previousElementSibling; if (l2) l2.textContent = 'Date (optional)'; }
    // Required, not optional: no mount opt-in row, no on/off switch on the names.
    root.classList.add('pfc-occ-mode');
    elOcc = document.createElement('div');
    elOcc.className = 'pfc-occ';
    var usVocab = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) === 'USD';   // "mat", as the spec line does
    elOcc.innerHTML =
      '<div class="pfc-occ__group"><label>' + (usVocab ? 'Mat' : 'Mount') + '</label><div class="pfc-extra__align pfc-occ__chips" role="group" aria-label="' + (usVocab ? 'Mat' : 'Mount') + ' colour" data-occ-board></div></div>' +
      '<div class="pfc-occ__group"><label>Template</label><div class="pfc-extra__align pfc-occ__chips" role="group" aria-label="Template" data-occ-template></div></div>' +
      '<div class="pfc-occ__group"><label>Lettering</label><div class="pfc-occ__letters" role="group" aria-label="Lettering" data-occ-lettering></div></div>' +
      '<div class="pfc-occ__group"><label>Ink <span class="pfc-occ__inkname" data-occ-ink-name></span></label><div class="pfc-occ__inks" role="group" aria-label="Ink colour" data-occ-ink></div></div>';
    var occFields = elPersPanel.querySelector('.pfc-extra__fields');
    if (occFields && occFields.nextSibling) elPersPanel.insertBefore(elOcc, occFields.nextSibling); else elPersPanel.appendChild(elOcc);
    // Preset: a mount with the text on, opened.
    state.pers.on = true;
    if (elPersToggle) elPersToggle.checked = true;
    if (elAddMount && !elAddMount.checked && !certActive()) { elAddMount.checked = true; refreshMountUi(); }
    if (state.mount) commitMount(occFitMount(state.mount));
    // Default size too small to print on at any opening: open on the first size that fits.
    if (state.mount && !persFits()) { var occV = occFirstFittingVariant(); if (occV && occV !== state.variant) selectVariant(occV); }
    if (elPersPanel) elPersPanel.hidden = !persFits();
    occRenderControls();
    occLoadFonts();
    persChanged();
    occDrawDemos();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(occDrawDemos);
  }
  // Below-fold template examples: the lower half of a 12x16 mount, drawn with
  // the same core functions as the customiser and the print file.
  function occDrawDemos() {
    var cvs = document.querySelectorAll('[data-occ-demo]');
    Array.prototype.forEach.call(cvs, function (cv) {
      var tKey = cv.getAttribute('data-occ-demo'), T = MC.persTemplateByKey(tKey), L = MC.letteringByKey(cv.getAttribute('data-occ-lettering') || 'playfair');
      var BW = 305, BH = 406, AW = 152, AH = 203, ax = (BW - AW) / 2, ay = (BH - AH) / 2;
      // Crop: the bottom 40mm of the photo down to the board edge, centred.
      var viewW = 196, cropTop = ay + AH - 40, viewH = BH - cropTop;   // wide enough for the French line sides
      var cssW = cv.clientWidth || 320, k = cssW / viewW, cssH = viewH * k, dpr = Math.min(window.devicePixelRatio || 1, 3);
      cv.width = Math.round(cssW * dpr); cv.height = Math.round(cssH * dpr); cv.style.height = cssH + 'px';
      var ctx = cv.getContext('2d'); ctx.setTransform(dpr * k, 0, 0, dpr * k, 0, 0);
      ctx.translate(-(BW - viewW) / 2, -cropTop);                           // mm space
      ctx.fillStyle = '#fafaf6'; ctx.fillRect(0, 0, BW, BH);
      ctx.fillStyle = '#fffdf7'; ctx.fillRect(ax - 1.6, ay - 1.6, AW + 3.2, AH + 3.2);   // bevel
      var g = ctx.createLinearGradient(0, ay, 0, ay + AH); g.addColorStop(0, '#c9d3d6'); g.addColorStop(0.6, '#e3dccd'); g.addColorStop(1, '#8c8a72');
      ctx.fillStyle = g; ctx.fillRect(ax, ay, AW, AH);
      // Unstyled examples take the page's own opening ink (gold-tone on the golden anniversary).
      var ink = MC.persInkHex(cv.getAttribute('data-occ-ink') || (OCC && OCC.ink) || 'black', '#fafaf6'), clear = 0;
      if (T.keyline) {
        var kp = MC.patternByKey(T.keyline);
        ctx.save(); ctx.beginPath(); ctx.rect(0, 0, BW, BH); ctx.clip();
        MC.paintMountFill(ctx, 0, 0, BW, BH, BW, { premium: true, pattern: kp, stripeColour: ink, plainHex: '#fafaf6', printMode: true, apertureMm: { w: AW, h: AH } });
        ctx.restore();
        clear = (kp.gapMm || 0) + ((kp.lines || 1) - 1) * (kp.spacingMm || 0) + (kp.lineMm || 0);
      }
      var top = ay + AH + 3 + clear, pad = 3;
      var spec = { v: 2, style: { key: L.key, names: L.names, date: L.date }, template: tKey, lines: [(OCC && OCC.namesPlaceholder) || 'Emma & James', (OCC && OCC.datePlaceholder) || '12 June 2026'], capMm: Math.max(L.names.minMm || 0, 5), inkHex: ink };
      MC.drawStyledBox(ctx, spec, { x: 12 + pad, y: top + pad, w: BW - 24 - pad * 2, h: BH - 12 - top - pad * 2 }, spec.capMm);
    });
  }

  // One-shot photo handoff from the frame finish gallery (/pages/picture-frames):
  // a photo the visitor uploaded there, stashed in sessionStorage (the objectURL
  // can't cross the navigation). Pre-load it into the cropper so they land ready
  // to position.
  (function () {
    var data;
    try { data = sessionStorage.getItem('pfc-frame-handoff'); } catch (_) { return; }
    if (!data) return;
    try { sessionStorage.removeItem('pfc-frame-handoff'); } catch (_) {}
    function openIt() {
      if (state.originalUrl) { try { URL.revokeObjectURL(state.originalUrl); } catch (_) {} }
      state.originalUrl = data;
      state.cropData = null;
      openCropperWithUrl(data);
    }
    if (typeof Cropper !== 'undefined') openIt();
    else window.addEventListener('load', openIt, { once: true });
  })();
})();

